export type Language = 'sv' | 'en' | 'no' | 'da' | 'de' | 'fr' | 'hu';

export const languageNames: Record<Language, string> = {
  sv: 'Svenska',
  en: 'English',
  no: 'Norsk',
  da: 'Dansk',
  de: 'Deutsch',
  fr: 'Français',
  hu: 'Magyar',
};

export const languageFlags: Record<Language, string> = {
  sv: '🇸🇪',
  en: '🇬🇧',
  no: '🇳🇴',
  da: '🇩🇰',
  de: '🇩🇪',
  fr: '🇫🇷',
  hu: '🇭🇺',
};

type TranslationKeys = {
  // TopBar
  appTitle: string;
  save: string;
  load: string;
  saveFile: string;
  loadFile: string;
  welcome: string;
  savedAsFile: string;
  donate: string;
  undo: string;
  redo: string;
  exportPng: string;
  import_: string;
  copyJson: string;
  simulate: string;
  clear: string;
  connectedDb: string;
  usingLocal: string;
  db: string;
  local: string;
  help: string;

  // MobileTopBar / drawer (#20, POST_QA_SUBPLAN Wave D)
  aiAndJson: string;
  aiAssistant: string;
  cloudWorkspaces: string;
  shareWorkspace: string;
  reportBug: string;
  featureRequest: string;
  adminDashboard: string;
  signIn: string;
  signOut: string;
  openMenu: string;
  menu: string;
  notSignedIn: string;

  // ToolSidebar
  tools: string;
  createUser: string;
  disableMailbox: string;
  deleteM365: string;
  sendEmail: string;
  webhook: string;
  addToGroup: string;
  condition: string;
  generalFunction: string;
  actions: string;
  connecting: string;
  connectBlocks: string;
  grouping: string;
  groupBlocks: string;
  blocks: string;
  groups: string;
  changeColor: string;
  dragToCanvas: string;
  lockGroup: string;
  unlockGroup: string;
  search: string;
  searchCanvasPlaceholder: string;
  searchCanvasTooltip: string;
  searchCanvasScopeAll: string;
  searchCanvasScopeWorkflows: string;
  searchCanvasScopeAtlas: string;
  searchCanvasMatchCount: string;
  collapseAll: string;
  collapseToLayers: string;
  expandAll: string;
  groupBatchActions: string;
  coopJoined: string;
  coopAccessDenied: string;
  coopAccessDeniedHint: string;
  coopLoadFailed: string;
  coopEnableTitle: string;
  coopEnableHint: string;
  coopActive: string;
  coopInactive: string;
  coopCollaborators: string;
  auditHistory: string;
  auditEmpty: string;

  // PropertiesPanel
  groupProperties: string;
  blockProperties: string;
  name: string;
  description: string;
  details: string;
  recipient: string;
  subject: string;
  blockColor: string;
  groupColor: string;
  templateSettings: string;
  trigger: string;
  userCreated: string;
  userModified: string;
  userDeleted: string;
  custom: string;
  overview: string;
  clickToConfig: string;
  blocksInGroup: string;
  writeDescription: string;

  // Canvas
  zoomIn: string;
  zoomOut: string;
  resetZoom: string;
  addBlock: string;
  noBlockTypes: string;
  fitToContent: string;

  // Toasts
  saved: string;
  savedToLocal: string;
  loaded: string;
  loadedFromLocal: string;
  noDataFound: string;
  couldNotFindWorkflow: string;
  cleared: string;
  canvasCleared: string;
  imported: string;
  importedFromJson: string;
  copied: string;
  jsonCopied: string;
  simulatingTitle: string;
  simulatingDesc: string;
  groupCreated: string;
  blocksGrouped: string;
  savedToDb: string;
  error: string;
  saveError: string;
  loadError: string;
  deleteError: string;
  deleted: string;
  documentDeleted: string;
  exportRefSaved: string;
  exportRefDesc: string;
  exportPngDesc: string;
  couldNotCopy: string;
  couldNotReadWorkflow: string;

  // Save Dialog
  saveWorkflow: string;
  saveAsNew: string;
  titleLabel: string;
  categoryLabel: string;
  cancel: string;

  // Load Dialog
  loadWorkflow: string;
  noDocuments: string;
  loadBtn: string;
  deleteBtn: string;

  // Import Dialog
  importJson: string;
  pasteJsonHere: string;
  importBtn: string;
  invalidJson: string;
  invalidStructure: string;

  // Help Dialog
  helpTitle: string;
  gettingStartedTab: string;
  integrationTab: string;
  databaseTab: string;
  jsonTab: string;
  aiPromptTab: string;

  // Getting Started
  gettingStartedTitle: string;
  gettingStartedDesc: string;
  gsWhatIsTitle: string;
  gsWhatIsDesc: string;
  gsAddBlocksTitle: string;
  gsAddBlocksDesc: string;
  gsConnectTitle: string;
  gsConnectDesc: string;
  gsGroupTitle: string;
  gsGroupDesc: string;
  gsEditTitle: string;
  gsEditDesc: string;
  gsSaveLoadTitle: string;
  gsSaveLoadDesc: string;
  gsImportExportTitle: string;
  gsImportExportDesc: string;
  gsAiTitle: string;
  gsAiDesc: string;
  gsKeyboardTitle: string;
  gsKeyboardShortcuts: string;
  gsLayersTitle: string;
  gsLayersDesc: string;

  integrationTitle: string;
  integrationDesc: string;
  integrationStep1: string;
  integrationStep2: string;
  integrationStep3: string;
  integrationStep4: string;
  integrationStep5: string;
  integrationStep6: string;
  integrationStep7: string;
  integrationCodeTitle: string;
  integrationCodeExample: string;
  integrationNote: string;
  databaseTitle: string;
  databaseDesc: string;
  databaseReq1: string;
  databaseReq2: string;
  databaseReq3: string;
  databaseReq4: string;
  jsonTitle: string;
  jsonDesc: string;
  jsonStep1: string;
  jsonStep2: string;
  jsonStep3: string;
  jsonStep4: string;
  jsonStructure: string;
  aiPromptTitle: string;
  aiPromptDesc: string;
  aiPromptUseCases: string;
  aiPromptUseCase1: string;
  aiPromptUseCase2: string;
  aiPromptUseCase3: string;
  aiPromptUseCase4: string;
  aiPromptUseCase5: string;
  aiPromptTemplateTitle: string;
  aiPromptTemplate: string;
  aiPromptTipsTitle: string;
  aiPromptTip1: string;
  aiPromptTip2: string;
  aiPromptTip3: string;
  aiPromptTip4: string;
  aiPromptCopy: string;
  aiPromptCopied: string;
  rename: string;
  editName: string;
  editDescription: string;
  editColor: string;
  editBlock: string;
  editGroup: string;
  editLayer: string;
  groupThisBlock: string;
  createGroupFromSelection: string;
  convertToLayer: string;
  duplicate: string;
  explainBlock: string;
  delete: string;
  deleteBlock: string;
  deleteGroup: string;
  collapseToLayer: string;
  enterLayer: string;
  expandLayer: string;
  close: string;
  keyboardShortcutsTitle: string;
  keyboardShortcutsHint: string;
  blocksGroupedCount_one: string;
  blocksGroupedCount_other: string;
  blocksCount_one: string;
  blocksCount_other: string;
  connectorsCount_one: string;
  connectorsCount_other: string;
  groupsCount_one: string;
  groupsCount_other: string;
  export: string;
  pleaseWait: string;
  exportDone: string;
  auditCreated: string;
  auditDeleted: string;
  auditRenamed: string;
  auditEdited: string;
  auditCoopEnabled: string;
  auditCoopDisabled: string;
  auditRedactedActor: string;
  auditCollabInvite: string;
  auditCollabRemove: string;
  auditCollabRoleChange: string;
  auditCollabAccept: string;
  // Cross-sheet linking (ToolSidebar)
  crossSheetLink: string;
  crossSheetActive: string;
  crossSheetHint: string;
  crossSheetActiveHint: string;

  // Generic
  back: string;
  // Process Engine
  processEngineDisabled: string;
  processEngineUpsellTitle: string;
  processEngineUpsellBody: string;
  processEngineUpsellCta: string;
  processEngineLoadTitle: string;
  processEngineLoadFromFile: string;
  processEngineLoadFromPaste: string;
  processEngineLoadImport: string;
  processEngineImportSuccess: string;
  processEngineImportFailed: string;
  processEngineLiveAvailable: string;
  processEngineDesignOnly: string;
  processEngineDryRun: string;
  processEngineDryRunDone: string;
  processEngineRunning: string;
  processEngineLoadFromTemplate: string;
  processEngineOpenFinal: string;
  processEngineSuggest: string;
  processEngineSuggestNone: string;
  processEngineSuggestDone: string;
  processEngineRunLive: string;
  processEngineRunDone: string;
  processEngineRunFailed: string;
  processEngineRunBlocked: string;
  processEngineSave: string;
  processEngineSaving: string;
  processEngineSaveDone: string;
  processEngineSaveFailed: string;
  processEngineLoadFromSaved: string;
  finalTldr: string;
  // /final
  finalPageTitle: string;
  finalSubtitle: string;
  finalAudience: string;
  finalHubTitle: string;
  finalHubSubtitle: string;
  finalNoFlowTitle: string;
  finalNoFlowBody: string;
  finalGoToProcessEngine: string;
  finalContainerFlowWeaverTitle: string;
  finalContainerFlowWeaverEmpty: string;
  finalContainerOpenFlowWeaver: string;
  finalContainerProcessEngineTitle: string;
  finalContainerProcessEngineSubtitle: string;
  finalContainerProcessEngineEmpty: string;
  finalContainerOpenProcessEngine: string;
  finalContainerAtlasTitle: string;
  finalContainerAtlasEmpty: string;
  finalContainerOpenAtlas: string;
  finalContainerGroups: string;
  finalContainerFlows: string;
  finalContainerTotalBlocks: string;
  finalContainerCategories: string;
  finalActDesign: string;
  finalActDesignSubtitle: string;
  finalActKnowledge: string;
  finalActKnowledgeSubtitle: string;
  finalActOperation: string;
  finalActOperationSubtitle: string;
  finalSheets: string;
  finalBlocks: string;
  finalConnectors: string;
  finalBindings: string;
  finalAtlas: string;
  finalDocs: string;
  finalCrossDocRefs: string;
  finalRuns30d: string;
  finalSuccessRate: string;
  finalMeanDuration: string;
  finalRoi: string;
  finalRoiHoursSaved: string;
  finalRoiErrorsPrevented: string;
  finalRoiMtto: string;
  finalRoiValue: string;
  finalRoiMethodology: string;
  finalWatermark: string;
  // /final
  finalDisabled: string;
  finalUpsellTitle: string;
  finalUpsellBody: string;
  finalUpsellCta: string;
  finalPhaseHint: string;
  // Mobile UI labels
  blockCreated: string;
  toggleCollapse: string;
  toggleLock: string;
};

const sv: TranslationKeys = {
  appTitle: 'EngiCoro Flow Weaver',
  save: 'Spara',
  load: 'Ladda',
  saveFile: 'Spara fil',
  loadFile: 'Ladda fil',
  welcome: 'Välkommen',
  savedAsFile: 'Workflow har sparats som fil',
  donate: 'Donera',
  undo: 'Ångra',
  redo: 'Gör om',
  exportPng: 'Exportera bild',
  import_: 'Importera',
  copyJson: 'Kopiera JSON',
  simulate: 'Simulera',
  clear: 'Rensa',
  connectedDb: 'Ansluten till databas',
  usingLocal: 'Använder localStorage',
  db: 'DB',
  local: 'Lokal',
  help: 'Hjälp',

  // MobileTopBar / drawer (#20, Wave D)
  aiAndJson: 'AI och JSON',
  aiAssistant: 'AI-assistent',
  cloudWorkspaces: 'Molnarbetsytor',
  shareWorkspace: 'Dela arbetsyta',
  reportBug: 'Rapportera bug',
  featureRequest: 'Funktionsförslag',
  adminDashboard: 'Adminpanel',
  signIn: 'Logga in',
  signOut: 'Logga ut',
  openMenu: 'Öppna meny',
  menu: 'Meny',
  notSignedIn: 'Inte inloggad',
  gettingStartedTab: 'Kom igång',
  gettingStartedTitle: 'Välkommen till Flow Weaver',
  gettingStartedDesc: 'Flow Weaver är ett visuellt verktyg för att skapa flödesscheman och orkestreringsdiagram. Dra block, koppla ihop dem och organisera dem i grupper och lager — allt direkt i webbläsaren. Appen kan köras fristående eller bäddas in i ett annat projekt via iframe.',
  gsWhatIsTitle: '🎯 Vad är Flow Weaver?',
  gsWhatIsDesc: 'Ett drag-and-drop-verktyg för att bygga visuella flöden. Perfekt för att dokumentera IT-processer, systemarkitektur, onboarding-flöden, automationer och mycket mer. Stödjer obegränsad nesting av lager, view-only-läge för slutanvändare, och export till PNG/SVG.',
  gsAddBlocksTitle: '➕ Lägga till block',
  gsAddBlocksDesc: 'Dra ett block från den vänstra sidopanelen och släpp det på canvas. Varje block representerar ett steg i ditt flöde. Välj mellan fördefinierade typer (Skapa Användare, Webhook, Villkor, etc.) eller använd "Allmän funktion". Klicka på ett block för att redigera dess ikon, namn och beskrivning.',
  gsConnectTitle: '🔗 Koppla block',
  gsConnectDesc: 'Klicka på "Koppla block"-knappen i sidopanelen. Klicka sedan på en port (de små cirklarna runt blocken) för att starta kopplingen, och klicka på en port på ett annat block för att avsluta. Block har 8 portar (topp, höger, botten, vänster + hörn) för flexibel routing. Dubbelklicka på en linje för att lägga till en waypoint (styrnod) — waypoints gör att kurvan böjer sig smidigt. Dubbelklicka på en waypoint för att ta bort den.',
  gsGroupTitle: '📦 Gruppera block',
  gsGroupDesc: 'Klicka på "Gruppera block"-knappen, klicka sedan på de block du vill gruppera (de markeras med en ring), och klicka på knappen igen för att skapa gruppen. Grupper får en färgad ram och en etikett. Du kan låsa grupper så att alla block rör sig tillsammans.',
  gsEditTitle: '✏️ Redigera egenskaper',
  gsEditDesc: 'Klicka på ett block eller en grupp för att öppna egenskapspanelen till höger. Där kan du ändra namn, beskrivning (stödjer radbrytningar), ikon och färg. Minimappen längst ner visar hela canvasens layout och din aktuella vy.',
  gsLayersTitle: '🧩 Lager & Nesting',
  gsLayersDesc: 'Grupper kan kollapsas till kompakta kort som representerar hela lager. Klicka på lager-ikonen (⧉) i sidopanelen för att kollapsa/expandera. Dubbelklicka på ett kollapserat kort för att dyka in i lagret. Brödsmulor uppe till vänster visar var du är och låter dig navigera tillbaka. Enskilda block kan konverteras till lager via lager-knappen — detta skapar en tom grupp som du kan fylla med innehåll. Obegränsad nesting stöds.',
  gsSaveLoadTitle: '💾 Spara & Ladda',
  gsSaveLoadDesc: 'Klicka "Spara" för att spara ditt flöde. Utan databas sparas det i webbläsarens localStorage. Med en databasanslutning (via postMessage från värd-appen) sparas det permanent i databasen. Använd "Ladda" för att öppna sparade flöden. Dokument kan kategoriseras och fästas.',
  gsImportExportTitle: '📤 Import & Export',
  gsImportExportDesc: 'Använd "Kopiera JSON" för att exportera ditt flöde som JSON-text. Använd "Import" för att klistra in JSON och ladda ett flöde. Exportera som PNG eller SVG — välj mellan "Aktuell arbetsyta" (hela canvasen) eller "Hel översikt" (alla lager utfällda). Knappen "Overview" visar hela hierarkin med nestade lager, interna kopplingar och kors-entitetskopplingar.',
  gsAiTitle: '🤖 Skapa flöden med AI',
  gsAiDesc: 'Klicka på "AI & JSON"-knappen i verktygsfältet för att öppna en dialog med en färdig AI-prompt och JSON-referens. Kopiera prompten till valfri AI (ChatGPT, Claude, Grok, etc.), beskriv ditt system, och importera det genererade JSON-resultatet direkt i Flow Weaver.',
  gsKeyboardTitle: '⌨️ Navigation, Zoom & Genvägar',
  gsKeyboardShortcuts: 'Ctrl/Cmd + scroll för att zooma in/ut\n• Scroll för att panorera\n• Dra block för att flytta dem\n• Zoom-kontroller uppe till höger (+ / − / Återställ)\n• Dubbelklicka på kollapserat lager för att dyka in\n• Enter-tangent på kollapserat lager för att öppna det\n• Brödsmulor för lagernavigering\n• Dubbelklicka på linje för att lägga till waypoint\n• Dubbelklicka på waypoint för att ta bort den\n• Piltangenter för att navigera mellan block\n• Ctrl+Z / Cmd+Z för att ångra\n• Ctrl+Shift+Z / Cmd+Shift+Z för att göra om\n• Ctrl+S / Cmd+S för att spara\n• Delete / Backspace för att ta bort valt block\n• Escape för att avmarkera',
  tools: 'Verktyg',
  createUser: 'Aktivitet',
  disableMailbox: 'Kommunikation',
  deleteM365: 'System',
  sendEmail: 'Notifiering',
  webhook: 'Händelse',
  addToGroup: 'Intressent',
  condition: 'Beslut',
  generalFunction: 'Process',
  actions: 'Åtgärder',
  connecting: 'Kopplar...',
  connectBlocks: 'Koppla block',
  grouping: 'Grupperar',
  groupBlocks: 'Gruppera block',
  blocks: 'Block',
  groups: 'Grupper',
  changeColor: 'Byt färg',
  dragToCanvas: 'Dra verktyg till canvas',
  lockGroup: 'Lås grupp',
  unlockGroup: 'Lås upp grupp',
  search: 'Sök...',
  searchCanvasPlaceholder: 'Sök allt…',
  searchCanvasTooltip: 'Skriv för att filtrera. Enter öppnar Sök-canvas. Syntax: "frasen", -uteslut, name:värde, type:webhook, source:atlas. Cmd+K fokuserar.',
  searchCanvasScopeAll: 'Allt',
  searchCanvasScopeWorkflows: 'Arbetsflöden',
  searchCanvasScopeAtlas: 'Atlas',
  searchCanvasMatchCount: '{count} träffar',
  collapseAll: 'Kolapsa allt',
  collapseToLayers: 'Kolapsa till lager',
  expandAll: 'Expandera allt',
  groupBatchActions: 'Gruppåtgärder',
  coopJoined: 'Co-op arbetsyta ansluten',
  coopAccessDenied: 'Ingen åtkomst till arbetsytan',
  coopAccessDeniedHint: 'Be ägaren aktivera Co-op och bjuda in dig.',
  coopLoadFailed: 'Kunde inte ladda delad arbetsyta',
  coopEnableTitle: 'Aktivera Co-op',
  coopEnableHint: 'Måste vara på för att delningslänken ska fungera',
  coopActive: 'Co-op är aktivt',
  coopInactive: 'Co-op är avstängt',
  coopCollaborators: 'Medverkande',
  auditHistory: 'Historik',
  auditEmpty: 'Inga ändringar registrerade än.',
  groupProperties: 'Grupp Egenskaper',
  blockProperties: 'Block Egenskaper',
  name: 'Namn',
  description: 'Beskrivning',
  details: 'Detaljer…',
  recipient: 'Mottagare',
  subject: 'Ämne',
  blockColor: 'Block färg',
  groupColor: 'Grupp färg',
  templateSettings: 'Template Inställningar',
  trigger: 'Trigger',
  userCreated: 'Användare Skapad',
  userModified: 'Användare Ändrad',
  userDeleted: 'Användare Raderad',
  custom: 'Anpassad',
  overview: 'Översikt',
  clickToConfig: 'Klicka på ett block eller en grupp för att konfigurera',
  blocksInGroup: 'block i gruppen',
  writeDescription: 'Skriv en beskrivning... (stödjer radbrytningar)',
  zoomIn: 'Förstora',
  zoomOut: 'Förminska',
  resetZoom: 'Återställ skala',
  addBlock: 'Lägg till block',
  noBlockTypes: 'Inga blocktyper tillgängliga',
  fitToContent: 'Anpassa till innehåll',
  saved: 'Sparat!',
  savedToLocal: 'Workflow har sparats till localStorage',
  loaded: 'Laddat!',
  loadedFromLocal: 'Workflow har laddats från localStorage',
  noDataFound: 'Ingen data hittad',
  couldNotFindWorkflow: 'Kunde inte hitta sparad workflow',
  cleared: 'Rensat!',
  canvasCleared: 'Canvas har tömts',
  imported: 'Importerat!',
  importedFromJson: 'Importerat från JSON',
  copied: 'Kopierat!',
  jsonCopied: 'Workflow JSON har kopierats till urklipp',
  simulatingTitle: 'Simulerar...',
  simulatingDesc: 'Kör',
  groupCreated: 'Grupp skapad',
  blocksGrouped: 'block grupperade',
  savedToDb: 'har sparats till databasen',
  error: 'Fel',
  saveError: 'Fel vid sparning',
  loadError: 'Fel vid laddning',
  deleteError: 'Fel vid borttagning',
  deleted: 'Borttaget',
  documentDeleted: 'Dokumentet har tagits bort',
  exportRefSaved: 'Export-referens sparad',
  exportRefDesc: 'En referens för PNG-export har sparats i databasen',
  exportPngDesc: 'PNG-export kräver dom-to-image bibliotek',
  couldNotCopy: 'Kunde inte kopiera till urklipp',
  couldNotReadWorkflow: 'Kunde inte läsa workflow-data',
  saveWorkflow: 'Spara Workflow',
  saveAsNew: 'Spara som ny',
  titleLabel: 'Titel',
  categoryLabel: 'Kategori',
  cancel: 'Avbryt',
  loadWorkflow: 'Ladda Workflow',
  noDocuments: 'Inga sparade dokument',
  loadBtn: 'Ladda',
  deleteBtn: 'Ta bort',
  importJson: 'Importera JSON',
  pasteJsonHere: 'Klistra in WorkflowState JSON här...',
  importBtn: 'Importera',
  invalidJson: 'Ogiltig JSON',
  invalidStructure: 'JSON saknar nödvändig struktur (blocks, connectors, groups)',
  helpTitle: 'Instruktioner & Dokumentation',
  integrationTab: 'Integration',
  databaseTab: 'Databas',
  jsonTab: 'JSON-mappning',
  integrationTitle: 'Integrera i ditt projekt',
  integrationDesc: 'Flow Weaver kan köras fristående eller bäddas in i ett annat projekt via iframe. Det stödjer två lägen: redigeringsläge (standard) och view-only-läge (via ?mode=view). I view-only-läget döljs alla redigeringsfunktioner — användaren kan bara se, zooma, navigera lager, ladda och exportera.',
  integrationStep1: '1. Publicera appen via Lovable och kopiera den publicerade URL:en (t.ex. https://flow-weaver-05.lovable.app)',
  integrationStep2: '2. Bädda in med <iframe src="URL?mode=view" width="100%" height="600px" /> för view-only, eller utan ?mode=view för redigeringsläge',
  integrationStep3: '3. Utan databasintegration sparas allt i webbläsarens localStorage — perfekt för testning',
  integrationStep4: '4. För databaslagring: skicka dina Supabase-credentials till iframen via postMessage (se kodexempel nedan). Iframen lyssnar automatiskt efter meddelandet "supabase-credentials"',
  integrationStep5: '5. Använd ?mode=view för slutanvändare (read-only) och standard-URL utan parameter för administratörer (redigeringsläge)',
  integrationStep6: '6. Användaren måste vara inloggad i värd-appen — access token från sessionen skickas med för att autentisera mot databasen',
  integrationStep7: '7. Om inga credentials skickas faller iframen tillbaka till localStorage (ingen data förloras). View-only-läget fungerar oavsett databasanslutning',
  integrationCodeTitle: 'Kodexempel — skicka credentials från värd-appen',
  integrationCodeExample: `import { useRef, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

function FlowchartPage({ isAdmin = false }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const mode = isAdmin ? '' : '?mode=view';

  const sendCredentials = useCallback(async () => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;

    iframe.contentWindow.postMessage({
      type: 'supabase-credentials',
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
      supabaseKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      accessToken: session.access_token,
    }, '*');
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'request-supabase-credentials') {
        sendCredentials();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [sendCredentials]);

  return (
    <iframe
      ref={iframeRef}
      src={\`https://flow-weaver-05.lovable.app/\${mode}\`}
      onLoad={() => setTimeout(sendCredentials, 500)}
      className="w-full h-full"
    />
  );
}`,
  integrationNote: '⚠️ View-only-läge (?mode=view): Döljer TopBar, ToolSidebar och PropertiesPanel. Visar en minimal topbar med Ladda, Export Image och Overview. Alla redigeringsfunktioner (drag, kopplingar, portar) är inaktiverade. Zoom, scroll och lagernavigering fungerar som vanligt.',
  databaseTitle: 'Databaskrav för värd-appen',
  databaseDesc: 'Följande krävs i värd-appen för att databaslagring ska fungera. Flow Weaver skickar workflow-data som JSON till en edge function som hanterar sparande.',
  databaseReq1: 'Tabell: workflows\n— id (uuid PK, default gen_random_uuid())\n— title (text, NOT NULL)\n— blocks (jsonb, NOT NULL, default \'[]\')\n— connectors (jsonb, NOT NULL, default \'[]\')\n— groups (jsonb, NOT NULL, default \'[]\')\n— workflow_name (text, default \'\')\n— coordinate_origin (text, default \'center\')\n— category (text, default "workflow")\n— created_by (uuid, NOT NULL — referens till användarens auth.uid())\n— is_pinned (boolean, default false)\n— pinned_at (timestamptz, nullable)\n— pinned_by (uuid, nullable)\n— last_modified_by (uuid, nullable)\n— created_at (timestamptz, default now())\n— updated_at (timestamptz, default now())',
  databaseReq2: 'En "save-workflow" edge function som: (1) Validerar JWT-token via getClaims, (2) Extraherar user ID från token, (3) Tar emot { id?, title, blocks, connectors, groups, workflow_name, coordinate_origin, category } i request body, (4) Gör INSERT vid nytt workflow eller UPDATE vid befintligt, (5) Returnerar { id, title } vid success',
  databaseReq3: 'RLS-policies på workflows-tabellen:\n— SELECT: auth.uid() = created_by\n— INSERT: auth.uid() = created_by\n— UPDATE: auth.uid() = created_by\n— DELETE: auth.uid() = created_by\nDetta säkerställer att varje användare bara kan se och hantera sina egna workflows',
  databaseReq4: 'Autentiseringsflöde: Användaren loggar in i värd-appen → värd-appen skickar access_token via postMessage till iframen → Flow Weaver använder token för att autentisera mot databasen. Utan token fungerar appen i localStorage-läge',
  jsonTitle: 'JSON-mappning',
  jsonDesc: 'Workflows lagras som JSON med följande struktur (WorkflowState):',
  jsonStep1: '1. Klicka "Kopiera JSON" i verktygsfältet för att exportera aktuellt flöde',
  jsonStep2: '2. Klicka "Import" för att klistra in och ladda ett flöde från JSON',
  jsonStep3: '3. Varje block har id, type, x, y, config (namn, beskrivning, ikon) och color. Block kan konverteras till lager (tomma kollapsade grupper) via lager-knappen.',
  jsonStep4: '4. Kopplingar refererar fromId/toId. Grupper (groups) kan ha 0+ block, stödjer nesting via parentGroupId, och kan kollapsas till lager med collapsed/collapsedX/collapsedY.',
  jsonStructure: '{ "name": "...", "blocks": [...], "connectors": [{ fromId, toId, fromPort, toPort }], "groups": [{ id, blockIds, color, name, collapsed, collapsedX, collapsedY, parentGroupId, locked }] }',
  aiPromptTab: 'AI-Prompt',
  aiPromptTitle: 'Generera flöden med AI',
  aiPromptDesc: 'Du kan använda valfri AI (ChatGPT, Grok, Claude, Lovable m.fl.) för att automatiskt skapa JSON som kan importeras direkt i Flow Weaver. Ge AI:n prompten nedan tillsammans med ditt material.',
  aiPromptUseCases: 'Användningsfall',
  aiPromptUseCase1: 'Be en AI att mappa ett befintligt projekt eller system till ett flödesschema',
  aiPromptUseCase2: 'Ladda upp bilder (arkitekturdiagram, whiteboards) och be AI:n konvertera till JSON',
  aiPromptUseCase3: 'Skicka dokumentation, specifikationer eller kravlistor och få ett visuellt flöde',
  aiPromptUseCase4: 'Beskriv en process med ord och låt AI:n bygga flödesschemat åt dig',
  aiPromptUseCase5: 'Bygg komplexa flerskiktsarkitekturer med nestade lager — överblick på toppnivå, detaljerad logik inuti',
  aiPromptTemplateTitle: 'Kopierbar AI-prompt',
  aiPromptTemplate: `Du är en JSON-generator för Flow Weaver. Returnera ENBART ett giltigt JSON-objekt (WorkflowState). Ingen förklarande text.

Du är en EXPERT-arkitekt — designa workflows med tydliga hierarkier, logiska grupperingar och optimal connector-routing. Tänk i lager: överblick på toppnivå, detaljerad logik inuti nestade grupper.

══════════════════════════════════
  SCHEMA
══════════════════════════════════
{
  "name": "Flödets namn",
  "blocks": [
    { "id": "b01", "type": "general", "x": 200, "y": 100,
      "config": { "name": "Blocknamn", "description": "Detaljerad beskrivning\\nFlera rader stöds", "icon": "wrench" },
      "color": "#6366f1" }
  ],
  "connectors": [
    { "id": "c01", "fromId": "b01", "toId": "b02", "fromPort": "right", "toPort": "left" }
  ],
  "groups": [
    { "id": "g1", "blockIds": ["b01","b02"], "color": "#8b5cf6", "name": "Gruppnamn",
      "locked": false, "collapsed": true, "parentGroupId": null,
      "collapsedX": 200, "collapsedY": 100 }
  ]
}

══════════════════════════════════
  1. BLOCK-TYPER
══════════════════════════════════
"create-user" | "disable-mailbox" | "delete-m365" | "send-email"
"webhook"      | "add-to-group"   | "condition"    | "general"

Använd "general" för steg som inte täcks ovan. Använd "condition" för beslutspunkter / förgreningar.

══════════════════════════════════
  2. IKONER (efter kategori)
══════════════════════════════════
Personer:       user, users
Kommunikation:  mail, send, message-square, phone, bell
Moln/Nätverk:   cloud, globe, server, database, link
Filer:          file-text, folder, archive, download, upload, copy
Verktyg:        wrench, settings, terminal, filter, search
Status:         check-circle, alert-triangle, clock, flag, star
Säkerhet:       shield, lock, key, eye
Media:          play, camera, monitor
Logik:          git-branch, shuffle, layers, refresh-cw
Övrigt:         zap, power, heart, tag, bookmark, bar-chart, package, trash, thumbs-up, calendar, help-circle

══════════════════════════════════
  3. RUTNÄT & KOORDINATER
══════════════════════════════════
- x och y MÅSTE vara multiplar av 20
- Horisontellt mellanrum: ~240px
- Vertikalt mellanrum:   ~220px
- id-format: "b01", "b02", ... (block), "c01", "c02", ... (connector), "g1", "g2", ... (grupp)
- Alla id:n måste vara globalt unika
- Block i en kollapsad grupp ska layoutas med egna koordinater (eget canvas)

══════════════════════════════════
  4. PORT-ROUTING (KRITISKT!)
══════════════════════════════════
Bestäm fromPort/toPort genom att jämföra source (x,y) med target (x,y):

  Riktning            │ fromPort       │ toPort
  ────────────────────┼────────────────┼────────────────
  → Höger (samma y)   │ "right"        │ "left"
  ← Vänster (samma y) │ "left"         │ "right"
  ↓ Nedåt (samma x)   │ "bottom"       │ "top"
  ↑ Uppåt (samma x)   │ "top"          │ "bottom"
  ↘ Snett ned-höger   │ "bottom-right" │ "top-left"
  ↙ Snett ned-vänster │ "bottom-left"  │ "top-right"
  ↗ Snett upp-höger   │ "top-right"    │ "bottom-left"
  ↖ Snett upp-vänster │ "top-left"     │ "bottom-right"

⚠ ALDRIG diagonala portar för block på samma rad (y) eller kolumn (x).
⚠ fromPort och toPort MÅSTE alltid anges.
⚠ Auto-snap korrigerar suboptimal routing, men gör rätt för rena resultat.

══════════════════════════════════
  5. GRUPPER & NESTING
══════════════════════════════════
Grupp-fält:
  "id":            Unikt grupp-ID (t.ex. "g1", "g2")
  "blockIds":      Array av block-ID:n som tillhör gruppen (kan vara TOM [] för lager)
  "color":         Hexfärg (t.ex. "#8b5cf6")
  "name":          Visningsnamn
  "locked":        false (standard) — true = block rör sig tillsammans
  "collapsed":     true/false — true = gruppen visas som kompakt kort (LAGER-LÄGE)
  "parentGroupId": null för toppnivå, eller föräldragruppens ID för nesting
  "collapsedX":    X-position för kortet (multipel av 20)
  "collapsedY":    Y-position för kortet (multipel av 20)

Regler:
- Grupper kan ha 0 eller fler block (tomma grupper = tomma lager)
- Ett enskilt block kan konverteras till ett lager (skapar en tom kollapsad grupp)
- Block kan bara tillhöra EN grupp
- Nestade grupper använder OBEROENDE koordinatsystem (varje lager är sitt eget canvas)
- parentGroupId kopplar barn-grupper till förälder-grupper för hierarki

══════════════════════════════════
  6. LAGERSYSTEM (AVANCERAT)
══════════════════════════════════
Grupper med "collapsed": true blir navigerbara lager:

🏗️ ARKITEKTURMÖNSTER:
  Rotnivå:  Överblick med kollapsade grupper som kompakta kort
  Lager 1:  Delsystem — varje kollapsad grupp expanderas till eget canvas
  Lager 2+: Detaljerad implementeringslogik i nestade grupper

🔄 BLOCK-TILL-LAGER-KONVERTERING:
  - Enskilda block kan konverteras till lager via lager-knappen
  - Detta skapar en tom kollapsad grupp vid blockets position
  - Alla kopplingar till/från blocket omdirigeras till det nya lagret
  - Använd detta för att skapa "placeholder-lager" som fylls senare

📐 LAYOUT-STRATEGI:
  - Placera kollapsade grupper som block (~240px mellanrum)
  - Använd collapsedX/collapsedY för att positionera kort på föräldranivå
  - Inuti varje grupp: layouta block oberoende (eget koordinatsystem, börja från x:200, y:100)

🔗 KOPPLINGAR MELLAN LAGER:
  - Kopplingar mellan block i olika kollapsade grupper visas som kopplingar
    mellan gruppkorten på föräldranivå
  - Kopplingar kan referera grupp-ID:n direkt (fromId/toId = grupp-ID)

📋 EXEMPEL — 3-skiktsarkitektur:
  Rot:   [Auth-Grupp] → [API-Grupp] → [Databas-Grupp]
  Auth:  [Login] → [Validera] → [Token]
  API:   [Router] → [Controller] → [Middleware]
  DB:    [Query] → [Cache] → [Storage]

  JSON-struktur:
  - 3 grupper med collapsed: true, collapsedX/Y på rotnivå
  - Kopplingar mellan grupp-ID:n på rotnivå
  - Block inuti varje grupp med egna koordinater

══════════════════════════════════
  7. DESIGNMÖNSTER
══════════════════════════════════
📊 PIPELINE: Linjärt flöde → "right"/"left"-portar, samma Y
📐 FÖRGRENING: Villkor med flera vägar → "bottom"/"right"-portar
🔄 FEEDBACK-LOOP: Returpil → "left"/"top"-portar tillbaka
🏢 HIERARKI: Ledargrupper med arbetare i undergrupper
⚡ EVENT-DRIVET: Webhook-trigger med parallella vägar
🎨 FÄRGKODNING: Använd olika färger per domän/concern:
   Infrastruktur: "#6366f1"  API: "#8b5cf6"  Auth: "#ec4899"
   Data: "#06b6d4"  UI: "#22c55e"  Monitoring: "#f97316"

  ══════════════════════════════════
  CHECKLISTA INNAN OUTPUT
══════════════════════════════════
✅ Alla x/y är multiplar av 20
✅ Alla connectors har fromPort + toPort
✅ Portval matchar relativ position (se tabell ovan)
✅ Alla id:n är globalt unika
✅ Grupper kan vara tomma (blockIds: []) — ingen minimigräns
✅ Kollapsade grupper har collapsedX/collapsedY satta
✅ parentGroupId refererar giltigt grupp-ID (eller null)
✅ Block inuti lager har oberoende koordinater (eget canvas)
✅ Blockbeskrivningar är detaljerade och handlingsbara
✅ Färgkoda grupper efter domän/concern
✅ Waypoints (valfritt): lägg till "waypoints": [{"x":…,"y":…}] på en connector för att styra kurvan manuellt
✅ Alltid inkludera "coordinateOrigin": "center" på root-nivå
✅ Outputen är ENBART JSON`,
  aiPromptTipsTitle: 'Tips för bästa resultat',
  aiPromptTip1: 'Bifoga bilder på befintliga diagram — AI:n kan tolka och konvertera dem till JSON',
  aiPromptTip2: 'Beskriv processens steg i punktform för att få ett tydligare resultat',
  aiPromptTip3: 'Ange önskat antal block och grupperingar i din prompt för mer kontroll',
  aiPromptTip4: 'Kopiera JSON-resultatet och klistra in via Import-knappen i Flow Weaver',
  aiPromptCopy: 'Kopiera prompt',
  aiPromptCopied: 'Prompt kopierad!',
  close: 'Stäng',
  rename: 'Byt namn',
  editName: 'Redigera namn',
  editDescription: 'Redigera beskrivning',
  editColor: 'Redigera färg',
  editBlock: 'Redigera block',
  editGroup: 'Redigera grupp',
  editLayer: 'Redigera lager',
  groupThisBlock: 'Gruppera detta block',
  createGroupFromSelection: 'Skapa grupp från urval',
  convertToLayer: 'Konvertera till lager',
  duplicate: 'Duplicera',
  explainBlock: 'Förklara block…',
  delete: 'Ta bort',
  deleteBlock: 'Ta bort block',
  deleteGroup: 'Ta bort grupp',
  collapseToLayer: 'Kollapsa till lager',
  enterLayer: 'Öppna lager',
  expandLayer: 'Expandera (konvertera tillbaka till grupp)',
  keyboardShortcutsTitle: 'Tangentbordsgenvägar',
  keyboardShortcutsHint: 'Tryck ? för att visa denna ruta igen.',
  blocksGroupedCount_one: '{{count}} block grupperat',
  blocksGroupedCount_other: '{{count}} block grupperade',
  blocksCount_one: '{{count}} block',
  blocksCount_other: '{{count}} block',
  connectorsCount_one: '{{count}} koppling',
  connectorsCount_other: '{{count}} kopplingar',
  groupsCount_one: '{{count}} grupp',
  groupsCount_other: '{{count}} grupper',
  export: 'Exporterar',
  pleaseWait: 'Detta kan ta några sekunder för stora arbetsytor…',
  exportDone: 'Export klar',
  auditCreated: 'Skapade',
  auditDeleted: 'Tog bort',
  auditRenamed: 'Döpte om',
  auditEdited: 'Redigerade',
  auditCoopEnabled: 'Co-op aktiverat',
  auditCoopDisabled: 'Co-op inaktiverat',
  auditRedactedActor: '[borttagen användare]',
  auditCollabInvite: 'Bjöd in användare',
  auditCollabRemove: 'Tog bort användare',
  auditCollabRoleChange: 'Ändrade roll för användare',
  auditCollabAccept: 'Accepterade inbjudan:',
  crossSheetLink: 'Korslänk mellan blad',
  crossSheetActive: 'Korslänkning mellan blad…',
  crossSheetHint: 'Länka block mellan intilliggande blad',
  crossSheetActiveHint: 'Tryck Esc för att avbryta',
  back: 'Tillbaka',
  processEngineDisabled: 'Process Engine är avstängt i den här installationen.',
  processEngineUpsellTitle: 'Lås upp Process Engine',
  processEngineUpsellBody: 'Bygg attestflöden, provisionering och offboarding från dina Flow-weaver-diagram. Kräver {tier} eller högre.',
  processEngineUpsellCta: 'Se prisplaner',
  processEngineLoadTitle: 'Ladda ett Flow-weaver-flöde',
  processEngineLoadFromFile: 'Från fil (.json)',
  processEngineLoadFromPaste: 'Klistra in JSON',
  processEngineLoadImport: 'Importera',
  processEngineImportSuccess: 'Flöde importerat',
  processEngineImportFailed: 'Kunde inte importera flödet',
  processEngineLiveAvailable: 'Live-körningar är tillgängliga',
  processEngineDesignOnly: 'Endast design — uppgradera till {tier} för live-körning',
  processEngineDryRun: 'Testkörning',
  processEngineDryRunDone: 'Testkörning klar',
  processEngineRunning: 'Kör…',
  processEngineLoadFromTemplate: 'Eller starta från mall',
  processEngineOpenFinal: 'Slutleverans',
  processEngineSuggest: 'AI-förslag',
  processEngineSuggestNone: 'Inga förslag',
  processEngineSuggestDone: 'Förslag tillagda',
  processEngineRunLive: 'Kör live',
  processEngineRunDone: 'Live-körning klar',
  processEngineRunFailed: 'Live-körning misslyckades',
  processEngineRunBlocked: 'Live-körning är blockerad',
  processEngineSave: 'Spara',
  processEngineSaving: 'Sparar…',
  processEngineSaveDone: 'Sparat',
  processEngineSaveFailed: 'Sparning misslyckades',
  processEngineLoadFromSaved: 'Sparade flöden',
  finalTldr: 'Sammanfattning',
  finalPageTitle: 'Slutleverans',
  finalSubtitle: 'En komplett bild av design, kunskap och drift.',
  finalAudience: 'Målgrupp',
  finalHubTitle: 'Systemöversikt',
  finalHubSubtitle:
    'Live-status för varje system. Öppna en container för att gå djupare, eller promotera ett flöde från Process Engine för den detaljerade tre-akts-berättelsen nedan.',
  finalNoFlowTitle: 'Inget flöde valt',
  finalNoFlowBody: 'Gå till Process Engine och välj ett flöde först.',
  finalGoToProcessEngine: 'Öppna Process Engine',
  finalContainerFlowWeaverTitle: 'Flow-weaver',
  finalContainerFlowWeaverEmpty: 'Inget arbetsflöde sparat ännu — öppna Flow-weaver för att börja.',
  finalContainerOpenFlowWeaver: 'Öppna Flow-weaver',
  finalContainerProcessEngineTitle: 'Process Engine',
  finalContainerProcessEngineSubtitle: '{count} sparade flöden',
  finalContainerProcessEngineEmpty: 'Inga sparade flöden — promotera ett arbetsflöde till Process.',
  finalContainerOpenProcessEngine: 'Öppna Process Engine',
  finalContainerAtlasTitle: 'Atlas',
  finalContainerAtlasEmpty: 'Atlas-manifest ej laddat.',
  finalContainerOpenAtlas: 'Öppna Atlas',
  finalContainerGroups: 'Grupper',
  finalContainerFlows: 'Flöden',
  finalContainerTotalBlocks: 'Totalt block',
  finalContainerCategories: 'Kategorier',
  finalActDesign: 'Design',
  finalActDesignSubtitle: 'Hur processen modellerades i Flow-weaver.',
  finalActKnowledge: 'Kunskap',
  finalActKnowledgeSubtitle: 'Vad organisationen vet och länkar samman i Atlas.',
  finalActOperation: 'Drift',
  finalActOperationSubtitle: 'Hur flödet beter sig i verkligheten över 30 dagar.',
  finalSheets: 'Blad',
  finalBlocks: 'Block',
  finalConnectors: 'Kopplingar',
  finalBindings: 'Bindningar',
  finalAtlas: 'Atlas',
  finalDocs: 'Dokument',
  finalCrossDocRefs: 'Korslänkar',
  finalRuns30d: 'Körningar (30 d)',
  finalSuccessRate: 'Lyckandegrad',
  finalMeanDuration: 'Snittid',
  finalRoi: 'Värde',
  finalRoiHoursSaved: 'Sparade timmar',
  finalRoiErrorsPrevented: 'Förhindrade fel',
  finalRoiMtto: 'MTTO',
  finalRoiValue: 'Värde',
  finalRoiMethodology: 'Värdet beräknas konservativt på 30 min sparad manuell tid per lyckad körning, multiplicerat med en blandad timkostnad på 45 €.',
  finalWatermark: 'Genererat: {watermark}',
  finalDisabled: 'Slutleveransvyn är avstängd i den här installationen.',
  finalUpsellTitle: 'Lås upp slutleveransvyn',
  finalUpsellBody: 'En läsbar exekutiv översikt över Flow-weaver, Atlas och Process Engine. Kräver {tier} eller högre.',
  finalUpsellCta: 'Se prisplaner',
  finalPhaseHint: 'Slutleveranssidan färdigställs i en kommande fas.',
  blockCreated: 'Block tillagt',
  toggleCollapse: 'Växla minimering',
  toggleLock: 'Växla lås',
};

const en: TranslationKeys = {
  appTitle: 'EngiCoro Flow Weaver',
  save: 'Save',
  load: 'Load',
  exportPng: 'Export Image',
  import_: 'Import',
  copyJson: 'Copy JSON',
  simulate: 'Simulate',
  clear: 'Clear',
  connectedDb: 'Connected to database',
  usingLocal: 'Using localStorage',
  db: 'DB',
  local: 'Local',
  help: 'Help',

  // MobileTopBar / drawer (#20, Wave D)
  aiAndJson: 'AI & JSON',
  aiAssistant: 'AI Assistant',
  cloudWorkspaces: 'Cloud Workspaces',
  shareWorkspace: 'Share Workspace',
  reportBug: 'Report a Bug',
  featureRequest: 'Feature Request',
  adminDashboard: 'Admin Dashboard',
  signIn: 'Sign in',
  signOut: 'Sign out',
  openMenu: 'Open menu',
  menu: 'Menu',
  notSignedIn: 'Not signed in',
  gettingStartedTab: 'Getting Started',
  gettingStartedTitle: 'Welcome to Flow Weaver',
  gettingStartedDesc: 'Flow Weaver is a visual tool for creating flowcharts and orchestration diagrams. Drag blocks, connect them, and organize them into groups and layers — all directly in your browser. The app can run standalone or be embedded in another project via iframe.',
  gsWhatIsTitle: '🎯 What is Flow Weaver?',
  gsWhatIsDesc: 'A drag-and-drop tool for building visual flows. Perfect for documenting IT processes, system architecture, onboarding flows, automations, and much more. Supports unlimited layer nesting, view-only mode for end users, and export to PNG/SVG.',
  gsAddBlocksTitle: '➕ Adding Blocks',
  gsAddBlocksDesc: 'Drag a block from the left sidebar and drop it onto the canvas. Each block represents a step in your flow. Choose from predefined types (Create User, Webhook, Condition, etc.) or use "General Function". Click a block to edit its icon, name, and description.',
  gsConnectTitle: '🔗 Connecting Blocks',
  gsConnectDesc: 'Click the "Connect blocks" button in the sidebar. Then click on a port (the small circles around blocks) to start the connection, and click a port on another block to complete it. Blocks have 8 ports (top, right, bottom, left + corners) for flexible routing. Double-click a connector line to add a waypoint — waypoints bend the curve smoothly. Double-click a waypoint to remove it.',
  gsGroupTitle: '📦 Grouping Blocks',
  gsGroupDesc: 'Click the "Group blocks" button, then click on the blocks you want to group (they get highlighted with a ring), and click the button again to create the group. Groups get a colored frame and a label. You can lock groups so all blocks move together.',
  gsEditTitle: '✏️ Editing Properties',
  gsEditDesc: 'Click on a block or group to open the properties panel on the right. There you can change the name, description (supports line breaks), icon, and color. The minimap at the bottom shows the entire canvas layout and your current viewport.',
  gsLayersTitle: '🧩 Layers & Nesting',
  gsLayersDesc: 'Groups can be collapsed into compact cards representing entire layers. Click the layer icon (⧉) in the sidebar to collapse/expand. Double-click a collapsed card to dive into that layer. Breadcrumbs at the top-left show where you are and let you navigate back. Individual blocks can be converted to layers via the layer button — this creates an empty group you can fill with content. Unlimited nesting is supported.',
  gsSaveLoadTitle: '💾 Save & Load',
  gsSaveLoadDesc: 'Click "Save" to save your flow. Without a database, it is saved in the browser\'s localStorage. With a database connection (via postMessage from the host app), it is saved permanently. Use "Load" to open saved flows. Documents can be categorized and pinned.',
  gsImportExportTitle: '📤 Import & Export',
  gsImportExportDesc: 'Use "Copy JSON" to export your flow as JSON text. Use "Import" to paste JSON and load a flow. Export as PNG or SVG — choose between "Current workspace" (entire canvas) or "Full overview" (all layers unfolded). The "Overview" button shows the full hierarchy with nested layers, internal connectors, and cross-entity connections.',
  gsAiTitle: '🤖 Create Flows with AI',
  gsAiDesc: 'Click the "AI & JSON" button in the toolbar to open a dialog with a ready-made AI prompt and JSON reference. Copy the prompt to any AI (ChatGPT, Claude, Grok, etc.), describe your system, and import the generated JSON directly into Flow Weaver.',
  gsKeyboardTitle: '⌨️ Navigation, Zoom & Shortcuts',
  gsKeyboardShortcuts: 'Ctrl/Cmd + scroll to zoom in/out\n• Scroll to pan\n• Drag blocks to move them\n• Zoom controls at top-right (+ / − / Reset)\n• Double-click collapsed layer to dive in\n• Press Enter on collapsed layer to open it\n• Breadcrumbs for layer navigation\n• Double-click a connector to add a waypoint\n• Double-click a waypoint to remove it\n• Arrow keys to navigate between blocks\n• Ctrl+Z / Cmd+Z to undo\n• Ctrl+Shift+Z / Cmd+Shift+Z to redo\n• Ctrl+S / Cmd+S to save\n• Delete / Backspace to remove selected block\n• Escape to deselect',
  tools: 'Tools',
  createUser: 'Activity',
  disableMailbox: 'Communication',
  deleteM365: 'System',
  sendEmail: 'Notification',
  webhook: 'Event Trigger',
  addToGroup: 'Stakeholder',
  condition: 'Decision',
  generalFunction: 'Process',
  actions: 'Actions',
  connecting: 'Connecting...',
  connectBlocks: 'Connect blocks',
  grouping: 'Grouping',
  groupBlocks: 'Group blocks',
  blocks: 'Blocks',
  groups: 'Groups',
  changeColor: 'Change color',
  dragToCanvas: 'Drag tools to canvas',
  lockGroup: 'Lock group',
  unlockGroup: 'Unlock group',
  search: 'Search...',
  searchCanvasPlaceholder: 'Search everything…',
  searchCanvasTooltip: 'Type to filter. Enter opens Search Canvas. Syntax: "phrase", -exclude, name:value, type:webhook, source:atlas. Cmd+K to focus.',
  searchCanvasScopeAll: 'All',
  searchCanvasScopeWorkflows: 'Workflows',
  searchCanvasScopeAtlas: 'Atlas',
  searchCanvasMatchCount: '{count} matches',
  collapseAll: 'Collapse all',
  collapseToLayers: 'Collapse to layers',
  expandAll: 'Expand all',
  groupBatchActions: 'Group actions',
  coopJoined: 'Co-op workspace joined',
  coopAccessDenied: 'No access to this workspace',
  coopAccessDeniedHint: 'Ask the owner to enable Co-op and invite you.',
  coopLoadFailed: 'Could not load shared workspace',
  coopEnableTitle: 'Enable Co-op',
  coopEnableHint: 'Must be on for the share link to work',
  coopActive: 'Co-op is active',
  coopInactive: 'Co-op is off',
  coopCollaborators: 'Collaborators',
  auditHistory: 'History',
  auditEmpty: 'No changes recorded yet.',
  groupProperties: 'Group Properties',
  blockProperties: 'Block Properties',
  name: 'Name',
  description: 'Description',
  details: 'Details…',
  recipient: 'Recipient',
  subject: 'Subject',
  blockColor: 'Block color',
  groupColor: 'Group color',
  templateSettings: 'Template Settings',
  trigger: 'Trigger',
  userCreated: 'User Created',
  userModified: 'User Modified',
  userDeleted: 'User Deleted',
  custom: 'Custom',
  overview: 'Overview',
  clickToConfig: 'Click on a block or group to configure',
  blocksInGroup: 'blocks in group',
  writeDescription: 'Write a description... (supports line breaks)',
  zoomIn: 'Zoom in',
  zoomOut: 'Zoom out',
  resetZoom: 'Reset zoom',
  addBlock: 'Add block',
  noBlockTypes: 'No block types available',
  fitToContent: 'Fit to content',
  saved: 'Saved!',
  savedToLocal: 'Workflow saved to localStorage',
  loaded: 'Loaded!',
  loadedFromLocal: 'Workflow loaded from localStorage',
  noDataFound: 'No data found',
  couldNotFindWorkflow: 'Could not find saved workflow',
  cleared: 'Cleared!',
  canvasCleared: 'Canvas has been cleared',
  imported: 'Imported!',
  importedFromJson: 'Imported from JSON',
  copied: 'Copied!',
  jsonCopied: 'Workflow JSON copied to clipboard',
  simulatingTitle: 'Simulating...',
  simulatingDesc: 'Running',
  groupCreated: 'Group created',
  blocksGrouped: 'blocks grouped',
  savedToDb: 'saved to database',
  error: 'Error',
  saveError: 'Error saving',
  loadError: 'Error loading',
  deleteError: 'Error deleting',
  deleted: 'Deleted',
  documentDeleted: 'Document has been deleted',
  exportRefSaved: 'Export reference saved',
  exportRefDesc: 'A PNG export reference has been saved to the database',
  exportPngDesc: 'PNG export requires dom-to-image library',
  couldNotCopy: 'Could not copy to clipboard',
  couldNotReadWorkflow: 'Could not read workflow data',
  saveWorkflow: 'Save Workflow',
  saveAsNew: 'Save as new',
  titleLabel: 'Title',
  categoryLabel: 'Category',
  cancel: 'Cancel',
  loadWorkflow: 'Load Workflow',
  noDocuments: 'No saved documents',
  loadBtn: 'Load',
  deleteBtn: 'Delete',
  importJson: 'Import JSON',
  pasteJsonHere: 'Paste WorkflowState JSON here...',
  importBtn: 'Import',
  invalidJson: 'Invalid JSON',
  invalidStructure: 'JSON is missing required structure (blocks, connectors, groups)',
  helpTitle: 'Instructions & Documentation',
  integrationTab: 'Integration',
  databaseTab: 'Database',
  jsonTab: 'JSON Mapping',
  integrationTitle: 'Integrate into your project',
  integrationDesc: 'Flow Weaver can run standalone or be embedded in another project via iframe. It supports two modes: edit mode (default) and view-only mode (via ?mode=view). In view-only mode, all editing features are hidden — users can only view, zoom, navigate layers, load, and export.',
  integrationStep1: '1. Publish the app via Lovable and copy the published URL (e.g. https://flow-weaver-05.lovable.app)',
  integrationStep2: '2. Embed with <iframe src="URL?mode=view" width="100%" height="600px" /> for view-only, or without ?mode=view for edit mode',
  integrationStep3: '3. Without database integration, everything is saved in the browser\'s localStorage — perfect for testing',
  integrationStep4: '4. For database storage: send your Supabase credentials to the iframe via postMessage (see code example below). The iframe automatically listens for the "supabase-credentials" message',
  integrationStep5: '5. Use ?mode=view for end users (read-only) and the standard URL without parameter for administrators (edit mode)',
  integrationStep6: '6. The user must be logged in to the host app — the access token from the session is sent to authenticate against the database',
  integrationStep7: '7. If no credentials are sent, the iframe falls back to localStorage (no data is lost). View-only mode works regardless of database connection',
  integrationCodeTitle: 'Code example — send credentials from the host app',
  integrationCodeExample: `import { useRef, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

function FlowchartPage({ isAdmin = false }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const mode = isAdmin ? '' : '?mode=view';

  const sendCredentials = useCallback(async () => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;

    iframe.contentWindow.postMessage({
      type: 'supabase-credentials',
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
      supabaseKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      accessToken: session.access_token,
    }, '*');
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'request-supabase-credentials') {
        sendCredentials();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [sendCredentials]);

  return (
    <iframe
      ref={iframeRef}
      src={\`https://flow-weaver-05.lovable.app/\${mode}\`}
      onLoad={() => setTimeout(sendCredentials, 500)}
      className="w-full h-full"
    />
  );
}`,
  integrationNote: '⚠️ View-only mode (?mode=view): Hides TopBar, ToolSidebar, and PropertiesPanel. Shows a minimal topbar with Load, Export Image, and Overview. All editing features (drag, connections, ports) are disabled. Zoom, scroll, and layer navigation work as usual.',
  databaseTitle: 'Database requirements for the host app',
  databaseDesc: 'The following is required in the host app for database storage to work. Flow Weaver sends workflow data as JSON to an edge function that handles saving.',
  databaseReq1: 'Table: workflows\n— id (uuid PK, default gen_random_uuid())\n— title (text, NOT NULL)\n— blocks (jsonb, NOT NULL, default \'[]\')\n— connectors (jsonb, NOT NULL, default \'[]\')\n— groups (jsonb, NOT NULL, default \'[]\')\n— workflow_name (text, default \'\')\n— coordinate_origin (text, default \'center\')\n— category (text, default "workflow")\n— created_by (uuid, NOT NULL — reference to user\'s auth.uid())\n— is_pinned (boolean, default false)\n— pinned_at (timestamptz, nullable)\n— pinned_by (uuid, nullable)\n— last_modified_by (uuid, nullable)\n— created_at (timestamptz, default now())\n— updated_at (timestamptz, default now())',
  databaseReq2: 'A "save-workflow" edge function that: (1) Validates JWT token via getClaims, (2) Extracts user ID from token, (3) Accepts { id?, title, blocks, connectors, groups, workflow_name, coordinate_origin, category } in request body, (4) Does INSERT for new workflows or UPDATE for existing ones, (5) Returns { id, title } on success',
  databaseReq3: 'RLS policies on the workflows table:\n— SELECT: auth.uid() = created_by\n— INSERT: auth.uid() = created_by\n— UPDATE: auth.uid() = created_by\n— DELETE: auth.uid() = created_by\nThis ensures each user can only view and manage their own workflows',
  databaseReq4: 'Authentication flow: User logs in to host app → host app sends access_token via postMessage to the iframe → Flow Weaver uses the token to authenticate against the database. Without a token, the app operates in localStorage mode',
  jsonTitle: 'JSON Mapping',
  jsonDesc: 'Workflows are stored as JSON with the following structure (WorkflowState):',
  jsonStep1: '1. Click "Copy JSON" in the toolbar to export the current flow',
  jsonStep2: '2. Click "Import" to paste and load a flow from JSON',
  jsonStep3: '3. Each block has id, type, x, y, config (name, description, icon) and color. Blocks can be converted to layers (empty collapsed groups) via the layer button.',
  jsonStep4: '4. Connectors reference fromId/toId. Groups can have 0+ blocks, support nesting via parentGroupId, and can be collapsed into layers with collapsed/collapsedX/collapsedY.',
  jsonStructure: '{ "name": "...", "blocks": [...], "connectors": [{ fromId, toId, fromPort, toPort }], "groups": [{ id, blockIds, color, name, collapsed, collapsedX, collapsedY, parentGroupId, locked }] }',
  aiPromptTab: 'AI Prompt',
  aiPromptTitle: 'Generate flows with AI',
  aiPromptDesc: 'You can use any AI (ChatGPT, Grok, Claude, Lovable, etc.) to automatically generate JSON that can be imported directly into Flow Weaver. Give the AI the prompt below along with your material.',
  aiPromptUseCases: 'Use cases',
  aiPromptUseCase1: 'Ask an AI to map an existing project or system into a flowchart',
  aiPromptUseCase2: 'Upload images (architecture diagrams, whiteboards) and ask the AI to convert to JSON',
  aiPromptUseCase3: 'Send documentation, specifications or requirement lists and get a visual flow',
  aiPromptUseCase4: 'Describe a process in words and let the AI build the flowchart for you',
  aiPromptUseCase5: 'Build complex multi-layer architectures with nested layers — high-level overview at root, detailed logic inside',
  aiPromptTemplateTitle: 'Copyable AI prompt',
  aiPromptTemplate: `You are a JSON generator for Flow Weaver. Return ONLY a valid JSON object (WorkflowState). No explanatory text.

You are an EXPERT architect — design workflows with clear hierarchies, logical groupings, and optimal connector routing. Think in layers: high-level overview at root, detailed logic inside nested groups.

══════════════════════════════════
  SCHEMA
══════════════════════════════════
{
  "name": "Flow name",
  "blocks": [
    { "id": "b01", "type": "general", "x": 200, "y": 100,
      "config": { "name": "Block name", "description": "Detailed description\\nMulti-line supported", "icon": "wrench" },
      "color": "#6366f1" }
  ],
  "connectors": [
    { "id": "c01", "fromId": "b01", "toId": "b02", "fromPort": "right", "toPort": "left" }
  ],
  "groups": [
    { "id": "g1", "blockIds": ["b01","b02"], "color": "#8b5cf6", "name": "Group name",
      "locked": false, "collapsed": true, "parentGroupId": null,
      "collapsedX": 200, "collapsedY": 100 }
  ]
}

══════════════════════════════════
  1. BLOCK TYPES
══════════════════════════════════
"create-user" | "disable-mailbox" | "delete-m365" | "send-email"
"webhook"      | "add-to-group"   | "condition"    | "general"

Use "general" for any step not covered above. Use "condition" for decision points / branching.

══════════════════════════════════
  2. ICONS (by category)
══════════════════════════════════
People:    user, users
Comms:     mail, send, message-square, phone, bell
Cloud/Net: cloud, globe, server, database, link
Files:     file-text, folder, archive, download, upload, copy
Tools:     wrench, settings, terminal, filter, search
Status:    check-circle, alert-triangle, clock, flag, star
Security:  shield, lock, key, eye
Media:     play, camera, monitor
Logic:     git-branch, shuffle, layers, refresh-cw
Other:     zap, power, heart, tag, bookmark, bar-chart, package, trash, thumbs-up, calendar, help-circle

══════════════════════════════════
  3. GRID & COORDINATES
══════════════════════════════════
- x and y MUST be multiples of 20
- Horizontal spacing between blocks: ~240px
- Vertical spacing between blocks: ~220px
- ID format: "b01", "b02", ... (blocks), "c01", "c02", ... (connectors), "g1", "g2", ... (groups)
- All IDs must be globally unique
- Blocks inside a collapsed group use their own coordinate space

══════════════════════════════════
  4. PORT ROUTING (CRITICAL!)
══════════════════════════════════
Determine fromPort/toPort by comparing source (x,y) with target (x,y):

  Direction            │ fromPort       │ toPort
  ─────────────────────┼────────────────┼────────────────
  → Right (same y)     │ "right"        │ "left"
  ← Left (same y)      │ "left"         │ "right"
  ↓ Down (same x)      │ "bottom"       │ "top"
  ↑ Up (same x)        │ "top"          │ "bottom"
  ↘ Diag down-right    │ "bottom-right" │ "top-left"
  ↙ Diag down-left     │ "bottom-left"  │ "top-right"
  ↗ Diag up-right      │ "top-right"    │ "bottom-left"
  ↖ Diag up-left       │ "top-left"     │ "bottom-right"

⚠ NEVER use diagonal ports for blocks on the same row (y) or column (x).
⚠ fromPort and toPort MUST always be specified.
⚠ Auto-snap will correct suboptimal routing, but get it right for clean results.

══════════════════════════════════
  5. GROUPS & NESTING
══════════════════════════════════
Group fields:
  "id":            Unique group ID (e.g. "g1", "g2")
  "blockIds":      Array of block IDs belonging to this group (CAN BE EMPTY [] for layers)
  "color":         Hex color (e.g. "#8b5cf6")
  "name":          Display name for the group
  "locked":        false (default) — when true, blocks move together
  "collapsed":     true/false — when true, group appears as compact card (LAYER MODE)
  "parentGroupId": null for top-level, or parent group's ID for nesting
  "collapsedX":    X position of compact card when collapsed (multiple of 20)
  "collapsedY":    Y position of compact card when collapsed (multiple of 20)

Rules:
- Groups can have 0 or more blocks (empty groups = empty layers)
- A single block can be converted to a layer (creates an empty collapsed group)
- Blocks can only belong to ONE group
- Nested groups use INDEPENDENT coordinate systems (each layer is its own canvas)
- parentGroupId links child groups to parent groups for hierarchy

══════════════════════════════════
  6. LAYER SYSTEM (ADVANCED)
══════════════════════════════════
Groups with "collapsed": true become navigable layers:

🏗️ ARCHITECTURE PATTERN:
  Root level:  High-level overview with collapsed groups as compact cards
  Layer 1:     Subsystems — each collapsed group expands into its own canvas
  Layer 2+:    Detailed implementation logic inside nested groups

🔄 BLOCK-TO-LAYER CONVERSION:
  - Individual blocks can be converted to layers via the layer button
  - This creates an empty collapsed group at the block's position
  - All connectors to/from the block are remapped to the new layer
  - Use this to create "placeholder layers" to be filled later

📐 LAYOUT STRATEGY:
  - Space collapsed groups like blocks (~240px apart) at parent level
  - Use collapsedX/collapsedY to position the compact cards
  - Inside each group: layout blocks independently (own coordinate space, start from x:200, y:100)

🔗 CROSS-LAYER CONNECTORS:
  - Connectors between blocks in different collapsed groups appear as
    connections between the group cards at the parent level
  - Connectors can reference group IDs directly (fromId/toId = group ID)

📋 EXAMPLE — 3-tier architecture:
  Root:  [Auth Group] → [API Group] → [Database Group]
  Auth:  [Login] → [Validate] → [Token]
  API:   [Router] → [Controller] → [Middleware]
  DB:    [Query] → [Cache] → [Storage]

  JSON structure:
  - 3 groups with collapsed: true, collapsedX/Y at root level
  - Connectors between group IDs at root level
  - Blocks inside each group with their own coordinates

══════════════════════════════════
  7. DESIGN PATTERNS
══════════════════════════════════
📊 PIPELINE: Linear flow → use "right"/"left" ports, same Y
📐 BRANCHING: Condition with multiple paths → "bottom"/"right" ports
🔄 FEEDBACK LOOP: Return arrow → use "left"/"top" ports back
🏢 HIERARCHICAL: Manager groups with worker sub-groups
⚡ EVENT-DRIVEN: Webhook triggers with parallel paths
🎨 COLOR CODING: Use different colors per domain/concern:
   Infrastructure: "#6366f1"  API: "#8b5cf6"  Auth: "#ec4899"
   Data: "#06b6d4"  UI: "#22c55e"  Monitoring: "#f97316"

  ══════════════════════════════════
  CHECKLIST BEFORE OUTPUT
══════════════════════════════════
✅ All x/y are multiples of 20
✅ All connectors have fromPort + toPort
✅ Port choices match relative position (see table above)
✅ All IDs are globally unique
✅ Groups can be empty (blockIds: []) — no minimum requirement
✅ Collapsed groups have collapsedX/collapsedY set
✅ parentGroupId references valid group ID (or null)
✅ Blocks inside layers have independent coordinates (own canvas)
✅ Block descriptions are detailed and actionable
✅ Color-code groups by domain/concern for visual clarity
✅ Waypoints (optional): add "waypoints": [{"x":…,"y":…}] to a connector to manually guide the curve
✅ Always include "coordinateOrigin": "center" at root level
✅ Output is ONLY JSON`,
  aiPromptTipsTitle: 'Tips for best results',
  aiPromptTip1: 'Attach images of existing diagrams — the AI can interpret and convert them to JSON',
  aiPromptTip2: 'Describe the process steps as bullet points for a clearer result',
  aiPromptTip3: 'Specify the desired number of blocks and groupings in your prompt for more control',
  aiPromptTip4: 'Copy the JSON result and paste it via the Import button in Flow Weaver',
  aiPromptCopy: 'Copy prompt',
  aiPromptCopied: 'Prompt copied!',
  close: 'Close',
  saveFile: 'Save file',
  loadFile: 'Load file',
  welcome: 'Welcome',
  savedAsFile: 'Workflow saved as file',
  donate: 'Donate',
  undo: 'Undo',
  redo: 'Redo',
  rename: 'Rename',
  editName: 'Edit name',
  editDescription: 'Edit description',
  editColor: 'Edit color',
  editBlock: 'Edit block',
  editGroup: 'Edit group',
  editLayer: 'Edit layer',
  groupThisBlock: 'Group this block',
  createGroupFromSelection: 'Create group from selection',
  convertToLayer: 'Convert to layer',
  duplicate: 'Duplicate',
  explainBlock: 'Explain block…',
  delete: 'Delete',
  deleteBlock: 'Delete block',
  deleteGroup: 'Delete group',
  collapseToLayer: 'Collapse to layer',
  enterLayer: 'Enter layer',
  expandLayer: 'Expand (convert back to group)',
  keyboardShortcutsTitle: 'Keyboard shortcuts',
  keyboardShortcutsHint: 'Press ? to show this panel again.',
  blocksGroupedCount_one: '{{count}} block grouped',
  blocksGroupedCount_other: '{{count}} blocks grouped',
  blocksCount_one: '{{count}} block',
  blocksCount_other: '{{count}} blocks',
  connectorsCount_one: '{{count}} connector',
  connectorsCount_other: '{{count}} connectors',
  groupsCount_one: '{{count}} group',
  groupsCount_other: '{{count}} groups',
  export: 'Exporting',
  pleaseWait: 'This may take a few seconds for large canvases…',
  exportDone: 'Export done',
  auditCreated: 'Created',
  auditDeleted: 'Deleted',
  auditRenamed: 'Renamed',
  auditEdited: 'Edited',
  auditCoopEnabled: 'Co-op enabled',
  auditCoopDisabled: 'Co-op disabled',
  auditRedactedActor: '[deleted user]',
  auditCollabInvite: 'Invited user',
  auditCollabRemove: 'Removed user',
  auditCollabRoleChange: 'Changed role for user',
  auditCollabAccept: 'Accepted invite:',
  crossSheetLink: 'Cross-sheet link',
  crossSheetActive: 'Cross-sheet linking…',
  crossSheetHint: 'Link blocks between adjacent sheets',
  crossSheetActiveHint: 'Press Esc to cancel',
  back: 'Back',
  processEngineDisabled: 'Process Engine is disabled in this install.',
  processEngineUpsellTitle: 'Unlock Process Engine',
  processEngineUpsellBody: 'Build attestation, provisioning and offboarding flows from your Flow-weaver diagrams. Requires {tier} or higher.',
  processEngineUpsellCta: 'View plans',
  processEngineLoadTitle: 'Load a Flow-weaver flow',
  processEngineLoadFromFile: 'From file (.json)',
  processEngineLoadFromPaste: 'Paste JSON',
  processEngineLoadImport: 'Import',
  processEngineImportSuccess: 'Flow imported',
  processEngineImportFailed: 'Could not import flow',
  processEngineLiveAvailable: 'Live runs available',
  processEngineDesignOnly: 'Design only — upgrade to {tier} for live runs',
  processEngineDryRun: 'Dry run',
  processEngineDryRunDone: 'Dry run complete',
  processEngineRunning: 'Running…',
  processEngineLoadFromTemplate: 'Or start from a template',
  processEngineOpenFinal: 'Final view',
  processEngineSuggest: 'AI suggest',
  processEngineSuggestNone: 'No confident matches',
  processEngineSuggestDone: 'Suggestions added',
  processEngineRunLive: 'Run live',
  processEngineRunDone: 'Live run complete',
  processEngineRunFailed: 'Live run failed',
  processEngineRunBlocked: 'Live run is blocked',
  processEngineSave: 'Save',
  processEngineSaving: 'Saving…',
  processEngineSaveDone: 'Saved',
  processEngineSaveFailed: 'Save failed',
  processEngineLoadFromSaved: 'Saved flows',
  finalTldr: 'Summary',
  finalPageTitle: 'Final',
  finalSubtitle: 'A complete picture across design, knowledge, and operation.',
  finalAudience: 'Audience',
  finalHubTitle: 'System overview',
  finalHubSubtitle:
    'A live read-out of each system. Open a container to dive in, or promote a flow from Process Engine to see the detailed three-act narrative below.',
  finalNoFlowTitle: 'No flow selected',
  finalNoFlowBody: 'Open Process Engine and load a flow first.',
  finalGoToProcessEngine: 'Open Process Engine',
  finalContainerFlowWeaverTitle: 'Flow-weaver',
  finalContainerFlowWeaverEmpty: 'No workflow saved yet — open Flow-weaver to start.',
  finalContainerOpenFlowWeaver: 'Open Flow-weaver',
  finalContainerProcessEngineTitle: 'Process Engine',
  finalContainerProcessEngineSubtitle: '{count} saved flow(s)',
  finalContainerProcessEngineEmpty: 'No saved flows yet — promote a workflow to Process.',
  finalContainerOpenProcessEngine: 'Open Process Engine',
  finalContainerAtlasTitle: 'Atlas',
  finalContainerAtlasEmpty: 'Atlas manifest not loaded.',
  finalContainerOpenAtlas: 'Open Atlas',
  finalContainerGroups: 'Groups',
  finalContainerFlows: 'Flows',
  finalContainerTotalBlocks: 'Total blocks',
  finalContainerCategories: 'Categories',
  finalActDesign: 'Design',
  finalActDesignSubtitle: 'How the process is modelled in Flow-weaver.',
  finalActKnowledge: 'Knowledge',
  finalActKnowledgeSubtitle: 'What the organisation knows and links across in Atlas.',
  finalActOperation: 'Operation',
  finalActOperationSubtitle: 'How the flow behaves in production over 30 days.',
  finalSheets: 'Sheets',
  finalBlocks: 'Blocks',
  finalConnectors: 'Connectors',
  finalBindings: 'Bindings',
  finalAtlas: 'Atlas',
  finalDocs: 'Docs',
  finalCrossDocRefs: 'Cross-doc refs',
  finalRuns30d: 'Runs (30 d)',
  finalSuccessRate: 'Success rate',
  finalMeanDuration: 'Mean duration',
  finalRoi: 'Value',
  finalRoiHoursSaved: 'Hours saved',
  finalRoiErrorsPrevented: 'Errors prevented',
  finalRoiMtto: 'MTTO',
  finalRoiValue: 'Value',
  finalRoiMethodology: 'Value is computed conservatively at 30 min of saved manual work per successful run × a blended hourly rate of €45.',
  finalWatermark: 'Generated: {watermark}',
  finalDisabled: 'Final view is disabled in this install.',
  finalUpsellTitle: 'Unlock the Final view',
  finalUpsellBody: 'A read-only executive narrative across Flow-weaver, Atlas and Process Engine. Requires {tier} or higher.',
  finalUpsellCta: 'View plans',
  finalPhaseHint: 'The Final view ships in an upcoming phase.',
  blockCreated: 'Block added',
  toggleCollapse: 'Toggle collapse',
  toggleLock: 'Toggle lock',
};

const no: TranslationKeys = {
  appTitle: 'EngiCoro Flow Weaver',
  save: 'Lagre',
  load: 'Last',
  exportPng: 'Eksporter bilde',
  import_: 'Importer',
  copyJson: 'Kopier JSON',
  simulate: 'Simuler',
  clear: 'Tøm',
  connectedDb: 'Tilkoblet database',
  usingLocal: 'Bruker localStorage',
  db: 'DB',
  local: 'Lokal',
  help: 'Hjelp',

  // MobileTopBar / drawer (#20, Wave D)
  aiAndJson: 'AI og JSON',
  aiAssistant: 'AI-assistent',
  cloudWorkspaces: 'Skyarbeidsområder',
  shareWorkspace: 'Del arbeidsområde',
  reportBug: 'Rapporter feil',
  featureRequest: 'Funksjonsønske',
  adminDashboard: 'Adminpanel',
  signIn: 'Logg inn',
  signOut: 'Logg ut',
  openMenu: 'Åpne meny',
  menu: 'Meny',
  notSignedIn: 'Ikke logget inn',
  gettingStartedTab: 'Kom i gang',
  gettingStartedTitle: 'Velkommen til Flow Weaver',
  gettingStartedDesc: 'Flow Weaver er et visuelt verktøy for å lage flytskjemaer og orkestreringsdiagrammer. Dra blokker, koble dem sammen og organiser dem i grupper — alt direkte i nettleseren.',
  gsWhatIsTitle: '🎯 Hva er Flow Weaver?',
  gsWhatIsDesc: 'Et dra-og-slipp-verktøy for å bygge visuelle flyter. Perfekt for å dokumentere IT-prosesser, systemarkitektur, onboarding-flyter, automatiseringer og mye mer.',
  gsAddBlocksTitle: '➕ Legge til blokker',
  gsAddBlocksDesc: 'Dra en blokk fra sidepanelet til venstre og slipp den på lerretet. Hver blokk representerer et steg i flyten din. Velg mellom forhåndsdefinerte typer eller bruk den generelle "Generell funksjon".',
  gsConnectTitle: '🔗 Koble blokker',
  gsConnectDesc: 'Klikk på "Koble blokker"-knappen i sidepanelet, klikk deretter på en port (de små sirklene rundt blokkene) for å starte koblingen, og klikk på en annen port for å fullføre. Blokker har 8 porter (topp, høyre, bunn, venstre + hjørner). Dobbeltklikk på en linje for å legge til et veipunkt — veipunkter bøyer kurven mykt. Dobbeltklikk på et veipunkt for å fjerne det.',
  gsGroupTitle: '📦 Gruppere blokker',
  gsGroupDesc: 'Klikk på "Grupper blokker"-knappen, klikk deretter på blokkene du vil gruppere (de markeres), og klikk på knappen igjen for å opprette gruppen.',
  gsEditTitle: '✏️ Redigere egenskaper',
  gsEditDesc: 'Klikk på en blokk eller gruppe for å åpne egenskapspanelet til høyre. Der kan du endre navn, beskrivelse, ikon og farge.',
  gsSaveLoadTitle: '💾 Lagre og laste',
  gsSaveLoadDesc: 'Klikk "Lagre" for å lagre flyten. Uten database lagres den i nettleserens localStorage. Med databasetilkobling lagres den permanent.',
  gsImportExportTitle: '📤 Import og eksport',
  gsImportExportDesc: 'Bruk "Kopier JSON" for å eksportere flyten som JSON-tekst. Bruk "Importer" for å lime inn JSON og laste en flyt. Du kan også eksportere som PNG-bilde.',
  gsAiTitle: '🤖 Lag flyter med AI',
  gsAiDesc: 'Klikk på "AI & JSON"-knappen i verktøylinjen for å åpne en dialog med en ferdig AI-prompt og JSON-referanse. Kopier prompten til valgfri AI (ChatGPT, Claude, Grok osv.), beskriv systemet ditt og importer det genererte JSON-resultatet direkte i Flow Weaver.',
  gsKeyboardTitle: '⌨️ Navigering & Snarveier',
  gsKeyboardShortcuts: 'Scroll for å zoome inn/ut\n• Klikk og dra på lerretet for å panorere\n• Dra blokker for å flytte dem\n• Dobbeltklikk på kollapsert lag for å dykke inn\n• Trykk Enter på kollapsert lag for å åpne det\n• Brødsmuler for lagnavigering\n• Dobbeltklikk på en linje for å legge til veipunkt\n• Dobbeltklikk på veipunkt for å fjerne det\n• Piltaster for å navigere mellom blokker\n• Ctrl+Z / Cmd+Z for å angre\n• Ctrl+Shift+Z / Cmd+Shift+Z for å gjøre om\n• Ctrl+S / Cmd+S for å lagre\n• Delete / Backspace for å slette valgt blokk\n• Escape for å fjerne markering',
  gsLayersTitle: '🧩 Lag & Nesting',
  gsLayersDesc: 'Grupper kan kollapses til kompakte kort som representerer hele lag. Klikk på lag-ikonet (⧉) ved siden av en gruppe i sidepanelet for å kollapse/utvide den. Dobbeltklikk på et kollapsert kort for å dykke inn i laget. Brødsmuler vises for å navigere tilbake. Ubegrenset nesting støttes.',
  tools: 'Verktøy',
  createUser: 'Aktivitet',
  disableMailbox: 'Kommunikasjon',
  deleteM365: 'System',
  sendEmail: 'Varsling',
  webhook: 'Hendelse',
  addToGroup: 'Interessent',
  condition: 'Beslutning',
  generalFunction: 'Prosess',
  actions: 'Handlinger',
  connecting: 'Kobler...',
  connectBlocks: 'Koble blokker',
  grouping: 'Grupperer',
  groupBlocks: 'Grupper blokker',
  blocks: 'Blokker',
  groups: 'Grupper',
  changeColor: 'Bytt farge',
  dragToCanvas: 'Dra verktøy til lerret',
  lockGroup: 'Lås gruppe',
  unlockGroup: 'Lås opp gruppe',
  search: 'Søk...',
  searchCanvasPlaceholder: 'Søk alt…',
  searchCanvasTooltip: 'Skriv for å filtrere. Enter åpner Søk-canvas. Cmd+K for å fokusere.',
  searchCanvasScopeAll: 'Alt',
  searchCanvasScopeWorkflows: 'Arbeidsflyter',
  searchCanvasScopeAtlas: 'Atlas',
  searchCanvasMatchCount: '{count} treff',
  collapseAll: 'Kollaps alt',
  collapseToLayers: 'Kollaps til lag',
  expandAll: 'Utvid alt',
  groupBatchActions: 'Gruppehandlinger',
  coopJoined: 'Co-op arbeidsområde tilkoblet',
  coopAccessDenied: 'Ingen tilgang til arbeidsområdet',
  coopAccessDeniedHint: 'Be eieren aktivere Co-op og invitere deg.',
  coopLoadFailed: 'Kunne ikke laste delt arbeidsområde',
  coopEnableTitle: 'Aktiver Co-op',
  coopEnableHint: 'Må være på for at delingslenken skal fungere',
  coopActive: 'Co-op er aktivt',
  coopInactive: 'Co-op er av',
  coopCollaborators: 'Medvirkende',
  auditHistory: 'Historikk',
  auditEmpty: 'Ingen endringer registrert ennå.',
  groupProperties: 'Gruppeegenskaper',
  blockProperties: 'Blokkegenskaper',
  name: 'Navn',
  description: 'Beskrivelse',
  details: 'Detaljer…',
  recipient: 'Mottaker',
  subject: 'Emne',
  blockColor: 'Blokkfarge',
  groupColor: 'Gruppefarge',
  templateSettings: 'Malinnstillinger',
  trigger: 'Utløser',
  userCreated: 'Bruker Opprettet',
  userModified: 'Bruker Endret',
  userDeleted: 'Bruker Slettet',
  custom: 'Tilpasset',
  overview: 'Oversikt',
  clickToConfig: 'Klikk på en blokk eller gruppe for å konfigurere',
  blocksInGroup: 'blokker i gruppen',
  writeDescription: 'Skriv en beskrivelse... (støtter linjeskift)',
  zoomIn: 'Zoom inn',
  zoomOut: 'Zoom ut',
  resetZoom: 'Tilbakestill zoom',
  addBlock: 'Legg til blokk',
  noBlockTypes: 'Ingen blokktyper tilgjengelig',
  fitToContent: 'Tilpass til innhold',
  saved: 'Lagret!',
  savedToLocal: 'Arbeidsflyt lagret i localStorage',
  loaded: 'Lastet!',
  loadedFromLocal: 'Arbeidsflyt lastet fra localStorage',
  noDataFound: 'Ingen data funnet',
  couldNotFindWorkflow: 'Kunne ikke finne lagret arbeidsflyt',
  cleared: 'Tømt!',
  canvasCleared: 'Lerretet er tømt',
  imported: 'Importert!',
  importedFromJson: 'Importert fra JSON',
  copied: 'Kopiert!',
  jsonCopied: 'Arbeidsflyt-JSON kopiert til utklippstavle',
  simulatingTitle: 'Simulerer...',
  simulatingDesc: 'Kjører',
  groupCreated: 'Gruppe opprettet',
  blocksGrouped: 'blokker gruppert',
  savedToDb: 'lagret i databasen',
  error: 'Feil',
  saveError: 'Feil ved lagring',
  loadError: 'Feil ved lasting',
  deleteError: 'Feil ved sletting',
  deleted: 'Slettet',
  documentDeleted: 'Dokumentet er slettet',
  exportRefSaved: 'Eksportreferanse lagret',
  exportRefDesc: 'En PNG-eksportreferanse er lagret i databasen',
  exportPngDesc: 'PNG-eksport krever dom-to-image-bibliotek',
  couldNotCopy: 'Kunne ikke kopiere til utklippstavle',
  couldNotReadWorkflow: 'Kunne ikke lese arbeidsflytdata',
  saveWorkflow: 'Lagre Arbeidsflyt',
  saveAsNew: 'Lagre som ny',
  titleLabel: 'Tittel',
  categoryLabel: 'Kategori',
  cancel: 'Avbryt',
  loadWorkflow: 'Last Arbeidsflyt',
  noDocuments: 'Ingen lagrede dokumenter',
  loadBtn: 'Last',
  deleteBtn: 'Slett',
  importJson: 'Importer JSON',
  pasteJsonHere: 'Lim inn WorkflowState JSON her...',
  importBtn: 'Importer',
  invalidJson: 'Ugyldig JSON',
  invalidStructure: 'JSON mangler nødvendig struktur (blocks, connectors, groups)',
  helpTitle: 'Instruksjoner & Dokumentasjon',
  integrationTab: 'Integrasjon',
  databaseTab: 'Database',
  jsonTab: 'JSON-kartlegging',
  integrationTitle: 'Slik integrerer du',
  integrationDesc: 'Flow Weaver kan kjøres som en frittstående app eller bygges inn i et annet prosjekt via iframe.',
  integrationStep1: '1. Publiser appen via Lovable og kopier den publiserte URL-en (f.eks. https://flow-weaver-05.lovable.app)',
  integrationStep2: '2. Bygg inn med <iframe ref={iframeRef} src="URL" width="100%" height="600px" />',
  integrationStep3: '3. Uten databaseintegrasjon lagres alt i nettleserens localStorage',
  integrationStep4: '4. For å lagre til DITT prosjekts database: send dine Supabase-credentials til iframen via postMessage (se kodeeksempel nedenfor)',
  integrationStep5: '5. Iframen lytter automatisk etter meldingen "supabase-credentials" og bytter til din database',
  integrationStep6: '6. Brukeren må være innlogget i vertsappen — access token fra sesjonen sendes med',
  integrationStep7: '7. Hvis ingen credentials sendes, faller iframen tilbake til localStorage (ingen data går tapt)',
  integrationCodeTitle: 'Kodeeksempel — send credentials fra vertsappen',
  integrationCodeExample: `import { useRef, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

function FlowchartPage() {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const sendCredentials = useCallback(async () => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;

    iframe.contentWindow.postMessage({
      type: 'supabase-credentials',
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
      supabaseKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      accessToken: session.access_token,
    }, '*');
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'request-supabase-credentials') {
        sendCredentials();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [sendCredentials]);

  return (
    <iframe
      ref={iframeRef}
      src="https://flow-weaver-05.lovable.app"
      onLoad={() => setTimeout(sendCredentials, 500)}
      className="w-full h-full"
    />
  );
}`,
  integrationNote: '⚠️ Vertsappen trenger en "workflows"-tabell med kolonnene: id (uuid), title (text), blocks (jsonb), connectors (jsonb), groups (jsonb), workflow_name (text), coordinate_origin (text), category (text), created_by (uuid), created_at, updated_at, is_pinned (boolean). RLS-policyer skal tillate CRUD basert på auth.uid() = created_by. I tillegg trengs en "save-workflow" edge function som validerer JWT og håndterer insert/update.',
  databaseTitle: 'Databasekrav',
  databaseDesc: 'Vertsappen trenger følgende for at databaselagring skal fungere:',
  databaseReq1: 'Tabell: workflows — id (uuid PK), title (text), blocks (jsonb), connectors (jsonb), groups (jsonb), workflow_name (text), coordinate_origin (text), category (text, default "workflow"), created_by (uuid), is_pinned (boolean), last_modified_by (uuid), created_at, updated_at',
  databaseReq2: 'En "save-workflow" edge function som validerer JWT (via getClaims), henter bruker-ID og håndterer insert/update mot workflows-tabellen',
  databaseReq3: 'RLS-policyer: auth.uid() = created_by for SELECT, INSERT, UPDATE, DELETE',
  databaseReq4: 'Brukeren må være innlogget i vertsappen — sesjonen sendes til iframen via postMessage',
  jsonTitle: 'JSON-kartlegging',
  jsonDesc: 'Arbeidsflyter lagres som JSON med følgende struktur (WorkflowState):',
  jsonStep1: '1. Klikk "Kopier JSON" i verktøylinjen for å eksportere gjeldende flyt',
  jsonStep2: '2. Klikk "Importer" for å lime inn og laste en flyt fra JSON',
  jsonStep3: '3. Hver blokk har id, type, x, y, config (navn, beskrivelse, ikon) og color. Blokker kan konverteres til lag (tomme kollapsede grupper) via lag-knappen.',
  jsonStep4: '4. Koblinger refererer fromId/toId. Grupper kan ha 0+ blokker, støtter nesting via parentGroupId, og kan kollapses til lag med collapsed/collapsedX/collapsedY.',
  jsonStructure: '{ "name": "...", "blocks": [...], "connectors": [{ fromId, toId, fromPort, toPort }], "groups": [{ id, blockIds, color, name, collapsed, collapsedX, collapsedY, parentGroupId, locked }] }',
  aiPromptTab: 'AI-Prompt',
  aiPromptTitle: 'Generer flyter med AI',
  aiPromptDesc: 'Du kan bruke hvilken som helst AI (ChatGPT, Grok, Claude, Lovable osv.) for å automatisk generere JSON som kan importeres direkte i Flow Weaver. Gi AI-en prompten nedenfor sammen med materialet ditt.',
  aiPromptUseCases: 'Brukstilfeller',
  aiPromptUseCase1: 'Be en AI om å kartlegge et eksisterende prosjekt eller system til et flytskjema',
  aiPromptUseCase2: 'Last opp bilder (arkitekturdiagrammer, tavler) og be AI-en konvertere til JSON',
  aiPromptUseCase3: 'Send dokumentasjon, spesifikasjoner eller kravlister og få et visuelt flyt',
  aiPromptUseCase4: 'Beskriv en prosess med ord og la AI-en bygge flytskjemaet for deg',
  aiPromptUseCase5: 'Bygg komplekse flerlags-arkitekturer med nestede lag — oversikt på toppnivå, detaljert logikk inni',
  aiPromptTemplateTitle: 'Kopierbar AI-prompt',
  aiPromptTemplate: `Du er en JSON-generator for Flow Weaver. Returner KUN et gyldig JSON-objekt (WorkflowState). Ingen forklarende tekst.

══════════════════════════════════
  SKJEMA (eksempelblokk)
══════════════════════════════════
{
  "name": "Flytnavn",
  "blocks": [
    { "id": "b01", "type": "general", "x": 200, "y": 100,
      "config": { "name": "Blokknavn", "description": "Beskrivelse", "icon": "wrench" },
      "color": "#6366f1" }
  ],
  "connectors": [
    { "id": "c01", "fromId": "b01", "toId": "b02", "fromPort": "right", "toPort": "left" }
  ],
  "groups": [
    { "id": "g1", "blockIds": ["b01","b02"], "color": "#6366f1", "name": "Gruppenavn",
      "locked": false, "collapsed": true, "parentGroupId": null,
      "collapsedX": 200, "collapsedY": 100 }
  ]
}

══════════════════════════════════
  1. BLOKKTYPER
══════════════════════════════════
"create-user" | "disable-mailbox" | "delete-m365" | "send-email"
"webhook"      | "add-to-group"   | "condition"    | "general"

══════════════════════════════════
  2. IKONER (etter kategori)
══════════════════════════════════
Personer:    user, users
Kommunikasjon: mail, send, message-square, phone, bell
Sky/Nettverk:  cloud, globe, server, database, link
Filer:       file-text, folder, archive, download, upload, copy
Verktøy:     wrench, settings, terminal, filter, search
Status:      check-circle, alert-triangle, clock, flag, star
Sikkerhet:   shield, lock, key, eye
Media:       play, camera, monitor
Logikk:      git-branch, shuffle, layers, refresh-cw
Annet:       zap, power, heart, tag, bookmark, bar-chart, package, trash, thumbs-up, calendar, help-circle

══════════════════════════════════
  3. RUTENETT & KOORDINATER
══════════════════════════════════
- x og y MÅ være multipler av 20
- Horisontal avstand: ~240px
- Vertikal avstand:   ~220px
- ID-format: "b01", "b02", ... (blokker), "c01", "c02", ... (koblinger), "g1", "g2", ... (grupper)
- Alle ID-er må være unike

══════════════════════════════════
  4. PORT-ROUTING (KRITISK!)
══════════════════════════════════
Bestem fromPort/toPort ved å sammenligne source (x,y) med target (x,y):

  Retning              │ fromPort       │ toPort
  ─────────────────────┼────────────────┼────────────────
  → Høyre (samme y)    │ "right"        │ "left"
  ← Venstre (samme y)  │ "left"         │ "right"
  ↓ Ned (samme x)      │ "bottom"       │ "top"
  ↑ Opp (samme x)      │ "top"          │ "bottom"
  ↘ Diag ned-høyre     │ "bottom-right" │ "top-left"
  ↙ Diag ned-venstre   │ "bottom-left"  │ "top-right"
  ↗ Diag opp-høyre     │ "top-right"    │ "bottom-left"
  ↖ Diag opp-venstre   │ "top-left"     │ "bottom-right"

⚠ ALDRI bruk diagonale porter for blokker på samme rad (y) eller kolonne (x).
⚠ fromPort og toPort MÅ alltid angis.

══════════════════════════════════
  5. GRUPPER & NESTING
══════════════════════════════════
Gruppe-felt:
  "id":            Unikt gruppe-ID (f.eks. "g1", "g2")
  "blockIds":      Array av blokk-ID-er (KAN VÆRE TOM [] for lag)
  "color":         Hexfarge (f.eks. "#8b5cf6")
  "name":          Visningsnavn
  "locked":        false (standard) — true = blokker beveger seg sammen
  "collapsed":     true/false — true = gruppen vises som kompakt kort (LAG-MODUS)
  "parentGroupId": null for toppnivå, eller foreldgruppens ID for nesting
  "collapsedX":    X-posisjon for kortet (multiplum av 20)
  "collapsedY":    Y-posisjon for kortet (multiplum av 20)

Regler:
- Grupper kan ha 0 eller flere blokker (tomme grupper = tomme lag)
- Enkeltblokker kan konverteres til lag via lag-knappen
- Nestede grupper bruker UAVHENGIGE koordinatsystemer (hvert lag er sitt eget lerret)
- parentGroupId kobler barngrupper til foreldregrupper

══════════════════════════════════
  6. LAGSYSTEM (AVANSERT)
══════════════════════════════════
Grupper med "collapsed": true blir navigerbare lag:

🏗️ ARKITEKTURMØNSTER:
  Rotnivå:  Oversikt med kollapsede grupper som kompakte kort
  Lag 1:    Delsystemer — hver kollapsert gruppe utvides til eget lerret
  Lag 2+:   Detaljert implementeringslogikk i nestede grupper

🔄 BLOKK-TIL-LAG-KONVERTERING:
  - Enkeltblokker kan konverteres til lag via lag-knappen
  - Alle koblinger til/fra blokken omdirigeres til det nye laget
  - Bruk dette for å lage "plassholder-lag" som fylles senere

📐 LAYOUT-STRATEGI:
  - Plasser kollapsede grupper som blokker (~240px mellomrom)
  - Bruk collapsedX/collapsedY for å posisjonere kort
  - Inni hver gruppe: layout blokker uavhengig (eget koordinatsystem, start fra x:200, y:100)

🔗 KOBLINGER MELLOM LAG:
  - Koblinger kan referere gruppe-ID-er direkte (fromId/toId = gruppe-ID)

══════════════════════════════════
  7. DESIGNMØNSTRE
══════════════════════════════════
📊 PIPELINE: Lineær flyt → "right"/"left"-porter, samme Y
📐 FORGRENING: Betingelse med flere veier → "bottom"/"right"-porter
🎨 FARGEKODING: Bruk forskjellige farger per domene:
   Infrastruktur: "#6366f1"  API: "#8b5cf6"  Auth: "#ec4899"
   Data: "#06b6d4"  UI: "#22c55e"  Overvåking: "#f97316"

  ══════════════════════════════════
  SJEKKLISTE FØR OUTPUT
══════════════════════════════════
✅ Alle x/y er multipler av 20
✅ Alle koblinger har fromPort + toPort
✅ Portvalg matcher relativ posisjon (se tabell over)
✅ Alle ID-er er unike
✅ Grupper kan være tomme (blockIds: []) — ingen minimumskrav
✅ Kollapsede grupper har collapsedX/collapsedY satt
✅ parentGroupId refererer gyldig gruppe-ID (eller null)
✅ Blokker inni lag har uavhengige koordinater
✅ Veipunkter (valgfritt): legg til "waypoints": [{"x":…,"y":…}] på en kobling for å styre kurven manuelt
✅ Alltid inkluder "coordinateOrigin": "center" på rotnivå
✅ Outputen er KUN JSON`,
  aiPromptTipsTitle: 'Tips for best resultat',
  aiPromptTip1: 'Legg ved bilder av eksisterende diagrammer — AI-en kan tolke og konvertere dem til JSON',
  aiPromptTip2: 'Beskriv prosesstrinnene som punktliste for et tydeligere resultat',
  aiPromptTip3: 'Angi ønsket antall blokker og grupperinger i prompten for mer kontroll',
  aiPromptTip4: 'Kopier JSON-resultatet og lim inn via Import-knappen i Flow Weaver',
  aiPromptCopy: 'Kopier prompt',
  aiPromptCopied: 'Prompt kopiert!',
  close: 'Lukk',
  saveFile: 'Lagre fil',
  loadFile: 'Last fil',
  welcome: 'Velkommen',
  savedAsFile: 'Arbeidsflyt lagret som fil',
  donate: 'Doner',
  undo: 'Angre',
  redo: 'Gjør om',
  rename: 'Gi nytt navn',
  editName: 'Rediger navn',
  editDescription: 'Rediger beskrivelse',
  editColor: 'Rediger farge',
  editBlock: 'Rediger blokk',
  editGroup: 'Rediger gruppe',
  editLayer: 'Rediger lag',
  groupThisBlock: 'Grupper denne blokken',
  createGroupFromSelection: 'Lag gruppe fra utvalg',
  convertToLayer: 'Konverter til lag',
  duplicate: 'Dupliser',
  explainBlock: 'Forklar blokk…',
  delete: 'Slett',
  deleteBlock: 'Slett blokk',
  deleteGroup: 'Slett gruppe',
  collapseToLayer: 'Kollaps til lag',
  enterLayer: 'Åpne lag',
  expandLayer: 'Utvid (konverter tilbake til gruppe)',
  keyboardShortcutsTitle: 'Tastatursnarveier',
  keyboardShortcutsHint: 'Trykk ? for å vise denne ruten igjen.',
  blocksGroupedCount_one: '{{count}} blokk gruppert',
  blocksGroupedCount_other: '{{count}} blokker gruppert',
  blocksCount_one: '{{count}} blokk',
  blocksCount_other: '{{count}} blokker',
  connectorsCount_one: '{{count}} kobling',
  connectorsCount_other: '{{count}} koblinger',
  groupsCount_one: '{{count}} gruppe',
  groupsCount_other: '{{count}} grupper',
  export: 'Eksporterer',
  pleaseWait: 'Dette kan ta noen sekunder for store lerret…',
  exportDone: 'Eksport ferdig',
  auditCreated: 'Opprettet',
  auditDeleted: 'Slettet',
  auditRenamed: 'Omdøpte',
  auditEdited: 'Redigerte',
  auditCoopEnabled: 'Co-op aktivert',
  auditCoopDisabled: 'Co-op deaktivert',
  auditRedactedActor: '[slettet bruker]',
  auditCollabInvite: 'Inviterte bruker',
  auditCollabRemove: 'Fjernet bruker',
  auditCollabRoleChange: 'Endret rolle for bruker',
  auditCollabAccept: 'Aksepterte invitasjon:',
  crossSheetLink: 'Tverrarks-lenke',
  crossSheetActive: 'Tverrarks-lenking…',
  crossSheetHint: 'Koble blokker mellom tilstøtende ark',
  crossSheetActiveHint: 'Trykk Esc for å avbryte',
  back: 'Tilbake',
  processEngineDisabled: 'Process Engine er deaktivert i denne installasjonen.',
  processEngineUpsellTitle: 'Lås opp Process Engine',
  processEngineUpsellBody: 'Bygg attestflyt, klargjøring og offboarding fra Flow-weaver-diagrammene dine. Krever {tier} eller høyere.',
  processEngineUpsellCta: 'Se planer',
  processEngineLoadTitle: 'Last et Flow-weaver-flyt',
  processEngineLoadFromFile: 'Fra fil (.json)',
  processEngineLoadFromPaste: 'Lim inn JSON',
  processEngineLoadImport: 'Importer',
  processEngineImportSuccess: 'Flyt importert',
  processEngineImportFailed: 'Kunne ikke importere flyten',
  processEngineLiveAvailable: 'Live-kjøringer tilgjengelig',
  processEngineDesignOnly: 'Kun design — oppgrader til {tier} for live-kjøring',
  processEngineDryRun: 'Tørrkjøring',
  processEngineDryRunDone: 'Tørrkjøring fullført',
  processEngineRunning: 'Kjører…',
  processEngineLoadFromTemplate: 'Eller start fra en mal',
  processEngineOpenFinal: 'Sluttvisning',
  processEngineSuggest: 'AI-forslag',
  processEngineSuggestNone: 'Ingen treff',
  processEngineSuggestDone: 'Forslag lagt til',
  processEngineRunLive: 'Kjør live',
  processEngineRunDone: 'Live-kjøring fullført',
  processEngineRunFailed: 'Live-kjøring feilet',
  processEngineRunBlocked: 'Live-kjøring blokkert',
  processEngineSave: 'Lagre',
  processEngineSaving: 'Lagrer…',
  processEngineSaveDone: 'Lagret',
  processEngineSaveFailed: 'Lagring feilet',
  processEngineLoadFromSaved: 'Lagrede flyter',
  finalTldr: 'Sammendrag',
  finalPageTitle: 'Sluttvisning',
  finalSubtitle: 'Et helhetsbilde av design, kunnskap og drift.',
  finalAudience: 'Målgruppe',
  finalHubTitle: 'Systemoversikt',
  finalHubSubtitle:
    'Live-status for hvert system. Åpne en container for å gå dypere, eller promoter en flyt fra Process Engine for den detaljerte tre-akts-fortellingen nedenfor.',
  finalNoFlowTitle: 'Ingen flyt valgt',
  finalNoFlowBody: 'Åpne Process Engine og last inn en flyt først.',
  finalGoToProcessEngine: 'Åpne Process Engine',
  finalContainerFlowWeaverTitle: 'Flow-weaver',
  finalContainerFlowWeaverEmpty: 'Ingen arbeidsflyt lagret ennå — åpne Flow-weaver for å starte.',
  finalContainerOpenFlowWeaver: 'Åpne Flow-weaver',
  finalContainerProcessEngineTitle: 'Process Engine',
  finalContainerProcessEngineSubtitle: '{count} lagrede flyter',
  finalContainerProcessEngineEmpty: 'Ingen lagrede flyter — promoter en arbeidsflyt til Process.',
  finalContainerOpenProcessEngine: 'Åpne Process Engine',
  finalContainerAtlasTitle: 'Atlas',
  finalContainerAtlasEmpty: 'Atlas-manifest ikke lastet.',
  finalContainerOpenAtlas: 'Åpne Atlas',
  finalContainerGroups: 'Grupper',
  finalContainerFlows: 'Flyter',
  finalContainerTotalBlocks: 'Totalt blokker',
  finalContainerCategories: 'Kategorier',
  finalActDesign: 'Design',
  finalActDesignSubtitle: 'Hvordan prosessen er modellert i Flow-weaver.',
  finalActKnowledge: 'Kunnskap',
  finalActKnowledgeSubtitle: 'Hva organisasjonen vet og kobler sammen i Atlas.',
  finalActOperation: 'Drift',
  finalActOperationSubtitle: 'Hvordan flyten oppfører seg i drift over 30 dager.',
  finalSheets: 'Ark',
  finalBlocks: 'Blokker',
  finalConnectors: 'Koblinger',
  finalBindings: 'Bindinger',
  finalAtlas: 'Atlas',
  finalDocs: 'Dokumenter',
  finalCrossDocRefs: 'Krysslenker',
  finalRuns30d: 'Kjøringer (30 d)',
  finalSuccessRate: 'Suksessrate',
  finalMeanDuration: 'Snittid',
  finalRoi: 'Verdi',
  finalRoiHoursSaved: 'Spart tid',
  finalRoiErrorsPrevented: 'Forhindrede feil',
  finalRoiMtto: 'MTTO',
  finalRoiValue: 'Verdi',
  finalRoiMethodology: 'Verdien er konservativt beregnet til 30 min spart manuelt arbeid per vellykket kjøring × blandet timepris på 45 €.',
  finalWatermark: 'Generert: {watermark}',
  finalDisabled: 'Slutt-visningen er deaktivert i denne installasjonen.',
  finalUpsellTitle: 'Lås opp Slutt-visningen',
  finalUpsellBody: 'Skrivebeskyttet ledervisning over Flow-weaver, Atlas og Process Engine. Krever {tier} eller høyere.',
  finalUpsellCta: 'Se planer',
  finalPhaseHint: 'Slutt-visningen kommer i en senere fase.',
  blockCreated: 'Blokk lagt til',
  toggleCollapse: 'Veksle minimering',
  toggleLock: 'Veksle lås',
};

const da: TranslationKeys = {
  appTitle: 'EngiCoro Flow Weaver',
  save: 'Gem',
  load: 'Indlæs',
  exportPng: 'Eksporter billede',
  import_: 'Importer',
  copyJson: 'Kopier JSON',
  simulate: 'Simuler',
  clear: 'Ryd',
  connectedDb: 'Forbundet til database',
  usingLocal: 'Bruger localStorage',
  db: 'DB',
  local: 'Lokal',
  help: 'Hjælp',

  // MobileTopBar / drawer (#20, Wave D)
  aiAndJson: 'AI og JSON',
  aiAssistant: 'AI-assistent',
  cloudWorkspaces: 'Sky-arbejdsområder',
  shareWorkspace: 'Del arbejdsområde',
  reportBug: 'Rapportér fejl',
  featureRequest: 'Funktionsønske',
  adminDashboard: 'Adminpanel',
  signIn: 'Log ind',
  signOut: 'Log ud',
  openMenu: 'Åbn menu',
  menu: 'Menu',
  notSignedIn: 'Ikke logget ind',
  gettingStartedTab: 'Kom i gang',
  gettingStartedTitle: 'Velkommen til Flow Weaver',
  gettingStartedDesc: 'Flow Weaver er et visuelt værktøj til at oprette flowdiagrammer og orkestreringsdiagrammer. Træk blokke, forbind dem og organiser dem i grupper — alt direkte i browseren.',
  gsWhatIsTitle: '🎯 Hvad er Flow Weaver?',
  gsWhatIsDesc: 'Et træk-og-slip-værktøj til at bygge visuelle flows. Perfekt til at dokumentere IT-processer, systemarkitektur, onboarding-flows, automatiseringer og meget mere.',
  gsAddBlocksTitle: '➕ Tilføj blokke',
  gsAddBlocksDesc: 'Træk en blok fra sidepanelet til venstre og slip den på lærredet. Hver blok repræsenterer et trin i dit flow. Vælg mellem foruddefinerede typer eller brug den generelle "Generel funktion".',
  gsConnectTitle: '🔗 Forbind blokke',
  gsConnectDesc: 'Klik på "Forbind blokke"-knappen i sidepanelet, klik derefter på en port (de små cirkler rundt blokkene) for at starte forbindelsen og klik på en anden port for at fuldføre. Blokke har 8 porte (top, højre, bund, venstre + hjørner). Dobbeltklik på en linje for at tilføje et vejpunkt — vejpunkter bøjer kurven blødt. Dobbeltklik på et vejpunkt for at fjerne det.',
  gsGroupTitle: '📦 Grupper blokke',
  gsGroupDesc: 'Klik på "Grupper blokke"-knappen, klik derefter på de blokke du vil gruppere (de markeres), og klik på knappen igen for at oprette gruppen.',
  gsEditTitle: '✏️ Rediger egenskaber',
  gsEditDesc: 'Klik på en blok eller gruppe for at åbne egenskabspanelet til højre. Der kan du ændre navn, beskrivelse, ikon og farve.',
  gsSaveLoadTitle: '💾 Gem og indlæs',
  gsSaveLoadDesc: 'Klik "Gem" for at gemme dit flow. Uden database gemmes det i browserens localStorage. Med databaseforbindelse gemmes det permanent.',
  gsImportExportTitle: '📤 Import og eksport',
  gsImportExportDesc: 'Brug "Kopier JSON" for at eksportere dit flow som JSON-tekst. Brug "Importer" for at indsætte JSON og indlæse et flow. Du kan også eksportere som PNG-billede.',
  gsAiTitle: '🤖 Opret flows med AI',
  gsAiDesc: 'Klik på "AI & JSON"-knappen i værktøjslinjen for at åbne en dialog med en færdig AI-prompt og JSON-reference. Kopier prompten til en valgfri AI (ChatGPT, Claude, Grok osv.), beskriv dit system og importer det genererede JSON-resultat direkte i Flow Weaver.',
  gsKeyboardTitle: '⌨️ Navigation & Genveje',
  gsKeyboardShortcuts: 'Scroll for at zoome ind/ud\n• Klik og træk på lærredet for at panorere\n• Træk blokke for at flytte dem\n• Dobbeltklik på kollapseret lag for at dykke ind\n• Tryk Enter på kollapseret lag for at åbne det\n• Brødkrummer for lagnavigering\n• Dobbeltklik på en linje for at tilføje vejpunkt\n• Dobbeltklik på vejpunkt for at fjerne det\n• Piletaster for at navigere mellem blokke\n• Ctrl+Z / Cmd+Z for at fortryde\n• Ctrl+Shift+Z / Cmd+Shift+Z for at gentage\n• Ctrl+S / Cmd+S for at gemme\n• Delete / Backspace for at slette valgt blok\n• Escape for at fjerne markering',
  gsLayersTitle: '🧩 Lag & Nesting',
  gsLayersDesc: 'Grupper kan kollapses til kompakte kort der repræsenterer hele lag. Klik på lag-ikonet (⧉) ved siden af en gruppe i sidepanelet for at kollapse/udvide den. Dobbeltklik på et kollapseret kort for at dykke ind i laget. Brødkrummer vises for at navigere tilbage. Ubegrænset nesting understøttes.',
  tools: 'Værktøjer',
  createUser: 'Aktivitet',
  disableMailbox: 'Kommunikation',
  deleteM365: 'System',
  sendEmail: 'Notifikation',
  webhook: 'Hændelse',
  addToGroup: 'Interessent',
  condition: 'Beslutning',
  generalFunction: 'Proces',
  actions: 'Handlinger',
  connecting: 'Forbinder...',
  connectBlocks: 'Forbind blokke',
  grouping: 'Grupperer',
  groupBlocks: 'Grupper blokke',
  blocks: 'Blokke',
  groups: 'Grupper',
  changeColor: 'Skift farve',
  dragToCanvas: 'Træk værktøjer til lærred',
  lockGroup: 'Lås gruppe',
  unlockGroup: 'Lås gruppe op',
  search: 'Søg...',
  searchCanvasPlaceholder: 'Søg alt…',
  searchCanvasTooltip: 'Skriv for at filtrere. Enter åbner Søge-canvas. Cmd+K for at fokusere.',
  searchCanvasScopeAll: 'Alt',
  searchCanvasScopeWorkflows: 'Arbejdsgange',
  searchCanvasScopeAtlas: 'Atlas',
  searchCanvasMatchCount: '{count} match',
  collapseAll: 'Kollaps alt',
  collapseToLayers: 'Kollaps til lag',
  expandAll: 'Udvid alt',
  groupBatchActions: 'Gruppehandlinger',
  coopJoined: 'Co-op arbejdsområde tilsluttet',
  coopAccessDenied: 'Ingen adgang til arbejdsområdet',
  coopAccessDeniedHint: 'Bed ejeren aktivere Co-op og invitere dig.',
  coopLoadFailed: 'Kunne ikke indlæse delt arbejdsområde',
  coopEnableTitle: 'Aktiver Co-op',
  coopEnableHint: 'Skal være aktiveret for at delingslinket fungerer',
  coopActive: 'Co-op er aktivt',
  coopInactive: 'Co-op er slået fra',
  coopCollaborators: 'Samarbejdspartnere',
  auditHistory: 'Historik',
  auditEmpty: 'Ingen ændringer registreret endnu.',
  groupProperties: 'Gruppeegenskaber',
  blockProperties: 'Blokegenskaber',
  name: 'Navn',
  description: 'Beskrivelse',
  details: 'Detaljer…',
  recipient: 'Modtager',
  subject: 'Emne',
  blockColor: 'Blokfarve',
  groupColor: 'Gruppefarve',
  templateSettings: 'Skabelonindstillinger',
  trigger: 'Udløser',
  userCreated: 'Bruger Oprettet',
  userModified: 'Bruger Ændret',
  userDeleted: 'Bruger Slettet',
  custom: 'Tilpasset',
  overview: 'Oversigt',
  clickToConfig: 'Klik på en blok eller gruppe for at konfigurere',
  blocksInGroup: 'blokke i gruppen',
  writeDescription: 'Skriv en beskrivelse... (understøtter linjeskift)',
  zoomIn: 'Zoom ind',
  zoomOut: 'Zoom ud',
  resetZoom: 'Nulstil zoom',
  addBlock: 'Tilføj blok',
  noBlockTypes: 'Ingen bloktyper tilgængelige',
  fitToContent: 'Tilpas til indhold',
  saved: 'Gemt!',
  savedToLocal: 'Workflow gemt i localStorage',
  loaded: 'Indlæst!',
  loadedFromLocal: 'Workflow indlæst fra localStorage',
  noDataFound: 'Ingen data fundet',
  couldNotFindWorkflow: 'Kunne ikke finde gemt workflow',
  cleared: 'Ryddet!',
  canvasCleared: 'Lærredet er ryddet',
  imported: 'Importeret!',
  importedFromJson: 'Importeret fra JSON',
  copied: 'Kopieret!',
  jsonCopied: 'Workflow JSON kopieret til udklipsholder',
  simulatingTitle: 'Simulerer...',
  simulatingDesc: 'Kører',
  groupCreated: 'Gruppe oprettet',
  blocksGrouped: 'blokke grupperet',
  savedToDb: 'gemt i databasen',
  error: 'Fejl',
  saveError: 'Fejl ved gemning',
  loadError: 'Fejl ved indlæsning',
  deleteError: 'Fejl ved sletning',
  deleted: 'Slettet',
  documentDeleted: 'Dokumentet er slettet',
  exportRefSaved: 'Eksportreference gemt',
  exportRefDesc: 'En PNG-eksportreference er gemt i databasen',
  exportPngDesc: 'PNG-eksport kræver dom-to-image-bibliotek',
  couldNotCopy: 'Kunne ikke kopiere til udklipsholder',
  couldNotReadWorkflow: 'Kunne ikke læse workflowdata',
  saveWorkflow: 'Gem Workflow',
  saveAsNew: 'Gem som ny',
  titleLabel: 'Titel',
  categoryLabel: 'Kategori',
  cancel: 'Annuller',
  loadWorkflow: 'Indlæs Workflow',
  noDocuments: 'Ingen gemte dokumenter',
  loadBtn: 'Indlæs',
  deleteBtn: 'Slet',
  importJson: 'Importer JSON',
  pasteJsonHere: 'Indsæt WorkflowState JSON her...',
  importBtn: 'Importer',
  invalidJson: 'Ugyldig JSON',
  invalidStructure: 'JSON mangler nødvendig struktur (blocks, connectors, groups)',
  helpTitle: 'Instruktioner & Dokumentation',
  integrationTab: 'Integration',
  databaseTab: 'Database',
  jsonTab: 'JSON-kortlægning',
  integrationTitle: 'Sådan integrerer du',
  integrationDesc: 'Flow Weaver kan køre som en selvstændig app eller indlejres i et andet projekt via iframe.',
  integrationStep1: '1. Publicer appen via Lovable og kopier den publicerede URL (f.eks. https://flow-weaver-05.lovable.app)',
  integrationStep2: '2. Indlejr med <iframe ref={iframeRef} src="URL" width="100%" height="600px" />',
  integrationStep3: '3. Uden databaseintegration gemmes alt i browserens localStorage',
  integrationStep4: '4. For at gemme til DIT projekts database: send dine Supabase-credentials til iframen via postMessage (se kodeeksempel nedenfor)',
  integrationStep5: '5. Iframen lytter automatisk efter beskeden "supabase-credentials" og skifter til din database',
  integrationStep6: '6. Brugeren skal være logget ind i værtsappen — access token fra sessionen sendes med',
  integrationStep7: '7. Hvis ingen credentials sendes, falder iframen tilbage til localStorage (ingen data går tabt)',
  integrationCodeTitle: 'Kodeeksempel — send credentials fra værtsappen',
  integrationCodeExample: `import { useRef, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

function FlowchartPage() {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const sendCredentials = useCallback(async () => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;

    iframe.contentWindow.postMessage({
      type: 'supabase-credentials',
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
      supabaseKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      accessToken: session.access_token,
    }, '*');
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'request-supabase-credentials') {
        sendCredentials();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [sendCredentials]);

  return (
    <iframe
      ref={iframeRef}
      src="https://flow-weaver-05.lovable.app"
      onLoad={() => setTimeout(sendCredentials, 500)}
      className="w-full h-full"
    />
  );
}`,
  integrationNote: '⚠️ Værtsappen skal have en "workflows"-tabel med kolonnerne: id (uuid), title (text), blocks (jsonb), connectors (jsonb), groups (jsonb), workflow_name (text), coordinate_origin (text), category (text), created_by (uuid), created_at, updated_at, is_pinned (boolean). RLS-politikker skal tillade CRUD baseret på auth.uid() = created_by. Derudover skal en "save-workflow" edge function validere JWT og håndtere insert/update.',
  databaseTitle: 'Databasekrav',
  databaseDesc: 'Værtsappen skal have følgende for at databaselagring fungerer:',
  databaseReq1: 'Tabel: workflows — id (uuid PK), title (text), blocks (jsonb), connectors (jsonb), groups (jsonb), workflow_name (text), coordinate_origin (text), category (text, default "workflow"), created_by (uuid), is_pinned (boolean), last_modified_by (uuid), created_at, updated_at',
  databaseReq2: 'En "save-workflow" edge function der validerer JWT (via getClaims), udtrækker bruger-ID og håndterer insert/update mod workflows-tabellen',
  databaseReq3: 'RLS-politikker: auth.uid() = created_by for SELECT, INSERT, UPDATE, DELETE',
  databaseReq4: 'Brugeren skal være logget ind i værtsappen — sessionen sendes til iframen via postMessage',
  jsonTitle: 'JSON-kortlægning',
  jsonDesc: 'Workflows gemmes som JSON med følgende struktur (WorkflowState):',
  jsonStep1: '1. Klik "Kopier JSON" i værktøjslinjen for at eksportere det aktuelle flow',
  jsonStep2: '2. Klik "Importer" for at indsætte og indlæse et flow fra JSON',
  jsonStep3: '3. Hver blok har id, type, x, y, config (navn, beskrivelse, ikon) og color. Blokke kan konverteres til lag (tomme kollapsede grupper) via lag-knappen.',
  jsonStep4: '4. Forbindelser refererer fromId/toId. Grupper kan have 0+ blokke, understøtter nesting via parentGroupId, og kan kollapses til lag med collapsed/collapsedX/collapsedY.',
  jsonStructure: '{ "name": "...", "blocks": [...], "connectors": [{ fromId, toId, fromPort, toPort }], "groups": [{ id, blockIds, color, name, collapsed, collapsedX, collapsedY, parentGroupId, locked }] }',
  aiPromptTab: 'AI-Prompt',
  aiPromptTitle: 'Generer flows med AI',
  aiPromptDesc: 'Du kan bruge enhver AI (ChatGPT, Grok, Claude, Lovable osv.) til automatisk at generere JSON, der kan importeres direkte i Flow Weaver. Giv AI\'en prompten nedenfor sammen med dit materiale.',
  aiPromptUseCases: 'Anvendelsestilfælde',
  aiPromptUseCase1: 'Bed en AI om at kortlægge et eksisterende projekt eller system til et flowdiagram',
  aiPromptUseCase2: 'Upload billeder (arkitekturdiagrammer, whiteboards) og bed AI\'en konvertere til JSON',
  aiPromptUseCase3: 'Send dokumentation, specifikationer eller kravlister og få et visuelt flow',
  aiPromptUseCase4: 'Beskriv en proces med ord og lad AI\'en bygge flowdiagrammet for dig',
  aiPromptUseCase5: 'Byg komplekse flerlagsarkitekturer med indlejrede lag — overblik på topniveau, detaljeret logik indeni',
  aiPromptTemplateTitle: 'Kopierbar AI-prompt',
  aiPromptTemplate: `Du er en JSON-generator til Flow Weaver. Returner KUN et gyldigt JSON-objekt (WorkflowState). Ingen forklarende tekst.

══════════════════════════════════
  SKEMA (eksempelblok)
══════════════════════════════════
{
  "name": "Flownavn",
  "blocks": [
    { "id": "b01", "type": "general", "x": 200, "y": 100,
      "config": { "name": "Bloknavn", "description": "Beskrivelse", "icon": "wrench" },
      "color": "#6366f1" }
  ],
  "connectors": [
    { "id": "c01", "fromId": "b01", "toId": "b02", "fromPort": "right", "toPort": "left" }
  ],
  "groups": [
    { "id": "g1", "blockIds": ["b01","b02"], "color": "#6366f1", "name": "Gruppenavn",
      "locked": false, "collapsed": true, "parentGroupId": null,
      "collapsedX": 200, "collapsedY": 100 }
  ]
}

══════════════════════════════════
  1. BLOKTYPER
══════════════════════════════════
"create-user" | "disable-mailbox" | "delete-m365" | "send-email"
"webhook"      | "add-to-group"   | "condition"    | "general"

══════════════════════════════════
  2. IKONER (efter kategori)
══════════════════════════════════
Personer:    user, users
Kommunikation: mail, send, message-square, phone, bell
Sky/Netværk: cloud, globe, server, database, link
Filer:       file-text, folder, archive, download, upload, copy
Værktøjer:   wrench, settings, terminal, filter, search
Status:      check-circle, alert-triangle, clock, flag, star
Sikkerhed:   shield, lock, key, eye
Medier:      play, camera, monitor
Logik:       git-branch, shuffle, layers, refresh-cw
Andet:       zap, power, heart, tag, bookmark, bar-chart, package, trash, thumbs-up, calendar, help-circle

══════════════════════════════════
  3. GITTER & KOORDINATER
══════════════════════════════════
- x og y SKAL være multipla af 20
- Vandret afstand: ~240px
- Lodret afstand:  ~220px
- ID-format: "b01", "b02", ... (blokke), "c01", "c02", ... (forbindelser), "g1", "g2", ... (grupper)
- Alle ID'er skal være unikke

══════════════════════════════════
  4. PORT-ROUTING (KRITISK!)
══════════════════════════════════
Bestem fromPort/toPort ved at sammenligne source (x,y) med target (x,y):

  Retning              │ fromPort       │ toPort
  ─────────────────────┼────────────────┼────────────────
  → Højre (samme y)    │ "right"        │ "left"
  ← Venstre (samme y)  │ "left"         │ "right"
  ↓ Ned (samme x)      │ "bottom"       │ "top"
  ↑ Op (samme x)       │ "top"          │ "bottom"
  ↘ Diag ned-højre     │ "bottom-right" │ "top-left"
  ↙ Diag ned-venstre   │ "bottom-left"  │ "top-right"
  ↗ Diag op-højre      │ "top-right"    │ "bottom-left"
  ↖ Diag op-venstre    │ "top-left"     │ "bottom-right"

⚠ Brug ALDRIG diagonale porte for blokke på samme række (y) eller kolonne (x).
⚠ fromPort og toPort SKAL altid angives.

══════════════════════════════════
  5. GRUPPER & NESTING
══════════════════════════════════
Gruppe-felter:
  "id":            Unikt gruppe-ID (f.eks. "g1", "g2")
  "blockIds":      Array af blok-ID'er (KAN VÆRE TOM [] for lag)
  "color":         Hexfarve (f.eks. "#8b5cf6")
  "name":          Visningsnavn
  "locked":        false (standard) — true = blokke bevæger sig sammen
  "collapsed":     true/false — true = gruppen vises som kompakt kort (LAG-TILSTAND)
  "parentGroupId": null for topniveau, eller forældregruppens ID for nesting
  "collapsedX":    X-position for kortet (multiplum af 20)
  "collapsedY":    Y-position for kortet (multiplum af 20)

Regler:
- Grupper kan have 0 eller flere blokke (tomme grupper = tomme lag)
- Enkelte blokke kan konverteres til lag via lag-knappen
- Nestede grupper bruger UAFHÆNGIGE koordinatsystemer (hvert lag er sit eget lærred)
- parentGroupId forbinder undergrupper med forældregrupper

══════════════════════════════════
  6. LAGSYSTEM (AVANCERET)
══════════════════════════════════
Grupper med "collapsed": true bliver navigerbare lag:

🏗️ ARKITEKTURMØNSTER:
  Rodniveau: Overblik med kollapsede grupper som kompakte kort
  Lag 1:     Delsystemer — hver kollapseret gruppe udvides til eget lærred
  Lag 2+:    Detaljeret implementeringslogik i nestede grupper

🔄 BLOK-TIL-LAG-KONVERTERING:
  - Enkelte blokke kan konverteres til lag via lag-knappen
  - Alle forbindelser til/fra blokken omdirigeres til det nye lag
  - Brug dette til at oprette "pladsholder-lag" der fyldes senere

📐 LAYOUT-STRATEGI:
  - Placer kollapsede grupper som blokke (~240px mellemrum)
  - Brug collapsedX/collapsedY til at positionere kort
  - Inde i hver gruppe: layout blokke uafhængigt (eget koordinatsystem, start fra x:200, y:100)

🔗 FORBINDELSER MELLEM LAG:
  - Forbindelser kan referere gruppe-ID'er direkte (fromId/toId = gruppe-ID)

══════════════════════════════════
  7. DESIGNMØNSTRE
══════════════════════════════════
📊 PIPELINE: Lineært flow → "right"/"left"-porte, samme Y
📐 FORGRENING: Betingelse med flere veje → "bottom"/"right"-porte
🎨 FARVEKODNING: Brug forskellige farver per domæne:
   Infrastruktur: "#6366f1"  API: "#8b5cf6"  Auth: "#ec4899"
   Data: "#06b6d4"  UI: "#22c55e"  Overvågning: "#f97316"

  ══════════════════════════════════
  TJEKLISTE FØR OUTPUT
══════════════════════════════════
✅ Alle x/y er multipla af 20
✅ Alle forbindelser har fromPort + toPort
✅ Portvalg matcher relativ position (se tabel ovenfor)
✅ Alle ID'er er unikke
✅ Grupper kan være tomme (blockIds: []) — intet minimumskrav
✅ Kollapsede grupper har collapsedX/collapsedY sat
✅ parentGroupId refererer gyldigt gruppe-ID (eller null)
✅ Blokke inde i lag har uafhængige koordinater
✅ Vejpunkter (valgfrit): tilføj "waypoints": [{"x":…,"y":…}] til en forbindelse for at styre kurven manuelt
✅ Inkluder altid "coordinateOrigin": "center" på rodniveau
✅ Outputtet er KUN JSON`,
  aiPromptTipsTitle: 'Tips til bedste resultater',
  aiPromptTip1: 'Vedhæft billeder af eksisterende diagrammer — AI\'en kan fortolke og konvertere dem til JSON',
  aiPromptTip2: 'Beskriv procestrinene som punktliste for et tydeligere resultat',
  aiPromptTip3: 'Angiv det ønskede antal blokke og grupperinger i din prompt for mere kontrol',
  aiPromptTip4: 'Kopier JSON-resultatet og indsæt via Import-knappen i Flow Weaver',
  aiPromptCopy: 'Kopier prompt',
  aiPromptCopied: 'Prompt kopieret!',
  close: 'Luk',
  saveFile: 'Gem fil',
  loadFile: 'Indlæs fil',
  welcome: 'Velkommen',
  savedAsFile: 'Workflow gemt som fil',
  donate: 'Donér',
  undo: 'Fortryd',
  redo: 'Gentag',
  rename: 'Omdøb',
  editName: 'Rediger navn',
  editDescription: 'Rediger beskrivelse',
  editColor: 'Rediger farve',
  editBlock: 'Rediger blok',
  editGroup: 'Rediger gruppe',
  editLayer: 'Rediger lag',
  groupThisBlock: 'Gruppér denne blok',
  createGroupFromSelection: 'Opret gruppe fra valg',
  convertToLayer: 'Konverter til lag',
  duplicate: 'Dupliker',
  explainBlock: 'Forklar blok…',
  delete: 'Slet',
  deleteBlock: 'Slet blok',
  deleteGroup: 'Slet gruppe',
  collapseToLayer: 'Kollaps til lag',
  enterLayer: 'Åbn lag',
  expandLayer: 'Udvid (konverter tilbage til gruppe)',
  keyboardShortcutsTitle: 'Tastaturgenveje',
  keyboardShortcutsHint: 'Tryk ? for at vise denne rude igen.',
  blocksGroupedCount_one: '{{count}} blok grupperet',
  blocksGroupedCount_other: '{{count}} blokke grupperet',
  blocksCount_one: '{{count}} blok',
  blocksCount_other: '{{count}} blokke',
  connectorsCount_one: '{{count}} forbindelse',
  connectorsCount_other: '{{count}} forbindelser',
  groupsCount_one: '{{count}} gruppe',
  groupsCount_other: '{{count}} grupper',
  export: 'Eksporterer',
  pleaseWait: 'Dette kan tage nogle sekunder for store lærreder…',
  exportDone: 'Eksport færdig',
  auditCreated: 'Oprettet',
  auditDeleted: 'Slettet',
  auditRenamed: 'Omdøbte',
  auditEdited: 'Redigerede',
  auditCoopEnabled: 'Co-op aktiveret',
  auditCoopDisabled: 'Co-op deaktiveret',
  auditRedactedActor: '[slettet bruger]',
  auditCollabInvite: 'Inviterede bruger',
  auditCollabRemove: 'Fjernede bruger',
  auditCollabRoleChange: 'Ændrede rolle for bruger',
  auditCollabAccept: 'Accepterede invitation:',
  crossSheetLink: 'Tværarks-link',
  crossSheetActive: 'Tværarks-linkning…',
  crossSheetHint: 'Forbind blokke mellem tilstødende ark',
  crossSheetActiveHint: 'Tryk Esc for at annullere',
  back: 'Tilbage',
  processEngineDisabled: 'Process Engine er deaktiveret i denne installation.',
  processEngineUpsellTitle: 'Lås Process Engine op',
  processEngineUpsellBody: 'Byg attestflows, provisionering og offboarding ud fra dine Flow-weaver-diagrammer. Kræver {tier} eller højere.',
  processEngineUpsellCta: 'Se planer',
  processEngineLoadTitle: 'Indlæs et Flow-weaver-flow',
  processEngineLoadFromFile: 'Fra fil (.json)',
  processEngineLoadFromPaste: 'Indsæt JSON',
  processEngineLoadImport: 'Importer',
  processEngineImportSuccess: 'Flow importeret',
  processEngineImportFailed: 'Kunne ikke importere flowet',
  processEngineLiveAvailable: 'Live-kørsler tilgængelige',
  processEngineDesignOnly: 'Kun design — opgrader til {tier} for live-kørsler',
  processEngineDryRun: 'Tørkørsel',
  processEngineDryRunDone: 'Tørkørsel afsluttet',
  processEngineRunning: 'Kører…',
  processEngineLoadFromTemplate: 'Eller start fra en skabelon',
  processEngineOpenFinal: 'Slutvisning',
  processEngineSuggest: 'AI-forslag',
  processEngineSuggestNone: 'Ingen matches',
  processEngineSuggestDone: 'Forslag tilføjet',
  processEngineRunLive: 'Kør live',
  processEngineRunDone: 'Live-kørsel afsluttet',
  processEngineRunFailed: 'Live-kørsel fejlede',
  processEngineRunBlocked: 'Live-kørsel blokeret',
  processEngineSave: 'Gem',
  processEngineSaving: 'Gemmer…',
  processEngineSaveDone: 'Gemt',
  processEngineSaveFailed: 'Gem fejlede',
  processEngineLoadFromSaved: 'Gemte flows',
  finalTldr: 'Resumé',
  finalPageTitle: 'Slutvisning',
  finalSubtitle: 'En komplet oversigt over design, viden og drift.',
  finalAudience: 'Målgruppe',
  finalHubTitle: 'Systemoversigt',
  finalHubSubtitle:
    'Live-status for hvert system. Åbn en container for at dykke dybere, eller promovér et flow fra Process Engine for den detaljerede tre-akts-fortælling nedenfor.',
  finalNoFlowTitle: 'Intet flow valgt',
  finalNoFlowBody: 'Åbn Process Engine og indlæs et flow først.',
  finalGoToProcessEngine: 'Åbn Process Engine',
  finalContainerFlowWeaverTitle: 'Flow-weaver',
  finalContainerFlowWeaverEmpty: 'Intet workflow gemt endnu — åbn Flow-weaver for at starte.',
  finalContainerOpenFlowWeaver: 'Åbn Flow-weaver',
  finalContainerProcessEngineTitle: 'Process Engine',
  finalContainerProcessEngineSubtitle: '{count} gemte flows',
  finalContainerProcessEngineEmpty: 'Ingen gemte flows — promovér et workflow til Process.',
  finalContainerOpenProcessEngine: 'Åbn Process Engine',
  finalContainerAtlasTitle: 'Atlas',
  finalContainerAtlasEmpty: 'Atlas-manifest ikke indlæst.',
  finalContainerOpenAtlas: 'Åbn Atlas',
  finalContainerGroups: 'Grupper',
  finalContainerFlows: 'Flows',
  finalContainerTotalBlocks: 'Blokke i alt',
  finalContainerCategories: 'Kategorier',
  finalActDesign: 'Design',
  finalActDesignSubtitle: 'Hvordan processen er modelleret i Flow-weaver.',
  finalActKnowledge: 'Viden',
  finalActKnowledgeSubtitle: 'Hvad organisationen ved og forbinder i Atlas.',
  finalActOperation: 'Drift',
  finalActOperationSubtitle: 'Hvordan flowet opfører sig i drift over 30 dage.',
  finalSheets: 'Ark',
  finalBlocks: 'Blokke',
  finalConnectors: 'Forbindelser',
  finalBindings: 'Bindinger',
  finalAtlas: 'Atlas',
  finalDocs: 'Dokumenter',
  finalCrossDocRefs: 'Krydshenvisninger',
  finalRuns30d: 'Kørsler (30 d)',
  finalSuccessRate: 'Succesrate',
  finalMeanDuration: 'Gns. varighed',
  finalRoi: 'Værdi',
  finalRoiHoursSaved: 'Sparede timer',
  finalRoiErrorsPrevented: 'Forhindrede fejl',
  finalRoiMtto: 'MTTO',
  finalRoiValue: 'Værdi',
  finalRoiMethodology: 'Værdien beregnes konservativt som 30 min sparet manuelt arbejde pr. succesfuld kørsel × en blandet timesats på 45 €.',
  finalWatermark: 'Genereret: {watermark}',
  finalDisabled: 'Slut-visningen er deaktiveret i denne installation.',
  finalUpsellTitle: 'Lås Slut-visningen op',
  finalUpsellBody: 'En skrivebeskyttet ledelsesoversigt over Flow-weaver, Atlas og Process Engine. Kræver {tier} eller højere.',
  finalUpsellCta: 'Se planer',
  finalPhaseHint: 'Slut-visningen kommer i en senere fase.',
  blockCreated: 'Blok tilføjet',
  toggleCollapse: 'Skift minimering',
  toggleLock: 'Skift lås',
};

const de: TranslationKeys = {
  appTitle: 'EngiCoro Flow Weaver',
  save: 'Speichern',
  load: 'Laden',
  exportPng: 'Bild exportieren',
  import_: 'Importieren',
  copyJson: 'JSON kopieren',
  simulate: 'Simulieren',
  clear: 'Löschen',
  connectedDb: 'Mit Datenbank verbunden',
  usingLocal: 'Verwendet localStorage',
  db: 'DB',
  local: 'Lokal',
  help: 'Hilfe',

  // MobileTopBar / drawer (#20, Wave D)
  aiAndJson: 'KI & JSON',
  aiAssistant: 'KI-Assistent',
  cloudWorkspaces: 'Cloud-Arbeitsbereiche',
  shareWorkspace: 'Arbeitsbereich teilen',
  reportBug: 'Fehler melden',
  featureRequest: 'Funktionswunsch',
  adminDashboard: 'Admin-Dashboard',
  signIn: 'Anmelden',
  signOut: 'Abmelden',
  openMenu: 'Menü öffnen',
  menu: 'Menü',
  notSignedIn: 'Nicht angemeldet',
  gettingStartedTab: 'Erste Schritte',
  gettingStartedTitle: 'Willkommen bei Flow Weaver',
  gettingStartedDesc: 'Flow Weaver ist ein visuelles Werkzeug zum Erstellen von Flussdiagrammen und Orchestrierungsdiagrammen. Ziehen Sie Blöcke, verbinden Sie sie und organisieren Sie sie in Gruppen — alles direkt im Browser.',
  gsWhatIsTitle: '🎯 Was ist Flow Weaver?',
  gsWhatIsDesc: 'Ein Drag-and-Drop-Werkzeug zum Erstellen visueller Abläufe. Perfekt für die Dokumentation von IT-Prozessen, Systemarchitektur, Onboarding-Abläufen, Automatisierungen und vielem mehr.',
  gsAddBlocksTitle: '➕ Blöcke hinzufügen',
  gsAddBlocksDesc: 'Ziehen Sie einen Block aus dem linken Seitenbereich und legen Sie ihn auf der Leinwand ab. Jeder Block repräsentiert einen Schritt in Ihrem Ablauf. Wählen Sie aus vordefinierten Typen oder verwenden Sie die "Allgemeine Funktion".',
  gsConnectTitle: '🔗 Blöcke verbinden',
  gsConnectDesc: 'Klicken Sie auf "Blöcke verbinden" im Seitenbereich, klicken Sie dann auf einen Port (die kleinen Kreise um die Blöcke), um die Verbindung zu starten, und klicken Sie auf einen anderen Port, um sie abzuschließen. Blöcke haben 8 Ports (oben, rechts, unten, links + Ecken). Doppelklicken Sie auf eine Verbindungslinie, um einen Wegpunkt hinzuzufügen — Wegpunkte biegen die Kurve sanft. Doppelklicken Sie auf einen Wegpunkt, um ihn zu entfernen.',
  gsGroupTitle: '📦 Blöcke gruppieren',
  gsGroupDesc: 'Klicken Sie auf "Blöcke gruppieren", klicken Sie dann auf die Blöcke, die Sie gruppieren möchten (sie werden hervorgehoben), und klicken Sie erneut auf die Schaltfläche, um die Gruppe zu erstellen.',
  gsEditTitle: '✏️ Eigenschaften bearbeiten',
  gsEditDesc: 'Klicken Sie auf einen Block oder eine Gruppe, um das Eigenschaftspanel rechts zu öffnen. Dort können Sie Name, Beschreibung, Symbol und Farbe ändern.',
  gsSaveLoadTitle: '💾 Speichern & Laden',
  gsSaveLoadDesc: 'Klicken Sie "Speichern", um Ihren Ablauf zu speichern. Ohne Datenbank wird er im localStorage des Browsers gespeichert. Mit Datenbankverbindung wird er dauerhaft gespeichert.',
  gsImportExportTitle: '📤 Import & Export',
  gsImportExportDesc: 'Verwenden Sie "JSON kopieren", um Ihren Ablauf als JSON-Text zu exportieren. Verwenden Sie "Importieren", um JSON einzufügen und einen Ablauf zu laden. Sie können auch als PNG-Bild exportieren.',
  gsAiTitle: '🤖 Abläufe mit AI erstellen',
  gsAiDesc: 'Klicken Sie auf die Schaltfläche "AI & JSON" in der Symbolleiste, um einen Dialog mit einem vorgefertigten AI-Prompt und einer JSON-Referenz zu öffnen. Kopieren Sie den Prompt zu einer beliebigen AI (ChatGPT, Claude, Grok usw.), beschreiben Sie Ihr System und importieren Sie das generierte JSON direkt in Flow Weaver.',
  gsKeyboardTitle: '⌨️ Navigation & Tastenkürzel',
  gsKeyboardShortcuts: 'Scrollen zum Zoomen\n• Klicken und Ziehen auf der Leinwand zum Schwenken\n• Blöcke ziehen zum Verschieben\n• Doppelklick auf eingeklappte Ebene zum Eintauchen\n• Enter-Taste auf eingeklappter Ebene zum Öffnen\n• Brotkrumen für Ebenennavigation\n• Doppelklick auf Verbindungslinie für Wegpunkt\n• Doppelklick auf Wegpunkt zum Entfernen\n• Pfeiltasten zur Navigation zwischen Blöcken\n• Ctrl+Z / Cmd+Z zum Rückgängig machen\n• Ctrl+Shift+Z / Cmd+Shift+Z zum Wiederherstellen\n• Ctrl+S / Cmd+S zum Speichern\n• Entf / Backspace zum Löschen des ausgewählten Blocks\n• Escape zum Abwählen',
  gsLayersTitle: '🧩 Ebenen & Verschachtelung',
  gsLayersDesc: 'Gruppen können zu kompakten Karten zusammengeklappt werden, die ganze Ebenen darstellen. Klicken Sie auf das Ebenen-Symbol (⧉) neben einer Gruppe in der Seitenleiste. Doppelklicken Sie auf eine eingeklappte Karte, um in die Ebene einzutauchen. Breadcrumbs ermöglichen die Navigation zurück. Unbegrenzte Verschachtelung wird unterstützt.',
  tools: 'Werkzeuge',
  createUser: 'Aktivität',
  disableMailbox: 'Kommunikation',
  deleteM365: 'System',
  sendEmail: 'Benachrichtigung',
  webhook: 'Ereignis',
  addToGroup: 'Stakeholder',
  condition: 'Entscheidung',
  generalFunction: 'Prozess',
  actions: 'Aktionen',
  connecting: 'Verbinde...',
  connectBlocks: 'Blöcke verbinden',
  grouping: 'Gruppiere',
  groupBlocks: 'Blöcke gruppieren',
  blocks: 'Blöcke',
  groups: 'Gruppen',
  changeColor: 'Farbe ändern',
  dragToCanvas: 'Werkzeuge auf die Leinwand ziehen',
  lockGroup: 'Gruppe sperren',
  unlockGroup: 'Gruppe entsperren',
  search: 'Suchen...',
  searchCanvasPlaceholder: 'Alles durchsuchen…',
  searchCanvasTooltip: 'Tippen zum Filtern. Enter öffnet die Such-Canvas. Cmd+K für Fokus.',
  searchCanvasScopeAll: 'Alles',
  searchCanvasScopeWorkflows: 'Workflows',
  searchCanvasScopeAtlas: 'Atlas',
  searchCanvasMatchCount: '{count} Treffer',
  collapseAll: 'Alle reduzieren',
  collapseToLayers: 'Zu Ebenen reduzieren',
  expandAll: 'Alle erweitern',
  groupBatchActions: 'Gruppenaktionen',
  coopJoined: 'Co-op-Arbeitsbereich beigetreten',
  coopAccessDenied: 'Kein Zugriff auf diesen Arbeitsbereich',
  coopAccessDeniedHint: 'Bitten Sie den Eigentümer, Co-op zu aktivieren und Sie einzuladen.',
  coopLoadFailed: 'Geteilter Arbeitsbereich konnte nicht geladen werden',
  coopEnableTitle: 'Co-op aktivieren',
  coopEnableHint: 'Muss aktiviert sein, damit der Freigabelink funktioniert',
  coopActive: 'Co-op ist aktiv',
  coopInactive: 'Co-op ist aus',
  coopCollaborators: 'Mitarbeiter',
  auditHistory: 'Verlauf',
  auditEmpty: 'Noch keine Änderungen aufgezeichnet.',
  groupProperties: 'Gruppeneigenschaften',
  blockProperties: 'Blockeigenschaften',
  name: 'Name',
  description: 'Beschreibung',
  details: 'Details…',
  recipient: 'Empfänger',
  subject: 'Betreff',
  blockColor: 'Blockfarbe',
  groupColor: 'Gruppenfarbe',
  templateSettings: 'Vorlageneinstellungen',
  trigger: 'Auslöser',
  userCreated: 'Benutzer erstellt',
  userModified: 'Benutzer geändert',
  userDeleted: 'Benutzer gelöscht',
  custom: 'Benutzerdefiniert',
  overview: 'Übersicht',
  clickToConfig: 'Klicken Sie auf einen Block oder eine Gruppe zum Konfigurieren',
  blocksInGroup: 'Blöcke in der Gruppe',
  writeDescription: 'Beschreibung eingeben... (unterstützt Zeilenumbrüche)',
  zoomIn: 'Vergrößern',
  zoomOut: 'Verkleinern',
  resetZoom: 'Zoom zurücksetzen',
  addBlock: 'Block hinzufügen',
  noBlockTypes: 'Keine Blocktypen verfügbar',
  fitToContent: 'An Inhalt anpassen',
  saved: 'Gespeichert!',
  savedToLocal: 'Workflow in localStorage gespeichert',
  loaded: 'Geladen!',
  loadedFromLocal: 'Workflow aus localStorage geladen',
  noDataFound: 'Keine Daten gefunden',
  couldNotFindWorkflow: 'Gespeicherter Workflow nicht gefunden',
  cleared: 'Gelöscht!',
  canvasCleared: 'Leinwand wurde geleert',
  imported: 'Importiert!',
  importedFromJson: 'Aus JSON importiert',
  copied: 'Kopiert!',
  jsonCopied: 'Workflow-JSON in die Zwischenablage kopiert',
  simulatingTitle: 'Simuliert...',
  simulatingDesc: 'Ausführen',
  groupCreated: 'Gruppe erstellt',
  blocksGrouped: 'Blöcke gruppiert',
  savedToDb: 'in der Datenbank gespeichert',
  error: 'Fehler',
  saveError: 'Fehler beim Speichern',
  loadError: 'Fehler beim Laden',
  deleteError: 'Fehler beim Löschen',
  deleted: 'Gelöscht',
  documentDeleted: 'Dokument wurde gelöscht',
  exportRefSaved: 'Exportreferenz gespeichert',
  exportRefDesc: 'Eine PNG-Exportreferenz wurde in der Datenbank gespeichert',
  exportPngDesc: 'PNG-Export erfordert dom-to-image-Bibliothek',
  couldNotCopy: 'Konnte nicht in die Zwischenablage kopieren',
  couldNotReadWorkflow: 'Workflow-Daten konnten nicht gelesen werden',
  saveWorkflow: 'Workflow speichern',
  saveAsNew: 'Als neu speichern',
  titleLabel: 'Titel',
  categoryLabel: 'Kategorie',
  cancel: 'Abbrechen',
  loadWorkflow: 'Workflow laden',
  noDocuments: 'Keine gespeicherten Dokumente',
  loadBtn: 'Laden',
  deleteBtn: 'Löschen',
  importJson: 'JSON importieren',
  pasteJsonHere: 'WorkflowState JSON hier einfügen...',
  importBtn: 'Importieren',
  invalidJson: 'Ungültiges JSON',
  invalidStructure: 'JSON fehlt die erforderliche Struktur (blocks, connectors, groups)',
  helpTitle: 'Anleitung & Dokumentation',
  integrationTab: 'Integration',
  databaseTab: 'Datenbank',
  jsonTab: 'JSON-Zuordnung',
  integrationTitle: 'So integrieren Sie',
  integrationDesc: 'Flow Weaver kann als eigenständige App laufen oder über iframe in ein anderes Projekt eingebettet werden.',
  integrationStep1: '1. Veröffentlichen Sie die App über Lovable und kopieren Sie die veröffentlichte URL (z.B. https://flow-weaver-05.lovable.app)',
  integrationStep2: '2. Einbetten mit <iframe ref={iframeRef} src="URL" width="100%" height="600px" />',
  integrationStep3: '3. Ohne Datenbankintegration wird alles im localStorage des Browsers gespeichert',
  integrationStep4: '4. Um in die Datenbank IHRES Projekts zu speichern: Senden Sie Ihre Supabase-Anmeldedaten per postMessage an den iframe (siehe Codebeispiel unten)',
  integrationStep5: '5. Der iframe hört automatisch auf die Nachricht "supabase-credentials" und wechselt zu Ihrer Datenbank',
  integrationStep6: '6. Der Benutzer muss in der Host-App angemeldet sein — das Access Token der Session wird mitgesendet',
  integrationStep7: '7. Wenn keine Anmeldedaten gesendet werden, fällt der iframe auf localStorage zurück (keine Daten gehen verloren)',
  integrationCodeTitle: 'Codebeispiel — Anmeldedaten von der Host-App senden',
  integrationCodeExample: `import { useRef, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

function FlowchartPage() {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const sendCredentials = useCallback(async () => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;

    iframe.contentWindow.postMessage({
      type: 'supabase-credentials',
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
      supabaseKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      accessToken: session.access_token,
    }, '*');
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'request-supabase-credentials') {
        sendCredentials();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [sendCredentials]);

  return (
    <iframe
      ref={iframeRef}
      src="https://flow-weaver-05.lovable.app"
      onLoad={() => setTimeout(sendCredentials, 500)}
      className="w-full h-full"
    />
  );
}`,
  integrationNote: '⚠️ Die Host-App benötigt eine "workflows"-Tabelle mit den Spalten: id (uuid), title (text), blocks (jsonb), connectors (jsonb), groups (jsonb), workflow_name (text), coordinate_origin (text), category (text), created_by (uuid), created_at, updated_at, is_pinned (boolean). RLS-Richtlinien sollen CRUD basierend auf auth.uid() = created_by erlauben. Zusätzlich wird eine "save-workflow" Edge Function benötigt, die JWT validiert und Insert/Update verwaltet.',
  databaseTitle: 'Datenbankanforderungen',
  databaseDesc: 'Die Host-App benötigt Folgendes, damit die Datenbankspeicherung funktioniert:',
  databaseReq1: 'Tabelle: workflows — id (uuid PK), title (text), blocks (jsonb), connectors (jsonb), groups (jsonb), workflow_name (text), coordinate_origin (text), category (text, default "workflow"), created_by (uuid), is_pinned (boolean), last_modified_by (uuid), created_at, updated_at',
  databaseReq2: 'Eine "save-workflow" Edge Function, die JWT validiert (via getClaims), die Benutzer-ID extrahiert und Insert/Update gegen die workflows-Tabelle verwaltet',
  databaseReq3: 'RLS-Richtlinien: auth.uid() = created_by für SELECT, INSERT, UPDATE, DELETE',
  databaseReq4: 'Der Benutzer muss in der Host-App angemeldet sein — die Session wird per postMessage an den iframe gesendet',
  jsonTitle: 'JSON-Zuordnung',
  jsonDesc: 'Workflows werden als JSON mit folgender Struktur gespeichert (WorkflowState):',
  jsonStep1: '1. Klicken Sie "JSON kopieren" in der Symbolleiste, um den aktuellen Flow zu exportieren',
  jsonStep2: '2. Klicken Sie "Importieren", um einen Flow aus JSON einzufügen und zu laden',
  jsonStep3: '3. Jeder Block hat id, type, x, y, config (Name, Beschreibung, Icon) und color. Blöcke können über die Ebenen-Schaltfläche in Ebenen (leere eingeklappte Gruppen) umgewandelt werden.',
  jsonStep4: '4. Verbindungen referenzieren fromId/toId. Gruppen können 0+ Blöcke haben, unterstützen Verschachtelung via parentGroupId, und können mit collapsed/collapsedX/collapsedY zu Ebenen eingeklappt werden.',
  jsonStructure: '{ "name": "...", "blocks": [...], "connectors": [{ fromId, toId, fromPort, toPort }], "groups": [{ id, blockIds, color, name, collapsed, collapsedX, collapsedY, parentGroupId, locked }] }',
  aiPromptTab: 'AI-Prompt',
  aiPromptTitle: 'Abläufe mit AI generieren',
  aiPromptDesc: 'Sie können jede beliebige AI (ChatGPT, Grok, Claude, Lovable usw.) verwenden, um automatisch JSON zu generieren, das direkt in Flow Weaver importiert werden kann. Geben Sie der AI den Prompt unten zusammen mit Ihrem Material.',
  aiPromptUseCases: 'Anwendungsfälle',
  aiPromptUseCase1: 'Bitten Sie eine AI, ein bestehendes Projekt oder System in ein Flussdiagramm zu überführen',
  aiPromptUseCase2: 'Laden Sie Bilder hoch (Architekturdiagramme, Whiteboards) und lassen Sie die AI sie in JSON konvertieren',
  aiPromptUseCase3: 'Senden Sie Dokumentation, Spezifikationen oder Anforderungslisten und erhalten Sie einen visuellen Ablauf',
  aiPromptUseCase4: 'Beschreiben Sie einen Prozess in Worten und lassen Sie die AI das Flussdiagramm für Sie erstellen',
  aiPromptUseCase5: 'Erstellen Sie komplexe mehrschichtige Architekturen mit verschachtelten Ebenen — Übersicht auf oberster Ebene, detaillierte Logik innerhalb',
  aiPromptTemplateTitle: 'Kopierbarer AI-Prompt',
  aiPromptTemplate: `Du bist ein JSON-Generator für Flow Weaver. Gib NUR ein gültiges JSON-Objekt (WorkflowState) zurück. Kein erklärender Text.

══════════════════════════════════
  SCHEMA (Beispielblock)
══════════════════════════════════
{
  "name": "Ablaufname",
  "blocks": [
    { "id": "b01", "type": "general", "x": 200, "y": 100,
      "config": { "name": "Blockname", "description": "Beschreibung", "icon": "wrench" },
      "color": "#6366f1" }
  ],
  "connectors": [
    { "id": "c01", "fromId": "b01", "toId": "b02", "fromPort": "right", "toPort": "left" }
  ],
  "groups": [
    { "id": "g1", "blockIds": ["b01","b02"], "color": "#6366f1", "name": "Gruppenname",
      "locked": false, "collapsed": true, "parentGroupId": null,
      "collapsedX": 200, "collapsedY": 100 }
  ]
}

══════════════════════════════════
  1. BLOCKTYPEN
══════════════════════════════════
"create-user" | "disable-mailbox" | "delete-m365" | "send-email"
"webhook"      | "add-to-group"   | "condition"    | "general"

══════════════════════════════════
  2. ICONS (nach Kategorie)
══════════════════════════════════
Personen:    user, users
Kommunikation: mail, send, message-square, phone, bell
Cloud/Netz:  cloud, globe, server, database, link
Dateien:     file-text, folder, archive, download, upload, copy
Werkzeuge:   wrench, settings, terminal, filter, search
Status:      check-circle, alert-triangle, clock, flag, star
Sicherheit:  shield, lock, key, eye
Medien:      play, camera, monitor
Logik:       git-branch, shuffle, layers, refresh-cw
Sonstiges:   zap, power, heart, tag, bookmark, bar-chart, package, trash, thumbs-up, calendar, help-circle

══════════════════════════════════
  3. RASTER & KOORDINATEN
══════════════════════════════════
- x und y MÜSSEN Vielfache von 20 sein
- Horizontaler Abstand: ~240px
- Vertikaler Abstand:   ~220px
- ID-Format: "b01", "b02", ... (Blöcke), "c01", "c02", ... (Verbindungen), "g1", "g2", ... (Gruppen)
- Alle IDs müssen eindeutig sein

══════════════════════════════════
  4. PORT-ROUTING (KRITISCH!)
══════════════════════════════════
Bestimme fromPort/toPort durch Vergleich von source (x,y) mit target (x,y):

  Richtung             │ fromPort       │ toPort
  ─────────────────────┼────────────────┼────────────────
  → Rechts (gleiches y)│ "right"        │ "left"
  ← Links (gleiches y) │ "left"         │ "right"
  ↓ Unten (gleiches x) │ "bottom"       │ "top"
  ↑ Oben (gleiches x)  │ "top"          │ "bottom"
  ↘ Diag unten-rechts  │ "bottom-right" │ "top-left"
  ↙ Diag unten-links   │ "bottom-left"  │ "top-right"
  ↗ Diag oben-rechts   │ "top-right"    │ "bottom-left"
  ↖ Diag oben-links    │ "top-left"     │ "bottom-right"

⚠ Verwende NIEMALS diagonale Ports für Blöcke in derselben Zeile (y) oder Spalte (x).
⚠ fromPort und toPort MÜSSEN immer angegeben werden.

══════════════════════════════════
  5. GRUPPEN & VERSCHACHTELUNG
══════════════════════════════════
Gruppen-Felder:
  "id":            Eindeutige Gruppen-ID (z.B. "g1", "g2")
  "blockIds":      Array von Block-IDs (KANN LEER SEIN [] für Ebenen)
  "color":         Hexfarbe (z.B. "#8b5cf6")
  "name":          Anzeigename
  "locked":        false (Standard) — true = Blöcke bewegen sich zusammen
  "collapsed":     true/false — true = Gruppe wird als kompakte Karte angezeigt (EBENEN-MODUS)
  "parentGroupId": null für oberste Ebene, oder ID der Elterngruppe für Verschachtelung
  "collapsedX":    X-Position der Karte (Vielfaches von 20)
  "collapsedY":    Y-Position der Karte (Vielfaches von 20)

Regeln:
- Gruppen können 0 oder mehr Blöcke haben (leere Gruppen = leere Ebenen)
- Einzelne Blöcke können über die Ebenen-Schaltfläche in Ebenen umgewandelt werden
- Verschachtelte Gruppen verwenden UNABHÄNGIGE Koordinatensysteme (jede Ebene ist eine eigene Leinwand)
- parentGroupId verknüpft Untergruppen mit Elterngruppen

══════════════════════════════════
  6. EBENENSYSTEM (FORTGESCHRITTEN)
══════════════════════════════════
Gruppen mit "collapsed": true werden navigierbare Ebenen:

🏗️ ARCHITEKTURMUSTER:
  Wurzelebene: Übersicht mit eingeklappten Gruppen als kompakte Karten
  Ebene 1:     Teilsysteme — jede eingeklappte Gruppe expandiert zu eigener Leinwand
  Ebene 2+:    Detaillierte Implementierungslogik in verschachtelten Gruppen

🔄 BLOCK-ZU-EBENE-KONVERTIERUNG:
  - Einzelne Blöcke können über die Ebenen-Schaltfläche in Ebenen umgewandelt werden
  - Alle Verbindungen zum/vom Block werden auf die neue Ebene umgeleitet
  - Verwende dies, um "Platzhalter-Ebenen" zu erstellen, die später gefüllt werden

📐 LAYOUT-STRATEGIE:
  - Platziere eingeklappte Gruppen wie Blöcke (~240px Abstand)
  - Verwende collapsedX/collapsedY zum Positionieren der Karten
  - Innerhalb jeder Gruppe: Blöcke unabhängig layouten (eigenes Koordinatensystem, starte bei x:200, y:100)

🔗 EBENENÜBERGREIFENDE VERBINDUNGEN:
  - Verbindungen können Gruppen-IDs direkt referenzieren (fromId/toId = Gruppen-ID)

══════════════════════════════════
  7. DESIGNMUSTER
══════════════════════════════════
📊 PIPELINE: Linearer Ablauf → "right"/"left"-Ports, gleiches Y
📐 VERZWEIGUNG: Bedingung mit mehreren Pfaden → "bottom"/"right"-Ports
🎨 FARBCODIERUNG: Verwende verschiedene Farben pro Domäne:
   Infrastruktur: "#6366f1"  API: "#8b5cf6"  Auth: "#ec4899"
   Daten: "#06b6d4"  UI: "#22c55e"  Monitoring: "#f97316"

══════════════════════════════════
  CHECKLISTE VOR DER AUSGABE
══════════════════════════════════
✅ Alle x/y sind Vielfache von 20
✅ Alle Verbindungen haben fromPort + toPort
✅ Portwahl stimmt mit relativer Position überein (siehe Tabelle oben)
✅ Alle IDs sind eindeutig
✅ Gruppen können leer sein (blockIds: []) — keine Mindestanforderung
✅ Eingeklappte Gruppen haben collapsedX/collapsedY gesetzt
✅ parentGroupId verweist auf gültige Gruppen-ID (oder null)
✅ Blöcke innerhalb von Ebenen haben unabhängige Koordinaten
✅ Wegpunkte (optional): füge "waypoints": [{"x":…,"y":…}] zu einer Verbindung hinzu, um die Kurve manuell zu steuern
✅ Immer "coordinateOrigin": "center" auf Wurzelebene einschließen
✅ Die Ausgabe ist NUR JSON`,
  aiPromptTipsTitle: 'Tipps für beste Ergebnisse',
  aiPromptTip1: 'Fügen Sie Bilder vorhandener Diagramme bei — die AI kann sie interpretieren und in JSON konvertieren',
  aiPromptTip2: 'Beschreiben Sie die Prozessschritte als Aufzählungsliste für ein klareres Ergebnis',
  aiPromptTip3: 'Geben Sie die gewünschte Anzahl von Blöcken und Gruppierungen in Ihrem Prompt an',
  aiPromptTip4: 'Kopieren Sie das JSON-Ergebnis und fügen Sie es über die Import-Schaltfläche in Flow Weaver ein',
  aiPromptCopy: 'Prompt kopieren',
  aiPromptCopied: 'Prompt kopiert!',
  close: 'Schließen',
  saveFile: 'Datei speichern',
  loadFile: 'Datei laden',
  welcome: 'Willkommen',
  savedAsFile: 'Workflow als Datei gespeichert',
  donate: 'Spenden',
  undo: 'Rückgängig',
  redo: 'Wiederholen',
  rename: 'Umbenennen',
  editName: 'Name bearbeiten',
  editDescription: 'Beschreibung bearbeiten',
  editColor: 'Farbe bearbeiten',
  editBlock: 'Block bearbeiten',
  editGroup: 'Gruppe bearbeiten',
  editLayer: 'Ebene bearbeiten',
  groupThisBlock: 'Diesen Block gruppieren',
  createGroupFromSelection: 'Gruppe aus Auswahl erstellen',
  convertToLayer: 'In Ebene umwandeln',
  duplicate: 'Duplizieren',
  explainBlock: 'Block erklären…',
  delete: 'Löschen',
  deleteBlock: 'Block löschen',
  deleteGroup: 'Gruppe löschen',
  collapseToLayer: 'Zur Ebene zusammenklappen',
  enterLayer: 'Ebene öffnen',
  expandLayer: 'Erweitern (zurück zu Gruppe)',
  keyboardShortcutsTitle: 'Tastenkürzel',
  keyboardShortcutsHint: 'Drücke ? um dieses Fenster erneut zu öffnen.',
  blocksGroupedCount_one: '{{count}} Block gruppiert',
  blocksGroupedCount_other: '{{count}} Blöcke gruppiert',
  blocksCount_one: '{{count}} Block',
  blocksCount_other: '{{count}} Blöcke',
  connectorsCount_one: '{{count}} Verbindung',
  connectorsCount_other: '{{count}} Verbindungen',
  groupsCount_one: '{{count}} Gruppe',
  groupsCount_other: '{{count}} Gruppen',
  export: 'Exportiere',
  pleaseWait: 'Dies kann bei großen Arbeitsflächen einige Sekunden dauern…',
  exportDone: 'Export fertig',
  auditCreated: 'Erstellt',
  auditDeleted: 'Gelöscht',
  auditRenamed: 'Umbenannt',
  auditEdited: 'Bearbeitet',
  auditCoopEnabled: 'Co-op aktiviert',
  auditCoopDisabled: 'Co-op deaktiviert',
  auditRedactedActor: '[gelöschter Benutzer]',
  auditCollabInvite: 'Benutzer eingeladen',
  auditCollabRemove: 'Benutzer entfernt',
  auditCollabRoleChange: 'Rolle geändert für Benutzer',
  auditCollabAccept: 'Einladung angenommen:',
  crossSheetLink: 'Blattübergreifender Link',
  crossSheetActive: 'Blattübergreifende Verknüpfung…',
  crossSheetHint: 'Blöcke zwischen benachbarten Blättern verknüpfen',
  crossSheetActiveHint: 'Esc drücken zum Abbrechen',
  back: 'Zurück',
  processEngineDisabled: 'Process Engine ist in dieser Installation deaktiviert.',
  processEngineUpsellTitle: 'Process Engine freischalten',
  processEngineUpsellBody: 'Erstelle Attest-Flows, Provisionierung und Offboarding aus deinen Flow-weaver-Diagrammen. Erfordert {tier} oder höher.',
  processEngineUpsellCta: 'Pläne ansehen',
  processEngineLoadTitle: 'Flow-weaver-Ablauf laden',
  processEngineLoadFromFile: 'Aus Datei (.json)',
  processEngineLoadFromPaste: 'JSON einfügen',
  processEngineLoadImport: 'Importieren',
  processEngineImportSuccess: 'Ablauf importiert',
  processEngineImportFailed: 'Ablauf konnte nicht importiert werden',
  processEngineLiveAvailable: 'Live-Ausführungen verfügbar',
  processEngineDesignOnly: 'Nur Design — auf {tier} upgraden für Live-Ausführungen',
  processEngineDryRun: 'Probelauf',
  processEngineDryRunDone: 'Probelauf abgeschlossen',
  processEngineRunning: 'Läuft…',
  processEngineLoadFromTemplate: 'Oder mit Vorlage starten',
  processEngineOpenFinal: 'Final-Ansicht',
  processEngineSuggest: 'KI-Vorschlag',
  processEngineSuggestNone: 'Keine Treffer',
  processEngineSuggestDone: 'Vorschläge hinzugefügt',
  processEngineRunLive: 'Live ausführen',
  processEngineRunDone: 'Live-Ausführung abgeschlossen',
  processEngineRunFailed: 'Live-Ausführung fehlgeschlagen',
  processEngineRunBlocked: 'Live-Ausführung blockiert',
  processEngineSave: 'Speichern',
  processEngineSaving: 'Speichern…',
  processEngineSaveDone: 'Gespeichert',
  processEngineSaveFailed: 'Speichern fehlgeschlagen',
  processEngineLoadFromSaved: 'Gespeicherte Abläufe',
  finalTldr: 'Zusammenfassung',
  finalPageTitle: 'Final',
  finalSubtitle: 'Ein vollständiges Bild aus Design, Wissen und Betrieb.',
  finalAudience: 'Zielgruppe',
  finalHubTitle: 'Systemübersicht',
  finalHubSubtitle:
    'Live-Status für jedes System. Öffne einen Container, um tiefer einzusteigen, oder promote einen Ablauf aus Process Engine für die detaillierte Drei-Akt-Geschichte unten.',
  finalContainerFlowWeaverTitle: 'Flow-weaver',
  finalContainerFlowWeaverEmpty: 'Noch kein Workflow gespeichert — öffne Flow-weaver, um zu beginnen.',
  finalContainerOpenFlowWeaver: 'Flow-weaver öffnen',
  finalContainerProcessEngineTitle: 'Process Engine',
  finalContainerProcessEngineSubtitle: '{count} gespeicherte Abläufe',
  finalContainerProcessEngineEmpty: 'Keine gespeicherten Abläufe — promote einen Workflow zu Process.',
  finalContainerOpenProcessEngine: 'Process Engine öffnen',
  finalContainerAtlasTitle: 'Atlas',
  finalContainerAtlasEmpty: 'Atlas-Manifest nicht geladen.',
  finalContainerOpenAtlas: 'Atlas öffnen',
  finalContainerGroups: 'Gruppen',
  finalContainerFlows: 'Abläufe',
  finalContainerTotalBlocks: 'Blöcke gesamt',
  finalContainerCategories: 'Kategorien',
  finalNoFlowTitle: 'Kein Ablauf gewählt',
  finalNoFlowBody: 'Öffne Process Engine und lade zuerst einen Ablauf.',
  finalGoToProcessEngine: 'Process Engine öffnen',
  finalActDesign: 'Design',
  finalActDesignSubtitle: 'Wie der Prozess in Flow-weaver modelliert wurde.',
  finalActKnowledge: 'Wissen',
  finalActKnowledgeSubtitle: 'Was die Organisation weiß und in Atlas verknüpft.',
  finalActOperation: 'Betrieb',
  finalActOperationSubtitle: 'Wie sich der Ablauf in 30 Tagen Betrieb verhält.',
  finalSheets: 'Blätter',
  finalBlocks: 'Blöcke',
  finalConnectors: 'Verbindungen',
  finalBindings: 'Bindungen',
  finalAtlas: 'Atlas',
  finalDocs: 'Dokumente',
  finalCrossDocRefs: 'Querverweise',
  finalRuns30d: 'Läufe (30 T)',
  finalSuccessRate: 'Erfolgsquote',
  finalMeanDuration: 'Mittl. Dauer',
  finalRoi: 'Wert',
  finalRoiHoursSaved: 'Eingesparte Stunden',
  finalRoiErrorsPrevented: 'Verhinderte Fehler',
  finalRoiMtto: 'MTTO',
  finalRoiValue: 'Wert',
  finalRoiMethodology: 'Konservative Berechnung: 30 min eingesparte manuelle Arbeit pro erfolgreichem Lauf × Mischstundensatz 45 €.',
  finalWatermark: 'Erstellt: {watermark}',
  finalDisabled: 'Die Final-Ansicht ist in dieser Installation deaktiviert.',
  finalUpsellTitle: 'Final-Ansicht freischalten',
  finalUpsellBody: 'Eine schreibgeschützte Führungsübersicht über Flow-weaver, Atlas und Process Engine. Erfordert {tier} oder höher.',
  finalUpsellCta: 'Pläne ansehen',
  finalPhaseHint: 'Die Final-Ansicht folgt in einer späteren Phase.',
  blockCreated: 'Block hinzugefügt',
  toggleCollapse: 'Einklappen umschalten',
  toggleLock: 'Sperre umschalten',
};

const fr: TranslationKeys = {
  appTitle: 'EngiCoro Flow Weaver',
  save: 'Enregistrer',
  load: 'Charger',
  exportPng: 'Exporter image',
  import_: 'Importer',
  copyJson: 'Copier JSON',
  simulate: 'Simuler',
  clear: 'Effacer',
  connectedDb: 'Connecté à la base de données',
  usingLocal: 'Utilise localStorage',
  db: 'BD',
  local: 'Local',
  help: 'Aide',

  // MobileTopBar / drawer (#20, Wave D)
  aiAndJson: 'IA & JSON',
  aiAssistant: 'Assistant IA',
  cloudWorkspaces: 'Espaces de travail Cloud',
  shareWorkspace: 'Partager l\'espace',
  reportBug: 'Signaler un bug',
  featureRequest: 'Demande de fonctionnalité',
  adminDashboard: 'Tableau admin',
  signIn: 'Connexion',
  signOut: 'Déconnexion',
  openMenu: 'Ouvrir le menu',
  menu: 'Menu',
  notSignedIn: 'Non connecté',
  gettingStartedTab: 'Démarrage',
  gettingStartedTitle: 'Bienvenue dans Flow Weaver',
  gettingStartedDesc: 'Flow Weaver est un outil visuel pour créer des organigrammes et des diagrammes d\'orchestration. Glissez des blocs, connectez-les et organisez-les en groupes — le tout directement dans votre navigateur.',
  gsWhatIsTitle: '🎯 Qu\'est-ce que Flow Weaver ?',
  gsWhatIsDesc: 'Un outil glisser-déposer pour construire des flux visuels. Parfait pour documenter les processus IT, l\'architecture système, les flux d\'intégration, les automatisations et bien plus.',
  gsAddBlocksTitle: '➕ Ajouter des blocs',
  gsAddBlocksDesc: 'Glissez un bloc depuis le panneau latéral gauche et déposez-le sur le canevas. Chaque bloc représente une étape de votre flux. Choisissez parmi les types prédéfinis ou utilisez le bloc "Fonction générale".',
  gsConnectTitle: '🔗 Connecter des blocs',
  gsConnectDesc: 'Cliquez sur "Connecter blocs" dans le panneau latéral, puis cliquez sur un port (les petits cercles autour des blocs) pour démarrer la connexion et cliquez sur un autre port pour la terminer. Les blocs ont 8 ports (haut, droite, bas, gauche + coins). Double-cliquez sur une ligne pour ajouter un point de passage — les points de passage courbent doucement la ligne. Double-cliquez sur un point de passage pour le supprimer.',
  gsGroupTitle: '📦 Grouper des blocs',
  gsGroupDesc: 'Cliquez sur "Regrouper blocs", puis cliquez sur les blocs que vous souhaitez grouper (ils seront mis en évidence), et cliquez à nouveau sur le bouton pour créer le groupe.',
  gsEditTitle: '✏️ Modifier les propriétés',
  gsEditDesc: 'Cliquez sur un bloc ou un groupe pour ouvrir le panneau de propriétés à droite. Vous pouvez y modifier le nom, la description, l\'icône et la couleur.',
  gsSaveLoadTitle: '💾 Sauvegarder et charger',
  gsSaveLoadDesc: 'Cliquez "Enregistrer" pour sauvegarder votre flux. Sans base de données, il est enregistré dans le localStorage du navigateur. Avec une connexion base de données, il est sauvegardé de façon permanente.',
  gsImportExportTitle: '📤 Import et export',
  gsImportExportDesc: 'Utilisez "Copier JSON" pour exporter votre flux en texte JSON. Utilisez "Importer" pour coller du JSON et charger un flux. Vous pouvez aussi exporter en image PNG.',
  gsAiTitle: '🤖 Créer des flux avec l\'IA',
  gsAiDesc: 'Cliquez sur le bouton "AI & JSON" dans la barre d\'outils pour ouvrir un dialogue avec un prompt IA prêt à l\'emploi et une référence JSON. Copiez le prompt vers n\'importe quelle IA (ChatGPT, Claude, Grok, etc.), décrivez votre système et importez le JSON généré directement dans Flow Weaver.',
  gsKeyboardTitle: '⌨️ Navigation & Raccourcis',
  gsKeyboardShortcuts: 'Défilez pour zoomer\n• Cliquez et glissez sur le canevas pour panoramiquer\n• Glissez les blocs pour les déplacer\n• Double-cliquez sur une couche repliée pour plonger dedans\n• Appuyez sur Entrée sur une couche repliée pour l\'ouvrir\n• Fil d\'Ariane pour la navigation par couches\n• Double-cliquez sur une ligne pour ajouter un point de passage\n• Double-cliquez sur un point de passage pour le supprimer\n• Touches fléchées pour naviguer entre les blocs\n• Ctrl+Z / Cmd+Z pour annuler\n• Ctrl+Shift+Z / Cmd+Shift+Z pour rétablir\n• Ctrl+S / Cmd+S pour enregistrer\n• Suppr / Retour arrière pour supprimer le bloc sélectionné\n• Échap pour désélectionner',
  gsLayersTitle: '🧩 Couches & Imbrication',
  gsLayersDesc: 'Les groupes peuvent être repliés en cartes compactes représentant des couches entières. Cliquez sur l\'icône de couche (⧉) à côté d\'un groupe dans le panneau latéral. Double-cliquez sur une carte repliée pour plonger dans la couche. Des fil d\'Ariane apparaissent pour naviguer en arrière. Imbrication illimitée prise en charge.',
  tools: 'Outils',
  createUser: 'Activité',
  disableMailbox: 'Communication',
  deleteM365: 'Système',
  sendEmail: 'Notification',
  webhook: 'Événement',
  addToGroup: 'Partie prenante',
  condition: 'Décision',
  generalFunction: 'Processus',
  actions: 'Actions',
  connecting: 'Connexion...',
  connectBlocks: 'Connecter blocs',
  grouping: 'Regroupement',
  groupBlocks: 'Regrouper blocs',
  blocks: 'Blocs',
  groups: 'Groupes',
  changeColor: 'Changer couleur',
  dragToCanvas: 'Glisser les outils sur le canevas',
  lockGroup: 'Verrouiller groupe',
  unlockGroup: 'Déverrouiller groupe',
  search: 'Rechercher...',
  searchCanvasPlaceholder: 'Tout rechercher…',
  searchCanvasTooltip: 'Tapez pour filtrer. Entrée ouvre le canevas de recherche. Cmd+K pour focaliser.',
  searchCanvasScopeAll: 'Tout',
  searchCanvasScopeWorkflows: 'Workflows',
  searchCanvasScopeAtlas: 'Atlas',
  searchCanvasMatchCount: '{count} résultats',
  collapseAll: 'Tout réduire',
  collapseToLayers: 'Réduire en couches',
  expandAll: 'Tout développer',
  groupBatchActions: 'Actions de groupe',
  coopJoined: 'Espace de travail Co-op rejoint',
  coopAccessDenied: 'Pas d\'accès à cet espace de travail',
  coopAccessDeniedHint: 'Demandez au propriétaire d\'activer Co-op et de vous inviter.',
  coopLoadFailed: 'Impossible de charger l\'espace partagé',
  coopEnableTitle: 'Activer Co-op',
  coopEnableHint: 'Doit être activé pour que le lien de partage fonctionne',
  coopActive: 'Co-op est actif',
  coopInactive: 'Co-op est désactivé',
  coopCollaborators: 'Collaborateurs',
  auditHistory: 'Historique',
  auditEmpty: 'Aucune modification enregistrée pour le moment.',
  groupProperties: 'Propriétés du groupe',
  blockProperties: 'Propriétés du bloc',
  name: 'Nom',
  description: 'Description',
  details: 'Détails…',
  recipient: 'Destinataire',
  subject: 'Sujet',
  blockColor: 'Couleur du bloc',
  groupColor: 'Couleur du groupe',
  templateSettings: 'Paramètres du modèle',
  trigger: 'Déclencheur',
  userCreated: 'Utilisateur Créé',
  userModified: 'Utilisateur Modifié',
  userDeleted: 'Utilisateur Supprimé',
  custom: 'Personnalisé',
  overview: 'Aperçu',
  clickToConfig: 'Cliquez sur un bloc ou un groupe pour configurer',
  blocksInGroup: 'blocs dans le groupe',
  writeDescription: 'Écrivez une description... (prend en charge les retours à la ligne)',
  zoomIn: 'Agrandir',
  zoomOut: 'Réduire',
  resetZoom: 'Réinitialiser le zoom',
  addBlock: 'Ajouter un bloc',
  noBlockTypes: 'Aucun type de bloc disponible',
  fitToContent: 'Ajuster au contenu',
  saved: 'Enregistré !',
  savedToLocal: 'Workflow enregistré dans localStorage',
  loaded: 'Chargé !',
  loadedFromLocal: 'Workflow chargé depuis localStorage',
  noDataFound: 'Aucune donnée trouvée',
  couldNotFindWorkflow: 'Impossible de trouver le workflow enregistré',
  cleared: 'Effacé !',
  canvasCleared: 'Le canevas a été vidé',
  imported: 'Importé !',
  importedFromJson: 'Importé depuis JSON',
  copied: 'Copié !',
  jsonCopied: 'JSON du workflow copié dans le presse-papiers',
  simulatingTitle: 'Simulation...',
  simulatingDesc: 'Exécution',
  groupCreated: 'Groupe créé',
  blocksGrouped: 'blocs regroupés',
  savedToDb: 'enregistré dans la base de données',
  error: 'Erreur',
  saveError: 'Erreur d\'enregistrement',
  loadError: 'Erreur de chargement',
  deleteError: 'Erreur de suppression',
  deleted: 'Supprimé',
  documentDeleted: 'Le document a été supprimé',
  exportRefSaved: 'Référence d\'export enregistrée',
  exportRefDesc: 'Une référence d\'export PNG a été enregistrée dans la base de données',
  exportPngDesc: 'L\'export PNG nécessite la bibliothèque dom-to-image',
  couldNotCopy: 'Impossible de copier dans le presse-papiers',
  couldNotReadWorkflow: 'Impossible de lire les données du workflow',
  saveWorkflow: 'Enregistrer le Workflow',
  saveAsNew: 'Enregistrer comme nouveau',
  titleLabel: 'Titre',
  categoryLabel: 'Catégorie',
  cancel: 'Annuler',
  loadWorkflow: 'Charger le Workflow',
  noDocuments: 'Aucun document enregistré',
  loadBtn: 'Charger',
  deleteBtn: 'Supprimer',
  importJson: 'Importer JSON',
  pasteJsonHere: 'Collez le JSON WorkflowState ici...',
  importBtn: 'Importer',
  invalidJson: 'JSON invalide',
  invalidStructure: 'Le JSON n\'a pas la structure requise (blocks, connectors, groups)',
  helpTitle: 'Instructions & Documentation',
  integrationTab: 'Intégration',
  databaseTab: 'Base de données',
  jsonTab: 'Mappage JSON',
  integrationTitle: 'Comment intégrer',
  integrationDesc: 'Flow Weaver peut fonctionner comme application autonome ou être intégré dans un autre projet via iframe.',
  integrationStep1: '1. Publiez l\'application via Lovable et copiez l\'URL publiée (ex: https://flow-weaver-05.lovable.app)',
  integrationStep2: '2. Intégrez avec <iframe ref={iframeRef} src="URL" width="100%" height="600px" />',
  integrationStep3: '3. Sans intégration de base de données, tout est enregistré dans le localStorage du navigateur',
  integrationStep4: '4. Pour enregistrer dans la base de données de VOTRE projet : envoyez vos identifiants Supabase à l\'iframe via postMessage (voir l\'exemple de code ci-dessous)',
  integrationStep5: '5. L\'iframe écoute automatiquement le message "supabase-credentials" et bascule vers votre base de données',
  integrationStep6: '6. L\'utilisateur doit être connecté dans l\'application hôte — le jeton d\'accès de la session est envoyé',
  integrationStep7: '7. Si aucun identifiant n\'est envoyé, l\'iframe revient au localStorage (aucune donnée n\'est perdue)',
  integrationCodeTitle: 'Exemple de code — envoyer les identifiants depuis l\'application hôte',
  integrationCodeExample: `import { useRef, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

function FlowchartPage() {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const sendCredentials = useCallback(async () => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;

    iframe.contentWindow.postMessage({
      type: 'supabase-credentials',
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
      supabaseKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      accessToken: session.access_token,
    }, '*');
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'request-supabase-credentials') {
        sendCredentials();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [sendCredentials]);

  return (
    <iframe
      ref={iframeRef}
      src="https://flow-weaver-05.lovable.app"
      onLoad={() => setTimeout(sendCredentials, 500)}
      className="w-full h-full"
    />
  );
}`,
  integrationNote: '⚠️ L\'application hôte a besoin d\'une table "workflows" avec les colonnes : id (uuid), title (text), blocks (jsonb), connectors (jsonb), groups (jsonb), workflow_name (text), coordinate_origin (text), category (text), created_by (uuid), created_at, updated_at, is_pinned (boolean). Les politiques RLS doivent autoriser le CRUD basé sur auth.uid() = created_by. De plus, une edge function "save-workflow" est nécessaire pour valider le JWT et gérer les insert/update.',
  databaseTitle: 'Prérequis de la base de données',
  databaseDesc: 'L\'application hôte a besoin des éléments suivants pour que le stockage en base de données fonctionne :',
  databaseReq1: 'Table : workflows — id (uuid PK), title (text), blocks (jsonb), connectors (jsonb), groups (jsonb), workflow_name (text), coordinate_origin (text), category (text, default "workflow"), created_by (uuid), is_pinned (boolean), last_modified_by (uuid), created_at, updated_at',
  databaseReq2: 'Une edge function "save-workflow" qui valide le JWT (via getClaims), extrait l\'ID utilisateur et gère l\'insert/update dans la table workflows',
  databaseReq3: 'Politiques RLS : auth.uid() = created_by pour SELECT, INSERT, UPDATE, DELETE',
  databaseReq4: 'L\'utilisateur doit être connecté dans l\'application hôte — la session est envoyée à l\'iframe via postMessage',
  jsonTitle: 'Mappage JSON',
  jsonDesc: 'Les workflows sont stockés en JSON avec la structure suivante (WorkflowState) :',
  jsonStep1: '1. Cliquez "Copier JSON" dans la barre d\'outils pour exporter le flux actuel',
  jsonStep2: '2. Cliquez "Importer" pour coller et charger un flux depuis JSON',
  jsonStep3: '3. Chaque bloc a id, type, x, y, config (nom, description, icône) et color. Les blocs peuvent être convertis en couches (groupes repliés vides) via le bouton couche.',
  jsonStep4: '4. Les connecteurs référencent fromId/toId. Les groupes peuvent avoir 0+ blocs, supportent l\'imbrication via parentGroupId, et peuvent être repliés en couches avec collapsed/collapsedX/collapsedY.',
  jsonStructure: '{ "name": "...", "blocks": [...], "connectors": [{ fromId, toId, fromPort, toPort }], "groups": [{ id, blockIds, color, name, collapsed, collapsedX, collapsedY, parentGroupId, locked }] }',
  aiPromptTab: 'Prompt IA',
  aiPromptTitle: 'Générer des flux avec l\'IA',
  aiPromptDesc: 'Vous pouvez utiliser n\'importe quelle IA (ChatGPT, Grok, Claude, Lovable, etc.) pour générer automatiquement du JSON importable directement dans Flow Weaver. Donnez à l\'IA le prompt ci-dessous avec votre matériel.',
  aiPromptUseCases: 'Cas d\'utilisation',
  aiPromptUseCase1: 'Demandez à une IA de cartographier un projet ou système existant en organigramme',
  aiPromptUseCase2: 'Téléchargez des images (diagrammes d\'architecture, tableaux blancs) et demandez à l\'IA de les convertir en JSON',
  aiPromptUseCase3: 'Envoyez de la documentation, des spécifications ou des listes d\'exigences et obtenez un flux visuel',
  aiPromptUseCase4: 'Décrivez un processus en mots et laissez l\'IA construire l\'organigramme pour vous',
  aiPromptUseCase5: 'Construisez des architectures multi-couches complexes avec des couches imbriquées — vue d\'ensemble au niveau racine, logique détaillée à l\'intérieur',
  aiPromptTemplateTitle: 'Prompt IA copiable',
  aiPromptTemplate: `Tu es un générateur JSON pour Flow Weaver. Retourne UNIQUEMENT un objet JSON valide (WorkflowState). Aucun texte explicatif.

══════════════════════════════════
  SCHÉMA (bloc exemple)
══════════════════════════════════
{
  "name": "Nom du flux",
  "blocks": [
    { "id": "b01", "type": "general", "x": 200, "y": 100,
      "config": { "name": "Nom du bloc", "description": "Description", "icon": "wrench" },
      "color": "#6366f1" }
  ],
  "connectors": [
    { "id": "c01", "fromId": "b01", "toId": "b02", "fromPort": "right", "toPort": "left" }
  ],
  "groups": [
    { "id": "g1", "blockIds": ["b01","b02"], "color": "#6366f1", "name": "Nom du groupe",
      "locked": false, "collapsed": true, "parentGroupId": null,
      "collapsedX": 200, "collapsedY": 100 }
  ]
}

══════════════════════════════════
  1. TYPES DE BLOCS
══════════════════════════════════
"create-user" | "disable-mailbox" | "delete-m365" | "send-email"
"webhook"      | "add-to-group"   | "condition"    | "general"

══════════════════════════════════
  2. ICÔNES (par catégorie)
══════════════════════════════════
Personnes:   user, users
Communication: mail, send, message-square, phone, bell
Cloud/Réseau: cloud, globe, server, database, link
Fichiers:    file-text, folder, archive, download, upload, copy
Outils:      wrench, settings, terminal, filter, search
Statut:      check-circle, alert-triangle, clock, flag, star
Sécurité:    shield, lock, key, eye
Médias:      play, camera, monitor
Logique:     git-branch, shuffle, layers, refresh-cw
Autre:       zap, power, heart, tag, bookmark, bar-chart, package, trash, thumbs-up, calendar, help-circle

══════════════════════════════════
  3. GRILLE & COORDONNÉES
══════════════════════════════════
- x et y DOIVENT être des multiples de 20
- Espacement horizontal : ~240px
- Espacement vertical :   ~220px
- Format d'ID : "b01", "b02", ... (blocs), "c01", "c02", ... (connecteurs), "g1", "g2", ... (groupes)
- Tous les ID doivent être uniques

══════════════════════════════════
  4. ROUTAGE DES PORTS (CRITIQUE !)
══════════════════════════════════
Détermine fromPort/toPort en comparant source (x,y) avec target (x,y) :

  Direction            │ fromPort       │ toPort
  ─────────────────────┼────────────────┼────────────────
  → Droite (même y)    │ "right"        │ "left"
  ← Gauche (même y)    │ "left"         │ "right"
  ↓ Bas (même x)       │ "bottom"       │ "top"
  ↑ Haut (même x)      │ "top"          │ "bottom"
  ↘ Diag bas-droite    │ "bottom-right" │ "top-left"
  ↙ Diag bas-gauche    │ "bottom-left"  │ "top-right"
  ↗ Diag haut-droite   │ "top-right"    │ "bottom-left"
  ↖ Diag haut-gauche   │ "top-left"     │ "bottom-right"

⚠ N'utilise JAMAIS de ports diagonaux pour des blocs sur la même ligne (y) ou colonne (x).
⚠ fromPort et toPort DOIVENT toujours être spécifiés.

══════════════════════════════════
  5. GROUPES & IMBRICATION
══════════════════════════════════
Champs de groupe :
  "id":            ID de groupe unique (ex. "g1", "g2")
  "blockIds":      Tableau d'IDs de blocs (PEUT ÊTRE VIDE [] pour les couches)
  "color":         Couleur hex (ex. "#8b5cf6")
  "name":          Nom affiché
  "locked":        false (par défaut) — true = les blocs bougent ensemble
  "collapsed":     true/false — true = le groupe apparaît comme carte compacte (MODE COUCHE)
  "parentGroupId": null pour le niveau racine, ou ID du groupe parent pour l'imbrication
  "collapsedX":    Position X de la carte (multiple de 20)
  "collapsedY":    Position Y de la carte (multiple de 20)

Règles :
- Les groupes peuvent avoir 0 ou plus de blocs (groupes vides = couches vides)
- Un seul bloc peut être converti en couche via le bouton couche
- Les groupes imbriqués utilisent des systèmes de coordonnées INDÉPENDANTS (chaque couche est son propre canevas)
- parentGroupId relie les sous-groupes aux groupes parents

══════════════════════════════════
  6. SYSTÈME DE COUCHES (AVANCÉ)
══════════════════════════════════
Les groupes avec "collapsed": true deviennent des couches navigables :

🏗️ MODÈLE D'ARCHITECTURE :
  Niveau racine : Vue d'ensemble avec des groupes repliés en cartes compactes
  Couche 1 :      Sous-systèmes — chaque groupe replié s'étend en son propre canevas
  Couche 2+ :     Logique d'implémentation détaillée dans les groupes imbriqués

🔄 CONVERSION BLOC-EN-COUCHE :
  - Les blocs individuels peuvent être convertis en couches via le bouton couche
  - Tous les connecteurs vers/depuis le bloc sont redirigés vers la nouvelle couche
  - Utilisez ceci pour créer des "couches de placeholder" à remplir plus tard

📐 STRATÉGIE DE DISPOSITION :
  - Espacez les groupes repliés comme des blocs (~240px d'écart)
  - Utilisez collapsedX/collapsedY pour positionner les cartes
  - À l'intérieur de chaque groupe : disposez les blocs indépendamment (propre système de coordonnées, commencez à x:200, y:100)

🔗 CONNECTEURS INTER-COUCHES :
  - Les connecteurs peuvent référencer directement les IDs de groupe (fromId/toId = ID de groupe)

══════════════════════════════════
  7. MODÈLES DE CONCEPTION
══════════════════════════════════
📊 PIPELINE : Flux linéaire → ports "right"/"left", même Y
📐 EMBRANCHEMENT : Condition avec plusieurs chemins → ports "bottom"/"right"
🎨 CODE COULEUR : Utilisez différentes couleurs par domaine :
   Infrastructure : "#6366f1"  API : "#8b5cf6"  Auth : "#ec4899"
   Données : "#06b6d4"  UI : "#22c55e"  Monitoring : "#f97316"

══════════════════════════════════
  CHECKLIST AVANT SORTIE
══════════════════════════════════
✅ Tous les x/y sont des multiples de 20
✅ Tous les connecteurs ont fromPort + toPort
✅ Le choix des ports correspond à la position relative (voir tableau ci-dessus)
✅ Tous les ID sont uniques
✅ Les groupes peuvent être vides (blockIds: []) — aucune exigence minimale
✅ Les groupes repliés ont collapsedX/collapsedY définis
✅ parentGroupId référence un ID de groupe valide (ou null)
✅ Les blocs à l'intérieur des couches ont des coordonnées indépendantes
✅ Points de passage (optionnel) : ajouter "waypoints": [{"x":…,"y":…}] à un connecteur pour guider la courbe manuellement
✅ Toujours inclure "coordinateOrigin": "center" au niveau racine
✅ La sortie est UNIQUEMENT du JSON`,
  aiPromptTipsTitle: 'Conseils pour de meilleurs résultats',
  aiPromptTip1: 'Joignez des images de diagrammes existants — l\'IA peut les interpréter et les convertir en JSON',
  aiPromptTip2: 'Décrivez les étapes du processus sous forme de liste à puces pour un résultat plus clair',
  aiPromptTip3: 'Spécifiez le nombre souhaité de blocs et de regroupements dans votre prompt pour plus de contrôle',
  aiPromptTip4: 'Copiez le résultat JSON et collez-le via le bouton Import dans Flow Weaver',
  aiPromptCopy: 'Copier le prompt',
  aiPromptCopied: 'Prompt copié !',
  close: 'Fermer',
  saveFile: 'Enregistrer le fichier',
  loadFile: 'Charger le fichier',
  welcome: 'Bienvenue',
  savedAsFile: 'Workflow enregistré en fichier',
  donate: 'Faire un don',
  undo: 'Annuler',
  redo: 'Rétablir',
  rename: 'Renommer',
  editName: 'Modifier le nom',
  editDescription: 'Modifier la description',
  editColor: 'Modifier la couleur',
  editBlock: 'Modifier le bloc',
  editGroup: 'Modifier le groupe',
  editLayer: 'Modifier la couche',
  groupThisBlock: 'Grouper ce bloc',
  createGroupFromSelection: 'Créer un groupe depuis la sélection',
  convertToLayer: 'Convertir en couche',
  duplicate: 'Dupliquer',
  explainBlock: 'Expliquer le bloc…',
  delete: 'Supprimer',
  deleteBlock: 'Supprimer le bloc',
  deleteGroup: 'Supprimer le groupe',
  collapseToLayer: 'Réduire en couche',
  enterLayer: 'Ouvrir la couche',
  expandLayer: 'Développer (reconvertir en groupe)',
  keyboardShortcutsTitle: 'Raccourcis clavier',
  keyboardShortcutsHint: 'Appuyez sur ? pour afficher à nouveau ce panneau.',
  blocksGroupedCount_one: '{{count}} bloc regroupé',
  blocksGroupedCount_other: '{{count}} blocs regroupés',
  blocksCount_one: '{{count}} bloc',
  blocksCount_other: '{{count}} blocs',
  connectorsCount_one: '{{count}} connecteur',
  connectorsCount_other: '{{count}} connecteurs',
  groupsCount_one: '{{count}} groupe',
  groupsCount_other: '{{count}} groupes',
  export: 'Export en cours',
  pleaseWait: 'Cela peut prendre quelques secondes pour de grandes toiles…',
  exportDone: 'Export terminé',
  auditCreated: 'Créé',
  auditDeleted: 'Supprimé',
  auditRenamed: 'Renommé',
  auditEdited: 'Modifié',
  auditCoopEnabled: 'Co-op activé',
  auditCoopDisabled: 'Co-op désactivé',
  auditRedactedActor: '[utilisateur supprimé]',
  auditCollabInvite: 'Utilisateur invité',
  auditCollabRemove: 'Utilisateur retiré',
  auditCollabRoleChange: 'Rôle modifié pour utilisateur',
  auditCollabAccept: 'Invitation acceptée :',
  crossSheetLink: 'Lien inter-feuilles',
  crossSheetActive: 'Liaison inter-feuilles…',
  crossSheetHint: 'Lier des blocs entre feuilles adjacentes',
  crossSheetActiveHint: 'Appuyez sur Échap pour annuler',
  back: 'Retour',
  processEngineDisabled: 'Process Engine est désactivé sur cette installation.',
  processEngineUpsellTitle: 'Débloquer Process Engine',
  processEngineUpsellBody: 'Créez des flux d’attestation, de provisionnement et d’offboarding depuis vos schémas Flow-weaver. Requiert {tier} ou supérieur.',
  processEngineUpsellCta: 'Voir les plans',
  processEngineLoadTitle: 'Charger un flux Flow-weaver',
  processEngineLoadFromFile: 'Depuis un fichier (.json)',
  processEngineLoadFromPaste: 'Coller du JSON',
  processEngineLoadImport: 'Importer',
  processEngineImportSuccess: 'Flux importé',
  processEngineImportFailed: 'Impossible d’importer le flux',
  processEngineLiveAvailable: 'Exécutions en direct disponibles',
  processEngineDesignOnly: 'Conception uniquement — passez à {tier} pour les exécutions en direct',
  processEngineDryRun: 'Test à blanc',
  processEngineDryRunDone: 'Test à blanc terminé',
  processEngineRunning: 'En cours…',
  processEngineLoadFromTemplate: 'Ou démarrer à partir d’un modèle',
  processEngineOpenFinal: 'Vue Final',
  processEngineSuggest: 'Suggestion IA',
  processEngineSuggestNone: 'Aucune correspondance',
  processEngineSuggestDone: 'Suggestions ajoutées',
  processEngineRunLive: 'Exécuter en direct',
  processEngineRunDone: 'Exécution en direct terminée',
  processEngineRunFailed: 'Exécution en direct échouée',
  processEngineRunBlocked: 'Exécution en direct bloquée',
  processEngineSave: 'Enregistrer',
  processEngineSaving: 'Enregistrement…',
  processEngineSaveDone: 'Enregistré',
  processEngineSaveFailed: 'Échec de l’enregistrement',
  processEngineLoadFromSaved: 'Flux enregistrés',
  finalTldr: 'Résumé',
  finalPageTitle: 'Final',
  finalSubtitle: 'Une vue complète du design, des connaissances et de l’exploitation.',
  finalAudience: 'Audience',
  finalHubTitle: 'Vue système',
  finalHubSubtitle:
    'Statut en direct de chaque système. Ouvrez un conteneur pour explorer en détail, ou promouvez un flux depuis Process Engine pour voir le récit détaillé en trois actes ci-dessous.',
  finalNoFlowTitle: 'Aucun flux sélectionné',
  finalNoFlowBody: 'Ouvrez Process Engine et chargez un flux d’abord.',
  finalGoToProcessEngine: 'Ouvrir Process Engine',
  finalContainerFlowWeaverTitle: 'Flow-weaver',
  finalContainerFlowWeaverEmpty: 'Aucun workflow enregistré — ouvrez Flow-weaver pour commencer.',
  finalContainerOpenFlowWeaver: 'Ouvrir Flow-weaver',
  finalContainerProcessEngineTitle: 'Process Engine',
  finalContainerProcessEngineSubtitle: '{count} flux enregistrés',
  finalContainerProcessEngineEmpty: 'Aucun flux enregistré — promouvez un workflow vers Process.',
  finalContainerOpenProcessEngine: 'Ouvrir Process Engine',
  finalContainerAtlasTitle: 'Atlas',
  finalContainerAtlasEmpty: 'Manifest Atlas non chargé.',
  finalContainerOpenAtlas: 'Ouvrir Atlas',
  finalContainerGroups: 'Groupes',
  finalContainerFlows: 'Flux',
  finalContainerTotalBlocks: 'Blocs au total',
  finalContainerCategories: 'Catégories',
  finalActDesign: 'Conception',
  finalActDesignSubtitle: 'Comment le processus est modélisé dans Flow-weaver.',
  finalActKnowledge: 'Savoir',
  finalActKnowledgeSubtitle: 'Ce que l’organisation sait et relie dans Atlas.',
  finalActOperation: 'Exploitation',
  finalActOperationSubtitle: 'Comment le flux se comporte en production sur 30 jours.',
  finalSheets: 'Feuilles',
  finalBlocks: 'Blocs',
  finalConnectors: 'Connecteurs',
  finalBindings: 'Liaisons',
  finalAtlas: 'Atlas',
  finalDocs: 'Documents',
  finalCrossDocRefs: 'Liens croisés',
  finalRuns30d: 'Exécutions (30 j)',
  finalSuccessRate: 'Taux de succès',
  finalMeanDuration: 'Durée moy.',
  finalRoi: 'Valeur',
  finalRoiHoursSaved: 'Heures économisées',
  finalRoiErrorsPrevented: 'Erreurs évitées',
  finalRoiMtto: 'MTTO',
  finalRoiValue: 'Valeur',
  finalRoiMethodology: 'Valeur calculée prudemment : 30 min de travail manuel évité par exécution réussie × taux horaire mixte de 45 €.',
  finalWatermark: 'Généré : {watermark}',
  finalDisabled: 'La vue Final est désactivée sur cette installation.',
  finalUpsellTitle: 'Débloquer la vue Final',
  finalUpsellBody: 'Une synthèse exécutive en lecture seule sur Flow-weaver, Atlas et Process Engine. Requiert {tier} ou supérieur.',
  finalUpsellCta: 'Voir les plans',
  finalPhaseHint: 'La vue Final arrive dans une prochaine phase.',
  blockCreated: 'Bloc ajouté',
  toggleCollapse: 'Basculer le repli',
  toggleLock: 'Basculer le verrou',
};

const hu: TranslationKeys = {
  appTitle: 'EngiCoro Flow Weaver',
  save: 'Mentés',
  load: 'Betöltés',
  exportPng: 'Kép exportálása',
  import_: 'Importálás',
  copyJson: 'JSON másolása',
  simulate: 'Szimuláció',
  clear: 'Törlés',
  connectedDb: 'Adatbázishoz csatlakozva',
  usingLocal: 'localStorage használata',
  db: 'AB',
  local: 'Helyi',
  help: 'Súgó',

  // MobileTopBar / drawer (#20, Wave D)
  aiAndJson: 'AI és JSON',
  aiAssistant: 'AI-asszisztens',
  cloudWorkspaces: 'Felhő-munkaterületek',
  shareWorkspace: 'Munkaterület megosztása',
  reportBug: 'Hiba bejelentése',
  featureRequest: 'Funkciókérés',
  adminDashboard: 'Admin felület',
  signIn: 'Bejelentkezés',
  signOut: 'Kijelentkezés',
  openMenu: 'Menü megnyitása',
  menu: 'Menü',
  notSignedIn: 'Nincs bejelentkezve',
  gettingStartedTab: 'Első lépések',
  gettingStartedTitle: 'Üdvözli a Flow Weaver',
  gettingStartedDesc: 'A Flow Weaver vizuális eszköz folyamatábrák és orchestrációs diagramok készítéséhez. Húzz blokkokat, kösd össze őket, és rendezd csoportokba és rétegekbe — közvetlenül a böngésződben. Az alkalmazás futhat önállóan vagy beágyazva más projektbe iframe-ként.',
  gsWhatIsTitle: '🎯 Mi a Flow Weaver?',
  gsWhatIsDesc: 'Húzd-és-engedd alapú eszköz vizuális folyamatok építéséhez. Ideális IT-folyamatok, rendszerarchitektúrák, beléptetési folyamatok, automatizációk és sok más dokumentálásához. Korlátlan rétegmélységet, csak megtekintési módot a végfelhasználóknak, valamint PNG/SVG exportot támogat.',
  gsAddBlocksTitle: '➕ Blokkok hozzáadása',
  gsAddBlocksDesc: 'Húzz egy blokkot a bal oldali sávból a vászonra. Minden blokk a folyamat egy lépését jelenti. Válassz az előre definiált típusokból (Felhasználó létrehozása, Webhook, Feltétel stb.) vagy használd az "Általános funkciót". Kattints egy blokkra az ikon, név és leírás szerkesztéséhez.',
  gsConnectTitle: '🔗 Blokkok összekötése',
  gsConnectDesc: 'Kattints a "Blokkok összekötése" gombra a sávban. Ezután kattints egy portra (a blokkok körüli kis körök) a kapcsolat indításához, majd egy másik blokk portjára a befejezéshez. A blokkoknak 8 portja van (felső, jobb, alsó, bal + sarkok) a rugalmas útvonalvezetéshez. Kattints duplán egy összekötő vonalra útpont hozzáadásához — az útpontok simán hajlítják a görbét. Kattints duplán egy útpontra annak eltávolításához.',
  gsGroupTitle: '📦 Blokkok csoportosítása',
  gsGroupDesc: 'Kattints a "Blokkok csoportosítása" gombra, majd a csoportosítani kívánt blokkokra (kiemelve jelennek meg gyűrűvel), és kattints a gombra újra a csoport létrehozásához. A csoportok színes kerettel és címkével jelennek meg. A csoportokat zárolhatod, hogy a blokkok együtt mozogjanak.',
  gsEditTitle: '✏️ Tulajdonságok szerkesztése',
  gsEditDesc: 'Kattints egy blokkra vagy csoportra a jobb oldali tulajdonság-panel megnyitásához. Ott módosíthatod a nevet, leírást (sortörést támogat), ikont és színt. Az alul lévő minimap megmutatja a teljes vászon elrendezését és az aktuális nézetet.',
  gsLayersTitle: '🧩 Rétegek és beágyazás',
  gsLayersDesc: 'A csoportok kompakt kártyákká csukhatóak össze, amelyek egész rétegeket képviselnek. Kattints a réteg-ikonra (⧉) a sávban a be/kihajtáshoz. Kattints duplán egy összecsukott kártyára, hogy abba a rétegbe lépj. A bal felső sarokban lévő morzsasor mutatja, hol vagy, és lehetővé teszi a visszanavigálást. Egyedi blokkok rétegekké alakíthatók a réteg gombbal — ez egy üres csoportot hoz létre, amelyet feltölthetsz tartalommal. A korlátlan beágyazás támogatott.',
  gsSaveLoadTitle: '💾 Mentés és betöltés',
  gsSaveLoadDesc: 'Kattints a "Mentés" gombra a folyamat mentéséhez. Adatbázis nélkül a böngésző localStorage-ába mentődik. Adatbázis-kapcsolattal (a host alkalmazásból postMessage-en keresztül) tartósan mentődik. Használd a "Betöltés" gombot a mentett folyamatok megnyitásához. A dokumentumok kategorizálhatók és kitűzhetők.',
  gsImportExportTitle: '📤 Importálás és exportálás',
  gsImportExportDesc: 'Használd a "JSON másolása" gombot a folyamat JSON-szövegként exportálásához. Használd az "Importálás" gombot JSON beillesztéséhez és folyamat betöltéséhez. Exportálj PNG-ként vagy SVG-ként — válassz az "Aktuális munkaterület" (teljes vászon) és "Teljes áttekintés" (minden réteg kibontva) között. Az "Áttekintés" gomb mutatja a teljes hierarchiát beágyazott rétegekkel, belső összekötőkkel és entitások közötti kapcsolatokkal.',
  gsAiTitle: '🤖 Folyamatok létrehozása MI-vel',
  gsAiDesc: 'Kattints az eszköztáron az "MI és JSON" gombra egy kész MI-prompt és JSON-referencia párbeszédablak megnyitásához. Másold a promptot bármely MI-be (ChatGPT, Claude, Grok stb.), írd le a rendszered, és importáld a generált JSON-t közvetlenül a Flow Weaverbe.',
  gsKeyboardTitle: '⌨️ Navigáció, nagyítás és gyorsbillentyűk',
  gsKeyboardShortcuts: 'Ctrl/Cmd + görgetés a nagyításhoz/kicsinyítéshez\n• Görgetés a panoramázáshoz\n• Húzd a blokkokat az áthelyezésükhöz\n• Nagyításvezérlők a jobb felső sarokban (+ / − / Visszaállítás)\n• Kattints duplán egy összecsukott rétegre a belépéshez\n• Nyomj Entert egy összecsukott rétegen a megnyitáshoz\n• Morzsasor a rétegek közötti navigációhoz\n• Kattints duplán egy összekötőre útpont hozzáadásához\n• Kattints duplán egy útpontra annak eltávolításához\n• Nyílbillentyűkkel navigálhatsz a blokkok között\n• Ctrl+Z / Cmd+Z a visszavonáshoz\n• Ctrl+Shift+Z / Cmd+Shift+Z a megismétléshez\n• Ctrl+S / Cmd+S a mentéshez\n• Delete / Backspace a kijelölt blokk eltávolításához\n• Escape a kijelölés megszüntetéséhez',
  tools: 'Eszközök',
  createUser: 'Tevékenység',
  disableMailbox: 'Kommunikáció',
  deleteM365: 'Rendszer',
  sendEmail: 'Értesítés',
  webhook: 'Eseményindító',
  addToGroup: 'Érintett',
  condition: 'Döntés',
  generalFunction: 'Folyamat',
  actions: 'Műveletek',
  connecting: 'Csatlakozás...',
  connectBlocks: 'Blokkok összekötése',
  grouping: 'Csoportosítás',
  groupBlocks: 'Blokkok csoportosítása',
  blocks: 'Blokkok',
  groups: 'Csoportok',
  changeColor: 'Szín módosítása',
  dragToCanvas: 'Húzd az eszközöket a vászonra',
  lockGroup: 'Csoport zárolása',
  unlockGroup: 'Csoport feloldása',
  search: 'Keresés...',
  searchCanvasPlaceholder: 'Keresés mindenhol…',
  searchCanvasTooltip: 'Gépelj a szűréshez. Az Enter megnyitja a Keresési Vászont. Szintaxis: "kifejezés", -kizárás, név:érték, type:webhook, source:atlas. Cmd+K a fókuszáláshoz.',
  searchCanvasScopeAll: 'Minden',
  searchCanvasScopeWorkflows: 'Folyamatok',
  searchCanvasScopeAtlas: 'Atlas',
  searchCanvasMatchCount: '{count} találat',
  collapseAll: 'Minden összecsukása',
  collapseToLayers: 'Összecsukás rétegekké',
  expandAll: 'Minden kibontása',
  groupBatchActions: 'Csoportműveletek',
  coopJoined: 'Csatlakozott a kollab. munkaterülethez',
  coopAccessDenied: 'Nincs hozzáférés ehhez a munkaterülethez',
  coopAccessDeniedHint: 'Kérd meg a tulajdonost, hogy engedélyezze a kollab. módot és hívjon meg.',
  coopLoadFailed: 'Nem sikerült betölteni a megosztott munkaterületet',
  coopEnableTitle: 'Kollab. mód engedélyezése',
  coopEnableHint: 'Bekapcsolva kell lennie, hogy a megosztási link működjön',
  coopActive: 'A kollab. mód aktív',
  coopInactive: 'A kollab. mód ki van kapcsolva',
  coopCollaborators: 'Közreműködők',
  auditHistory: 'Előzmények',
  auditEmpty: 'Még nincsenek rögzített változások.',
  groupProperties: 'Csoport tulajdonságai',
  blockProperties: 'Blokk tulajdonságai',
  name: 'Név',
  description: 'Leírás',
  details: 'Részletek…',
  recipient: 'Címzett',
  subject: 'Tárgy',
  blockColor: 'Blokk színe',
  groupColor: 'Csoport színe',
  templateSettings: 'Sablonbeállítások',
  trigger: 'Eseményindító',
  userCreated: 'Felhasználó létrehozva',
  userModified: 'Felhasználó módosítva',
  userDeleted: 'Felhasználó törölve',
  custom: 'Egyéni',
  overview: 'Áttekintés',
  clickToConfig: 'Kattints egy blokkra vagy csoportra a konfiguráláshoz',
  blocksInGroup: 'blokk a csoportban',
  writeDescription: 'Írj leírást... (sortörés támogatott)',
  zoomIn: 'Nagyítás',
  zoomOut: 'Kicsinyítés',
  resetZoom: 'Nagyítás visszaállítása',
  addBlock: 'Blokk hozzáadása',
  noBlockTypes: 'Nincsenek elérhető bloktípusok',
  fitToContent: 'Igazítás a tartalomhoz',
  saved: 'Mentve!',
  savedToLocal: 'Folyamat mentve a localStorage-ba',
  loaded: 'Betöltve!',
  loadedFromLocal: 'Folyamat betöltve a localStorage-ból',
  noDataFound: 'Nincs talált adat',
  couldNotFindWorkflow: 'Nem sikerült megtalálni a mentett folyamatot',
  cleared: 'Törölve!',
  canvasCleared: 'A vászon törölve lett',
  imported: 'Importálva!',
  importedFromJson: 'Importálva JSON-ból',
  copied: 'Másolva!',
  jsonCopied: 'A folyamat JSON-ja a vágólapra másolva',
  simulatingTitle: 'Szimuláció...',
  simulatingDesc: 'Futtatás',
  groupCreated: 'Csoport létrehozva',
  blocksGrouped: 'blokk csoportosítva',
  savedToDb: 'mentve az adatbázisba',
  error: 'Hiba',
  saveError: 'Hiba mentéskor',
  loadError: 'Hiba betöltéskor',
  deleteError: 'Hiba törléskor',
  deleted: 'Törölve',
  documentDeleted: 'A dokumentum törölve lett',
  exportRefSaved: 'Export-hivatkozás mentve',
  exportRefDesc: 'PNG export-hivatkozás mentve az adatbázisba',
  exportPngDesc: 'A PNG-export a dom-to-image könyvtárt igényli',
  couldNotCopy: 'Nem sikerült a vágólapra másolni',
  couldNotReadWorkflow: 'Nem sikerült beolvasni a folyamat-adatokat',
  saveWorkflow: 'Folyamat mentése',
  saveAsNew: 'Mentés újként',
  titleLabel: 'Cím',
  categoryLabel: 'Kategória',
  cancel: 'Mégse',
  loadWorkflow: 'Folyamat betöltése',
  noDocuments: 'Nincsenek mentett dokumentumok',
  loadBtn: 'Betöltés',
  deleteBtn: 'Törlés',
  importJson: 'JSON importálása',
  pasteJsonHere: 'Illeszd be ide a WorkflowState JSON-t...',
  importBtn: 'Importálás',
  invalidJson: 'Érvénytelen JSON',
  invalidStructure: 'A JSON-ból hiányzik a szükséges struktúra (blocks, connectors, groups)',
  helpTitle: 'Útmutató és dokumentáció',
  integrationTab: 'Integráció',
  databaseTab: 'Adatbázis',
  jsonTab: 'JSON-leképezés',
  integrationTitle: 'Integráció a projektedbe',
  integrationDesc: 'A Flow Weaver futhat önállóan vagy beágyazva más projektbe iframe-ként. Két módot támogat: szerkesztési mód (alapértelmezett) és csak megtekintési mód (?mode=view paraméterrel). Csak megtekintési módban minden szerkesztő funkció rejtve van — a felhasználók csak nézegetni, nagyítani, rétegek között navigálni, betölteni és exportálni tudnak.',
  integrationStep1: '1. Tedd közzé az alkalmazást Lovable-en és másold a közzétett URL-t (pl. https://flow-weaver-05.lovable.app)',
  integrationStep2: '2. Ágyazd be <iframe src="URL?mode=view" width="100%" height="600px" /> formában a csak megtekintéshez, vagy ?mode=view nélkül a szerkesztési módhoz',
  integrationStep3: '3. Adatbázis-integráció nélkül minden a böngésző localStorage-ába mentődik — ideális teszteléshez',
  integrationStep4: '4. Adatbázis-tároláshoz: küldd a Supabase hitelesítő adataidat az iframe-nek postMessage-en keresztül (lásd a kódpéldát alább). Az iframe automatikusan figyeli a "supabase-credentials" üzenetet',
  integrationStep5: '5. Használd a ?mode=view paramétert a végfelhasználóknak (csak olvasható) és a standard URL-t paraméter nélkül az adminisztrátoroknak (szerkesztési mód)',
  integrationStep6: '6. A felhasználónak be kell jelentkeznie a host alkalmazásba — a session hozzáférési tokenje küldődik az adatbázishoz való hitelesítéshez',
  integrationStep7: '7. Ha nem küldődnek hitelesítő adatok, az iframe localStorage-módra esik vissza (nincs adatvesztés). A csak megtekintési mód az adatbázis-kapcsolattól függetlenül működik',
  integrationCodeTitle: 'Kódpélda — hitelesítő adatok küldése a host alkalmazásból',
  integrationCodeExample: `import { useRef, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

function FlowchartPage({ isAdmin = false }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const mode = isAdmin ? '' : '?mode=view';

  const sendCredentials = useCallback(async () => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;

    iframe.contentWindow.postMessage({
      type: 'supabase-credentials',
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
      supabaseKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      accessToken: session.access_token,
    }, '*');
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'request-supabase-credentials') {
        sendCredentials();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [sendCredentials]);

  return (
    <iframe
      ref={iframeRef}
      src={\`https://flow-weaver-05.lovable.app/\${mode}\`}
      onLoad={() => setTimeout(sendCredentials, 500)}
      className="w-full h-full"
    />
  );
}`,
  integrationNote: '⚠️ Csak megtekintési mód (?mode=view): Elrejti a TopBar-t, ToolSidebar-t és PropertiesPanel-t. Egy minimális topbart mutat Betöltés, Kép exportálása és Áttekintés elemekkel. Minden szerkesztő funkció (húzás, kapcsolatok, portok) le van tiltva. A nagyítás, görgetés és rétegnavigáció a megszokott módon működik.',
  databaseTitle: 'Adatbázis-követelmények a host alkalmazás számára',
  databaseDesc: 'A következő szükséges a host alkalmazásban, hogy az adatbázis-tárolás működjön. A Flow Weaver folyamat-adatokat küld JSON-ként egy edge functionnek, amely a mentést kezeli.',
  databaseReq1: 'Tábla: workflows\n— id (uuid PK, default gen_random_uuid())\n— title (text, NOT NULL)\n— blocks (jsonb, NOT NULL, default \'[]\')\n— connectors (jsonb, NOT NULL, default \'[]\')\n— groups (jsonb, NOT NULL, default \'[]\')\n— workflow_name (text, default \'\')\n— coordinate_origin (text, default \'center\')\n— category (text, default "workflow")\n— created_by (uuid, NOT NULL — hivatkozás a felhasználó auth.uid()-jére)\n— is_pinned (boolean, default false)\n— pinned_at (timestamptz, nullable)\n— pinned_by (uuid, nullable)\n— last_modified_by (uuid, nullable)\n— created_at (timestamptz, default now())\n— updated_at (timestamptz, default now())',
  databaseReq2: 'Egy "save-workflow" edge function, amely: (1) Validálja a JWT-tokent getClaims-en keresztül, (2) Kinyeri a user ID-t a tokenből, (3) Elfogadja a { id?, title, blocks, connectors, groups, workflow_name, coordinate_origin, category } adatokat a request body-ban, (4) INSERT-et végez új folyamatra vagy UPDATE-t létezőre, (5) Sikerre { id, title } választ ad',
  databaseReq3: 'RLS-szabályok a workflows táblán:\n— SELECT: auth.uid() = created_by\n— INSERT: auth.uid() = created_by\n— UPDATE: auth.uid() = created_by\n— DELETE: auth.uid() = created_by\nEz biztosítja, hogy minden felhasználó csak a saját folyamatait láthassa és kezelhesse',
  databaseReq4: 'Hitelesítési folyamat: A felhasználó bejelentkezik a host alkalmazásba → a host elküldi az access_token-t postMessage-en keresztül az iframe-nek → a Flow Weaver a tokent használja az adatbázisban való hitelesítéshez. Token nélkül az alkalmazás localStorage-módban működik',
  jsonTitle: 'JSON-leképezés',
  jsonDesc: 'A folyamatok JSON-ként tárolódnak az alábbi struktúrával (WorkflowState):',
  jsonStep1: '1. Kattints a "JSON másolása" gombra az eszköztáron az aktuális folyamat exportálásához',
  jsonStep2: '2. Kattints az "Importálás" gombra egy JSON-folyamat betöltéséhez',
  jsonStep3: '3. Minden blokknak van id, type, x, y, config (név, leírás, ikon) és color mezője. A blokkok átalakíthatók rétegekké (üres összecsukott csoportokká) a réteg gombbal.',
  jsonStep4: '4. Az összekötők fromId/toId-re hivatkoznak. A csoportoknak lehet 0+ blokkjuk, parentGroupId-vel beágyazhatók, és rétegekké csukhatók össze collapsed/collapsedX/collapsedY mezőkkel.',
  jsonStructure: '{ "name": "...", "blocks": [...], "connectors": [{ fromId, toId, fromPort, toPort }], "groups": [{ id, blockIds, color, name, collapsed, collapsedX, collapsedY, parentGroupId, locked }] }',
  aiPromptTab: 'MI-prompt',
  aiPromptTitle: 'Folyamatok generálása MI-vel',
  aiPromptDesc: 'Bármely MI-t használhatsz (ChatGPT, Grok, Claude, Lovable stb.) JSON automatikus generálására, amely közvetlenül importálható a Flow Weaverbe. Add meg a lenti promptot az MI-nek az anyagoddal együtt.',
  aiPromptUseCases: 'Felhasználási esetek',
  aiPromptUseCase1: 'Kérd meg az MI-t, hogy egy meglévő projektet vagy rendszert folyamatábrává alakítson',
  aiPromptUseCase2: 'Tölts fel képeket (architektúra-diagramok, fehértáblák) és kérd, hogy az MI alakítsa át JSON-ná',
  aiPromptUseCase3: 'Küldj dokumentációt, specifikációt vagy követelménylistákat és kapj vizuális folyamatot',
  aiPromptUseCase4: 'Írd le egy folyamatot szavakkal, és hagyd, hogy az MI elkészítse a folyamatábrát',
  aiPromptUseCase5: 'Készíts összetett, többrétegű architektúrákat beágyazott rétegekkel — magas szintű áttekintés a gyökéren, részletes logika belül',
  aiPromptTemplateTitle: 'Másolható MI-prompt',
  aiPromptTemplate: `Te egy JSON-generátor vagy a Flow Weaver számára. KIZÁRÓLAG egy érvényes JSON-objektumot (WorkflowState) adj vissza. Magyarázó szöveg nélkül.

SZAKÉRTŐ architektúra-tervező vagy — tervezz folyamatokat világos hierarchiákkal, logikus csoportosításokkal és optimális összekötő-útvonallal. Gondolj rétegekben: magas szintű áttekintés a gyökéren, részletes logika beágyazott csoportokban.

══════════════════════════════════
  SCHEMA
══════════════════════════════════
{
  "name": "Folyamat neve",
  "blocks": [
    { "id": "b01", "type": "general", "x": 200, "y": 100,
      "config": { "name": "Blokk neve", "description": "Részletes leírás\nTöbb sor támogatott", "icon": "wrench" },
      "color": "#6366f1" }
  ],
  "connectors": [
    { "id": "c01", "fromId": "b01", "toId": "b02", "fromPort": "right", "toPort": "left" }
  ],
  "groups": [
    { "id": "g1", "blockIds": ["b01","b02"], "color": "#8b5cf6", "name": "Csoport neve",
      "locked": false, "collapsed": true, "parentGroupId": null,
      "collapsedX": 200, "collapsedY": 100 }
  ]
}

══════════════════════════════════
  1. BLOKKTÍPUSOK
══════════════════════════════════
"create-user" | "disable-mailbox" | "delete-m365" | "send-email"
"webhook"      | "add-to-group"   | "condition"    | "general"

Használd a "general" típust minden olyan lépéshez, amelyet a fentiek nem fednek le. A "condition" döntési pontokra / elágazásokra való.

══════════════════════════════════
  2. IKONOK (kategóriánként)
══════════════════════════════════
Emberek:    user, users
Komm.:      mail, send, message-square, phone, bell
Felhő/Net:  cloud, globe, server, database, link
Fájlok:     file-text, folder, archive, download, upload, copy
Eszközök:   wrench, settings, terminal, filter, search
Állapot:    check-circle, alert-triangle, clock, flag, star
Bizt.:      shield, lock, key, eye
Média:      play, camera, monitor
Logika:     git-branch, shuffle, layers, refresh-cw
Egyéb:      zap, power, heart, tag, bookmark, bar-chart, package, trash, thumbs-up, calendar, help-circle

══════════════════════════════════
  3. RÁCS ÉS KOORDINÁTÁK
══════════════════════════════════
- x és y MUSZÁJ 20 többszörösei legyenek
- Vízszintes blokk-távolság: ~240px
- Függőleges blokk-távolság: ~220px
- ID formátum: "b01", "b02", ... (blokkok), "c01", "c02", ... (összekötők), "g1", "g2", ... (csoportok)
- Minden ID-nek globálisan egyedinek kell lennie
- Az összecsukott csoportokon belüli blokkok saját koordinátarendszert használnak

══════════════════════════════════
  4. PORTVÁLASZTÁS (KRITIKUS!)
══════════════════════════════════
A fromPort/toPort meghatározása a forrás (x,y) és a cél (x,y) összehasonlításával:

  Irány                │ fromPort       │ toPort
  ─────────────────────┼────────────────┼────────────────
  → Jobbra (azonos y)  │ "right"        │ "left"
  ← Balra (azonos y)   │ "left"         │ "right"
  ↓ Le (azonos x)      │ "bottom"       │ "top"
  ↑ Fel (azonos x)     │ "top"          │ "bottom"
  ↘ Átlós le-jobbra    │ "bottom-right" │ "top-left"
  ↙ Átlós le-balra     │ "bottom-left"  │ "top-right"
  ↗ Átlós fel-jobbra   │ "top-right"    │ "bottom-left"
  ↖ Átlós fel-balra    │ "top-left"     │ "bottom-right"

⚠ SOSE használj átlós portokat azonos sorban (y) vagy oszlopban (x) lévő blokkokra.
⚠ A fromPort és toPort MINDIG kötelezően megadandó.
⚠ Az auto-snap korrigálja a szuboptimális útvonalat, de a tiszta eredményhez add meg helyesen.

══════════════════════════════════
  5. CSOPORTOK ÉS BEÁGYAZÁS
══════════════════════════════════
Csoport-mezők:
  "id":            Egyedi csoport-ID (pl. "g1", "g2")
  "blockIds":      A csoporthoz tartozó blokkok ID-jeinek tömbje (LEHET ÜRES [] rétegekhez)
  "color":         Hex-szín (pl. "#8b5cf6")
  "name":          A csoport megjelenítendő neve
  "locked":        false (alapértelmezett) — true esetén a blokkok együtt mozognak
  "collapsed":     true/false — true esetén a csoport kompakt kártyaként jelenik meg (RÉTEG-MÓD)
  "parentGroupId": null a legfelső szinten, vagy a szülő csoport ID-je beágyazáshoz
  "collapsedX":    Kompakt kártya X-pozíciója összecsukva (20 többszöröse)
  "collapsedY":    Kompakt kártya Y-pozíciója összecsukva (20 többszöröse)

Szabályok:
- A csoportoknak lehet 0 vagy több blokkjuk (üres csoportok = üres rétegek)
- Egy önálló blokk átalakítható réteggé (üres összecsukott csoportot hoz létre)
- A blokkok CSAK EGY csoporthoz tartozhatnak
- A beágyazott csoportok FÜGGETLEN koordinátarendszert használnak (minden réteg saját vászon)
- A parentGroupId köti a gyermek csoportokat a szülő csoportokhoz a hierarchiához

══════════════════════════════════
  6. RÉTEG-RENDSZER (HALADÓ)
══════════════════════════════════
A "collapsed": true mezővel rendelkező csoportok navigálható rétegekké válnak:

🏗️ ARCHITEKTÚRA-MINTA:
  Gyökérszint:  Magas szintű áttekintés összecsukott csoportokkal, mint kompakt kártyák
  1. réteg:     Alrendszerek — minden összecsukott csoport saját vászonra bomlik ki
  2+ réteg:     Részletes implementációs logika beágyazott csoportokon belül

🔄 BLOKK→RÉTEG ÁTALAKÍTÁS:
  - Egyedi blokkok rétegekké alakíthatók a réteg gombbal
  - Ez egy üres összecsukott csoportot hoz létre a blokk pozíciójánál
  - A blokkhoz/blokkról menő összekötők újra-leképeződnek az új rétegre
  - Használd ezt "helyőrző-rétegek" létrehozására, amelyeket később feltöltesz

📐 ELRENDEZÉSI STRATÉGIA:
  - Helyezd az összecsukott csoportokat blokkokként (~240px) a szülő szinten
  - Használd a collapsedX/collapsedY-t a kompakt kártyák pozicionálására
  - Minden csoporton belül: rendezd a blokkokat függetlenül (saját koord-rendszer, x:200, y:100-tól)

🔗 RÉTEGKÖZI ÖSSZEKÖTŐK:
  - Különböző összecsukott csoportokba tartozó blokkok közötti összekötők
    a csoport-kártyák közötti kapcsolatként jelennek meg a szülő szinten
  - Az összekötők közvetlenül hivatkozhatnak csoport-ID-kre (fromId/toId = csoport-ID)

📋 PÉLDA — 3-rétegű architektúra:
  Gyökér: [Auth Csoport] → [API Csoport] → [Adatbázis Csoport]
  Auth:   [Bejelentkezés] → [Validálás] → [Token]
  API:    [Útválasztó] → [Vezérlő] → [Middleware]
  AB:     [Lekérdezés] → [Cache] → [Tárolás]

  JSON-struktúra:
  - 3 csoport collapsed: true-val, collapsedX/Y-nal a gyökér szinten
  - Csoport-ID-k közötti összekötők a gyökér szinten
  - Saját koordinátákkal rendelkező blokkok minden csoporton belül

══════════════════════════════════
  7. TERVEZÉSI MINTÁK
══════════════════════════════════
📊 PIPELINE: Lineáris folyamat → "right"/"left" portok, azonos Y
📐 ELÁGAZÁS: Feltétel több útvonallal → "bottom"/"right" portok
🔄 VISSZACSATOLÁSI HUROK: Vissza-nyíl → "left"/"top" portok vissza
🏢 HIERARCHIKUS: Vezetői csoportok dolgozói alcsoportokkal
⚡ ESEMÉNYVEZÉRELT: Webhook indítók párhuzamos útvonalakkal
🎨 SZÍNKÓDOLÁS: Domén/aggály szerint különböző színek:
   Infrastruktúra: "#6366f1"  API: "#8b5cf6"  Auth: "#ec4899"
   Adat: "#06b6d4"  UI: "#22c55e"  Monitorozás: "#f97316"

  ══════════════════════════════════
  ELLENŐRZŐ LISTA KIMENET ELŐTT
══════════════════════════════════
✅ Minden x/y érték 20 többszöröse
✅ Minden összekötőnek van fromPort + toPort mezője
✅ A portválasztások illenek a relatív pozícióhoz (lásd a fenti táblát)
✅ Minden ID globálisan egyedi
✅ A csoportok lehetnek üresek (blockIds: []) — nincs minimum követelmény
✅ Az összecsukott csoportoknak van collapsedX/collapsedY mezőjük
✅ A parentGroupId érvényes csoport-ID-re hivatkozik (vagy null)
✅ A rétegek belüli blokkok független koordinátákkal rendelkeznek (saját vászon)
✅ A blokkleírások részletesek és cselekvésorientáltak
✅ Színkódold a csoportokat domén/aggály szerint a vizuális tisztaság érdekében
✅ Útpontok (opcionális): adj "waypoints": [{"x":…,"y":…}] mezőt egy összekötőhöz a görbe manuális irányításához
✅ Mindig add meg a "coordinateOrigin": "center" mezőt a gyökér szinten
✅ A kimenet KIZÁRÓLAG JSON`,
  aiPromptTipsTitle: 'Tippek a legjobb eredményekhez',
  aiPromptTip1: 'Csatolj képeket meglévő diagramokról — az MI értelmezi és JSON-ná alakítja őket',
  aiPromptTip2: 'Írd le a folyamat lépéseit pontokba szedve a tisztább eredményért',
  aiPromptTip3: 'Adj meg kívánt blokk- és csoportszámot a promptban a nagyobb kontrollért',
  aiPromptTip4: 'Másold a JSON-eredményt és illeszd be a Flow Weaver Importálás gombján keresztül',
  aiPromptCopy: 'Prompt másolása',
  aiPromptCopied: 'Prompt másolva!',
  close: 'Bezárás',
  saveFile: 'Fájl mentése',
  loadFile: 'Fájl betöltése',
  welcome: 'Üdvözöljük',
  savedAsFile: 'A folyamat fájlként mentve',
  donate: 'Adományozás',
  undo: 'Visszavonás',
  redo: 'Megismétlés',
  rename: 'Átnevezés',
  editName: 'Név szerkesztése',
  editDescription: 'Leírás szerkesztése',
  editColor: 'Szín szerkesztése',
  editBlock: 'Blokk szerkesztése',
  editGroup: 'Csoport szerkesztése',
  editLayer: 'Réteg szerkesztése',
  groupThisBlock: 'Blokk csoportosítása',
  createGroupFromSelection: 'Csoport létrehozása a kijelölésből',
  convertToLayer: 'Átalakítás réteggé',
  duplicate: 'Duplikálás',
  explainBlock: 'Blokk magyarázata…',
  delete: 'Törlés',
  deleteBlock: 'Blokk törlése',
  deleteGroup: 'Csoport törlése',
  collapseToLayer: 'Összecsukás réteggé',
  enterLayer: 'Belépés a rétegbe',
  expandLayer: 'Kibontás (vissza csoporttá)',
  keyboardShortcutsTitle: 'Gyorsbillentyűk',
  keyboardShortcutsHint: 'Nyomj ?-et a panel újra megjelenítéséhez.',
  blocksGroupedCount_one: '{{count}} blokk csoportosítva',
  blocksGroupedCount_other: '{{count}} blokk csoportosítva',
  blocksCount_one: '{{count}} blokk',
  blocksCount_other: '{{count}} blokk',
  connectorsCount_one: '{{count}} összekötő',
  connectorsCount_other: '{{count}} összekötő',
  groupsCount_one: '{{count}} csoport',
  groupsCount_other: '{{count}} csoport',
  export: 'Exportálás',
  pleaseWait: 'Ez nagy vásznak esetén néhány másodpercig tarthat…',
  exportDone: 'Export kész',
  auditCreated: 'Létrehozva',
  auditDeleted: 'Törölve',
  auditRenamed: 'Átnevezve',
  auditEdited: 'Szerkesztve',
  auditCoopEnabled: 'Kollab. mód engedélyezve',
  auditCoopDisabled: 'Kollab. mód letiltva',
  auditRedactedActor: '[törölt felhasználó]',
  auditCollabInvite: 'Felhasználó meghívva',
  auditCollabRemove: 'Felhasználó eltávolítva',
  auditCollabRoleChange: 'Szerepkör módosítva',
  auditCollabAccept: 'Meghívás elfogadva:',
  crossSheetLink: 'Lapok közötti kapcsolat',
  crossSheetActive: 'Lapok közötti összekötés…',
  crossSheetHint: 'Blokkok összekötése szomszédos lapok között',
  crossSheetActiveHint: 'Nyomj Esc-et a megszakításhoz',
  back: 'Vissza',
  processEngineDisabled: 'A Process Engine ki van kapcsolva ezen a telepítésen.',
  processEngineUpsellTitle: 'Process Engine feloldása',
  processEngineUpsellBody: 'Építs jóváhagyási, kiosztási és kilépési folyamatokat Flow-weaver-diagramjaidból. Szükséges: {tier} vagy magasabb.',
  processEngineUpsellCta: 'Csomagok megtekintése',
  processEngineLoadTitle: 'Tölts be egy Flow-weaver folyamatot',
  processEngineLoadFromFile: 'Fájlból (.json)',
  processEngineLoadFromPaste: 'JSON beillesztése',
  processEngineLoadImport: 'Importálás',
  processEngineImportSuccess: 'Folyamat importálva',
  processEngineImportFailed: 'A folyamat nem importálható',
  processEngineLiveAvailable: 'Élő futtatások elérhetők',
  processEngineDesignOnly: 'Csak tervezés — váltson {tier} csomagra élő futtatásokhoz',
  processEngineDryRun: 'Próbafuttatás',
  processEngineDryRunDone: 'Próbafuttatás kész',
  processEngineRunning: 'Fut…',
  processEngineLoadFromTemplate: 'Vagy indulj sablonból',
  processEngineOpenFinal: 'Záró nézet',
  processEngineSuggest: 'MI javaslat',
  processEngineSuggestNone: 'Nincs egyezés',
  processEngineSuggestDone: 'Javaslatok hozzáadva',
  processEngineRunLive: 'Élő futtatás',
  processEngineRunDone: 'Élő futtatás kész',
  processEngineRunFailed: 'Élő futtatás sikertelen',
  processEngineRunBlocked: 'Élő futtatás letiltva',
  processEngineSave: 'Mentés',
  processEngineSaving: 'Mentés…',
  processEngineSaveDone: 'Elmentve',
  processEngineSaveFailed: 'Mentés sikertelen',
  processEngineLoadFromSaved: 'Mentett folyamatok',
  finalTldr: 'Összefoglaló',
  finalPageTitle: 'Záró',
  finalSubtitle: 'Teljes kép a tervezésről, a tudásról és a működésről.',
  finalAudience: 'Célközönség',
  finalHubTitle: 'Rendszer-áttekintés',
  finalHubSubtitle:
    'Élő áttekintés minden rendszerről. Nyiss meg egy konténert a mélyebb betekintéshez, vagy léptess elő egy folyamatot a Process Engine-ből a részletes három-felvonásos elbeszéléshez.',
  finalNoFlowTitle: 'Nincs folyamat kiválasztva',
  finalNoFlowBody: 'Nyisd meg a Process Engine-t és tölts be egy folyamatot.',
  finalGoToProcessEngine: 'Process Engine megnyitása',
  finalContainerFlowWeaverTitle: 'Flow-weaver',
  finalContainerFlowWeaverEmpty: 'Még nincs mentett munkafolyamat — nyisd meg a Flow-weaver-t.',
  finalContainerOpenFlowWeaver: 'Flow-weaver megnyitása',
  finalContainerProcessEngineTitle: 'Process Engine',
  finalContainerProcessEngineSubtitle: '{count} mentett folyamat',
  finalContainerProcessEngineEmpty: 'Nincsenek mentett folyamatok — léptess elő egy munkafolyamatot Process-be.',
  finalContainerOpenProcessEngine: 'Process Engine megnyitása',
  finalContainerAtlasTitle: 'Atlas',
  finalContainerAtlasEmpty: 'Atlas-manifeszt nincs betöltve.',
  finalContainerOpenAtlas: 'Atlas megnyitása',
  finalContainerGroups: 'Csoportok',
  finalContainerFlows: 'Folyamatok',
  finalContainerTotalBlocks: 'Blokkok összesen',
  finalContainerCategories: 'Kategóriák',
  finalActDesign: 'Tervezés',
  finalActDesignSubtitle: 'Hogyan modelleztük a folyamatot a Flow-weaverben.',
  finalActKnowledge: 'Tudás',
  finalActKnowledgeSubtitle: 'Mit tud a szervezet, és mit köt össze az Atlas.',
  finalActOperation: 'Működés',
  finalActOperationSubtitle: 'Hogyan viselkedik a folyamat 30 nap alatt.',
  finalSheets: 'Lapok',
  finalBlocks: 'Blokkok',
  finalConnectors: 'Kapcsolók',
  finalBindings: 'Kötések',
  finalAtlas: 'Atlas',
  finalDocs: 'Dokumentumok',
  finalCrossDocRefs: 'Kereszthivatkozások',
  finalRuns30d: 'Futások (30 n)',
  finalSuccessRate: 'Sikerarány',
  finalMeanDuration: 'Átlagos időtartam',
  finalRoi: 'Érték',
  finalRoiHoursSaved: 'Megtakarított órák',
  finalRoiErrorsPrevented: 'Megelőzött hibák',
  finalRoiMtto: 'MTTO',
  finalRoiValue: 'Érték',
  finalRoiMethodology: 'Az érték óvatos becslés: sikeres futásonként 30 perc megtakarított kézi munka × 45 €-s órabér.',
  finalWatermark: 'Generálva: {watermark}',
  finalDisabled: 'A Záró nézet ki van kapcsolva ezen a telepítésen.',
  finalUpsellTitle: 'Záró nézet feloldása',
  finalUpsellBody: 'Csak olvasható vezetői összefoglaló a Flow-weaver, az Atlas és a Process Engine fölött. Szükséges: {tier} vagy magasabb.',
  finalUpsellCta: 'Csomagok megtekintése',
  finalPhaseHint: 'A Záró nézet egy következő fázisban érkezik.',
  blockCreated: 'Blokk hozzáadva',
  toggleCollapse: 'Összecsukás váltása',
  toggleLock: 'Zár váltása',
};

export const translations: Record<Language, TranslationKeys> = { sv, en, no, da, de, fr, hu };
